import "@arc/tokens/css";
import "./styles.css";

import { StrictMode, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { createRoot } from "react-dom/client";
import type { User } from "@supabase/supabase-js";
import type {
  CoachBriefV2,
  Confidence,
  DriverCategory,
  Measurement,
  PracticeMode,
  UnauthPreview,
} from "@arc/brief-schema";
import { PROVENANCE_LABEL } from "@arc/brief-schema";
import { ArcBrandLockup } from "./components/ArcBrand";
import {
  createCoachMergeCode,
  listCoachLinkedIdentities,
  redeemCoachMergeCode,
  type CoachLinkedIdentity,
  type CoachMergeCode,
  type CoachMergeResult,
} from "./lib/account";
import {
  authCallbackNeedsFreshSession,
  authProviderRedirectUrl,
  authRedirectUrl,
  postAuthNextPath,
  rememberPostAuthNextPath,
  resolvePostAuthNextPath,
  waitForAuthResolution,
  waitForAuthSession,
} from "./lib/auth-flow";
import {
  fetchCoachBuildManifest,
  formatCoachBuildDetail,
  formatCoachBuildStamp,
  readBuildStampPreference,
  type CoachBuildManifest,
} from "./lib/build-info";
import {
  persistCoachThemePreference,
  readCoachThemePreference,
  type CoachTheme,
} from "./lib/theme";
import {
  acceptCoachCollaboration,
  fetchUnauthPreview,
  getCollaboration,
  listAcceptedCoachCollaborations,
  listAnnotations,
  listAssignments,
  buildArcCoachActionDraft,
  canUseSessionMedia,
  collaborationSurfaceTitle,
  runArcCoachPlan as invokeArcCoachPlan,
  createDrillTemplate,
  deleteDrillTemplate,
  listDrillTemplates,
  updateDrillTemplate,
  COACH_DRILL_TEMPLATE_FOCUS_TAGS,
  type CoachAnnotation,
  type CoachAssignment,
  type CoachAuthorKind,
  type CoachDrillTemplate,
  type CoachDrillTemplateDraft,
  type CoachDrillTemplateFocusTag,
  type CoachPracticeSession,
  type CoachRangePlanBlock,
  type CoachRangePlanBlockIntent,
  type CoachRangePlanGroundTruth,
  type CoachRangePlanGroundTruthMethod,
  type CoachRangePlanKind,
  type CoachRangePlanPrescription,
  COACH_RANGE_PLAN_BLOCK_INTENTS,
  COACH_RANGE_PLAN_GROUND_TRUTH_METHODS,
  type Collaboration,
} from "./lib/coach";
import {
  applyPracticeWindowToPlan,
  type BuilderTemplateFilterId,
  buildReviewedSessionCoachPath,
  buildStudentMediaPath,
  builderTemplateFilters,
  createRangePlanTemplateDraft,
  createCustomRangePlanSeed,
  focusTagForRangePlan,
  isBriefStale,
  latestActiveHumanPlan,
  latestCoachVerdictOverride,
  latestArcAssistPlan,
  latestRangePlanStatusSummary,
  mediaReviewSeedFromItems,
  type PracticeWindowId,
  rangePlanPrescriptionFromTemplate,
  reusableCoachPatterns,
  verdictTopFacts,
} from "./lib/player-page";
import {
  coachEntitlementUpsellCopy,
  coachSubscriptionStatusLabel,
  getCoachSubscription,
  openCoachBillingPortal,
  startCoachCheckout,
  type CoachSubscription,
} from "./lib/billing";
import {
  getCoachNotificationPreferences,
  resolveCoachNotificationPreferences,
  upsertCoachNotificationPreferences,
  type CoachNotificationPreferences,
} from "./lib/notifications";
import { coachEventBus } from "./lib/events";
import { getCoachRuntimeConfig } from "./lib/config";
import {
  countUnreadReplies,
  listAssignmentReplies,
  listInboxThreads,
  markAssignmentRepliesRead,
  subscribeToInboxReplies,
  type CoachAssignmentReply,
  type InboxRealtimeStatus,
  type InboxThreadRow,
} from "./lib/inbox";
import {
  preferredVoiceRecordingMime,
  signVoiceNoteUrl,
  uploadVoiceNote,
  voiceRecordingSupported,
  type UploadedVoiceNote,
} from "./lib/voice";
import { supabase } from "./lib/supabase";

const COACH_SITE_URL = import.meta.env.VITE_SITE_URL?.trim() || null;

type Route =
  | { kind: "today" }
  | { kind: "students" }
  | { kind: "student"; id: string }
  | { kind: "studentMedia"; id: string }
  | { kind: "inbox" }
  | { kind: "library" }
  | { kind: "billing" }
  | { kind: "publicCoach"; slug: string }
  | { kind: "preview"; token: string }
  | { kind: "authCallback" }
  | { kind: "notFound" };

type CoachActionMode = "note" | "plan" | "cue";
type RangePlanBuilderMode = "template" | "custom";

type RangePlanKind = CoachRangePlanKind;
type RangePlanBlock = CoachRangePlanBlock;
type RangePlanPrescription = CoachRangePlanPrescription;

type RangePlanPaceId =
  | "plan_cadence"
  | "seven_second_gap"
  | "full_routine"
  | "reset_between_blocks";
type RangePlanPracticeWindowId = PracticeWindowId;
type RangePlanGuardrailId =
  | "hold_block_order"
  | "same_miss_stop"
  | "media_checkpoint"
  | "coach_review_gate";
type RangePlanMediaCaptureId =
  | "final_five"
  | "anchor_per_block"
  | "all_swings"
  | "none";
type RangePlanCheckInId =
  | "block_summary"
  | "final_five_note"
  | "coach_review_required";

type StructuredRangePlanSettings = {
  practiceWindowId: RangePlanPracticeWindowId;
  paceId: RangePlanPaceId;
  guardrailId: RangePlanGuardrailId;
  mediaCaptureId: RangePlanMediaCaptureId;
  checkInId: RangePlanCheckInId;
};

type RangePlanStructuredOption<T extends string> = {
  id: T;
  label: string;
  instruction: string;
};

const RANGE_PLAN_KIND_LABEL: Record<RangePlanKind, string> = {
  coach_plan: "Coach Plan",
  homework: "Homework",
  measurement_test: "Measurement Test",
  free_practice_guardrails: "Free Practice Guardrails",
};

const RANGE_PLAN_BLOCK_INTENT_LABEL: Record<CoachRangePlanBlockIntent, string> = {
  stock: "Stock swings",
  mishit_mix: "Mishit mix",
  rehearsal: "Rehearsal (no ball)",
  warmup: "Warm-up",
  pressure: "Pressure",
};

const RANGE_PLAN_BLOCK_INTENT_HINT: Record<CoachRangePlanBlockIntent, string> = {
  stock: "Normal full swings.",
  mishit_mix: "Intentional mishits — top, fat, thin, heel, or toe.",
  rehearsal: "No-ball practice swings.",
  warmup: "Loosen up; reps don't count yet.",
  pressure: "Scored shots, full routine.",
};

const RANGE_PLAN_GROUND_TRUTH_LABEL: Record<CoachRangePlanGroundTruthMethod, string> = {
  none: "No ground-truth source",
  voice_marks: "Voice marks ('shot 1 driver…')",
  launch_monitor: "Launch monitor (R10 / Mevo / etc.)",
  toptracer: "Toptracer Range",
  manual: "Manual coach log",
};

const RANGE_PLAN_PACE_OPTIONS: RangePlanStructuredOption<RangePlanPaceId>[] = [
  {
    id: "plan_cadence",
    label: "Use block cadence",
    instruction: "Use each block's prescribed interval and routine.",
  },
  {
    id: "seven_second_gap",
    label: "7-8 seconds between balls",
    instruction: "Keep 7-8 seconds between real shots unless the block says full routine.",
  },
  {
    id: "full_routine",
    label: "Full routine every ball",
    instruction: "Reset fully before each real shot.",
  },
  {
    id: "reset_between_blocks",
    label: "One-minute block reset",
    instruction: "Pause one minute between blocks and name the miss before continuing.",
  },
];

const RANGE_PLAN_PRACTICE_WINDOW_OPTIONS: RangePlanStructuredOption<RangePlanPracticeWindowId>[] = [
  {
    id: "single_session",
    label: "Single session",
    instruction: "Send one structured range session only.",
  },
  {
    id: "weekly_bridge",
    label: "Weekly bridge",
    instruction: "Bridge the current lesson with one full coached session this week.",
  },
  {
    id: "monthly_bridge",
    label: "Monthly bridge (4 sessions)",
    instruction: "Bridge a month between lessons with four linked practice sessions.",
  },
];

const RANGE_PLAN_GUARDRAIL_OPTIONS: RangePlanStructuredOption<RangePlanGuardrailId>[] = [
  {
    id: "hold_block_order",
    label: "Hold block order",
    instruction: "Do not skip, reorder, or add free-practice swings.",
  },
  {
    id: "same_miss_stop",
    label: "Stop after repeat miss",
    instruction: "Stop the block after two same-side misses and ask for a reset.",
  },
  {
    id: "media_checkpoint",
    label: "Media before moving on",
    instruction: "Require the media checkpoint before the next block starts.",
  },
  {
    id: "coach_review_gate",
    label: "Coach review gate",
    instruction: "Hold the final block until the coach reviews the latest swings.",
  },
];

const RANGE_PLAN_MEDIA_CAPTURE_OPTIONS: RangePlanStructuredOption<RangePlanMediaCaptureId>[] = [
  {
    id: "final_five",
    label: "Final five swings",
    instruction: "Save the final five swings from the plan.",
  },
  {
    id: "anchor_per_block",
    label: "Anchor per block",
    instruction: "Save one clear swing and one result screenshot from each block.",
  },
  {
    id: "all_swings",
    label: "All swings",
    instruction: "Save every real struck ball in the plan.",
  },
  {
    id: "none",
    label: "No required media",
    instruction: "No required media capture for this plan.",
  },
];

const RANGE_PLAN_CHECK_IN_OPTIONS: RangePlanStructuredOption<RangePlanCheckInId>[] = [
  {
    id: "block_summary",
    label: "Block summary",
    instruction: "After each block, report dominant miss and best cue.",
  },
  {
    id: "final_five_note",
    label: "Final-five note",
    instruction: "After the final five swings, report what changed.",
  },
  {
    id: "coach_review_required",
    label: "Coach review required",
    instruction: "Stop after upload and wait for the coach's next cue.",
  },
];

const RANGE_PLAN_CAMERA_OPTIONS = [
  "Coach-selected angle",
  "Down-the-line",
  "Face-on",
  "Same angle",
  "Mixed but stay side view",
  "Mixed capture",
] as const;

const RANGE_PLAN_COMMON_CLUBS = [
  "Driver",
  "3-wood",
  "5 fairway",
  "Hybrid",
  "4-iron",
  "5-iron",
  "6-iron",
  "7-iron",
  "8-iron",
  "9-iron",
  "Pitching wedge",
  "Gap wedge",
  "Sand wedge",
  "Lob wedge",
  "Putter",
] as const;

const DEFAULT_RANGE_PLAN_SETTINGS: StructuredRangePlanSettings = {
  practiceWindowId: "weekly_bridge",
  paceId: "plan_cadence",
  guardrailId: "hold_block_order",
  mediaCaptureId: "final_five",
  checkInId: "block_summary",
};

const RANGE_PLAN_DEFAULT_SETTINGS: Record<string, StructuredRangePlanSettings> = {
  "mcnally-5w-validation": {
    practiceWindowId: "single_session",
    paceId: "plan_cadence",
    guardrailId: "hold_block_order",
    mediaCaptureId: "anchor_per_block",
    checkInId: "block_summary",
  },
  "next-lesson-homework": {
    practiceWindowId: "weekly_bridge",
    paceId: "reset_between_blocks",
    guardrailId: "same_miss_stop",
    mediaCaptureId: "final_five",
    checkInId: "final_five_note",
  },
  "free-practice-guardrails": {
    practiceWindowId: "single_session",
    paceId: "plan_cadence",
    guardrailId: "hold_block_order",
    mediaCaptureId: "final_five",
    checkInId: "block_summary",
  },
  "coach-cue-progression": {
    practiceWindowId: "weekly_bridge",
    paceId: "plan_cadence",
    guardrailId: "same_miss_stop",
    mediaCaptureId: "final_five",
    checkInId: "final_five_note",
  },
  "driver-start-line-reset": {
    practiceWindowId: "weekly_bridge",
    paceId: "full_routine",
    guardrailId: "same_miss_stop",
    mediaCaptureId: "final_five",
    checkInId: "final_five_note",
  },
  "contact-window-ladder": {
    practiceWindowId: "weekly_bridge",
    paceId: "seven_second_gap",
    guardrailId: "hold_block_order",
    mediaCaptureId: "anchor_per_block",
    checkInId: "block_summary",
  },
  "wedge-distance-matrix": {
    practiceWindowId: "weekly_bridge",
    paceId: "plan_cadence",
    guardrailId: "same_miss_stop",
    mediaCaptureId: "final_five",
    checkInId: "final_five_note",
  },
  "fairway-transfer-bridge": {
    practiceWindowId: "weekly_bridge",
    paceId: "full_routine",
    guardrailId: "hold_block_order",
    mediaCaptureId: "anchor_per_block",
    checkInId: "block_summary",
  },
  "pre-lesson-verification": {
    practiceWindowId: "single_session",
    paceId: "full_routine",
    guardrailId: "coach_review_gate",
    mediaCaptureId: "anchor_per_block",
    checkInId: "coach_review_required",
  },
  "putting-pace-ladder": {
    practiceWindowId: "weekly_bridge",
    paceId: "plan_cadence",
    guardrailId: "hold_block_order",
    mediaCaptureId: "final_five",
    checkInId: "final_five_note",
  },
};

const RANGE_PLAN_PRESETS: RangePlanPrescription[] = [
  {
    id: "coach-cue-progression",
    templateId: "dev-template-coach-cue-progression",
    title: "60-ball coach cue progression",
    kind: "coach_plan",
    totalBalls: 60,
    summary:
      "A structured coach-led plan for turning one lesson cue into baseline, transfer, and pressure reps.",
    setup:
      "Start ARC in Coach Plan mode. Keep the assigned cue visible and use the same target for the first two blocks.",
    coachCue:
      "Follow the cue progression in order and let the app stop you when the pattern repeats.",
    homework:
      "Complete the plan once before the next lesson and send the final-five note.",
    betweenBlocks:
      "Pause between blocks, name the dominant miss, then continue only when the next block is selected.",
    focusLabel: "Lesson cue transfer",
    clubLabel: "Assigned club",
    blocks: [
      {
        id: "warmup-read",
        balls: 8,
        title: "Warmup read",
        club: "Assigned iron",
        camera: "Coach-selected angle",
        cadence: "Comfortable rhythm.",
        how: "Make stock swings at 70-80% without correcting.",
        goal: "Find the starting pattern before the cue work.",
      },
      {
        id: "baseline",
        balls: 12,
        title: "Baseline",
        club: "Assigned club",
        camera: "Same angle",
        cadence: "7-8 seconds between balls.",
        how: "Use the normal routine and record the dominant miss.",
        goal: "Give the coach a clean before-read.",
      },
      {
        id: "cue-block",
        balls: 20,
        title: "Cue block",
        club: "Assigned club",
        camera: "Same angle",
        cadence: "8 seconds between balls.",
        how: "Use only the assigned cue. Stop after two same-side misses.",
        goal: "Test whether the cue changes the miss pattern.",
      },
      {
        id: "club-transfer",
        balls: 10,
        title: "Club transfer",
        club: "Assigned club + one longer club",
        camera: "Same angle",
        cadence: "Full routine.",
        how: "Alternate clubs every two balls while keeping the cue.",
        goal: "Check whether the cue survives a club change.",
      },
      {
        id: "pressure-finish",
        balls: 10,
        title: "Pressure finish",
        club: "Assigned club",
        camera: "Same angle",
        cadence: "Full routine.",
        how: "Score each shot as playable or not. Do not add extra reps.",
        goal: "Leave with an honest final read.",
      },
    ],
  },
  {
    id: "driver-start-line-reset",
    templateId: "dev-template-driver-start-line-reset",
    title: "48-ball driver start-line reset",
    kind: "coach_plan",
    totalBalls: 48,
    summary:
      "A driver-first starter for players who lose the face under speed or target pressure and need a simple start-line reset.",
    setup:
      "Start ARC in Coach Plan mode with Driver. Keep the same target and tee height for the first three blocks.",
    coachCue:
      "Hold the face-to-start-line cue through the whole session. Do not chase carry until the final block.",
    homework:
      "Run the full plan once, save the final five swings, and report which block held the start line best.",
    betweenBlocks:
      "Pause between blocks, name the miss window, and only add speed if the line stayed playable.",
    focusLabel: "Driver start line",
    clubLabel: "Driver",
    blocks: [
      {
        id: "driver-baseline",
        balls: 10,
        title: "Baseline window",
        club: "Driver",
        camera: "Down-the-line",
        cadence: "Full routine.",
        how: "Hit stock drivers without correcting.",
        goal: "Find the current start-line window.",
      },
      {
        id: "driver-gate",
        balls: 14,
        title: "Gate hold",
        club: "Driver",
        camera: "Down-the-line",
        cadence: "Full routine.",
        how: "Use a two-tee gate or start-line corridor. Stop after two same-side misses.",
        goal: "Narrow the start-line window before speed goes up.",
      },
      {
        id: "driver-pressure",
        balls: 12,
        title: "Target pressure",
        club: "Driver",
        camera: "Same angle",
        cadence: "Full routine.",
        how: "Keep the same cue while changing only the target width.",
        goal: "See whether the face stays stable when the picture tightens.",
      },
      {
        id: "driver-finish",
        balls: 12,
        title: "Playable finish",
        club: "Driver",
        camera: "Same angle",
        cadence: "Full routine.",
        how: "Score each swing as playable or not and finish without cleanup reps.",
        goal: "Leave with an honest playable-driver read.",
      },
    ],
  },
  {
    id: "mcnally-5w-validation",
    templateId: "dev-template-mcnally-5w",
    title: "150-ball fairway validation bucket",
    kind: "measurement_test",
    totalBalls: 150,
    summary:
      "Bias the bucket toward 5 fairway because fairway wood is the only residual bucket currently in shadow mode. Add a clean Driver block now that Toptracer labels are explicit, then finish with a holdout block.",
    setup:
      "Start a fresh ARC range session per block. Before each block, set the Toptracer club by hand and confirm the label. Make sure the watch says Mark shot. Tap once after each real ball only.",
    coachCue:
      "Follow the range plan. Prioritize clean 5 fairway labels, saved block boundaries, and a holdout finish.",
    homework:
      "After the bucket, open each Toptracer session detail, run the GraphQL export, and tag the final mixed block as holdout. Do not train on holdout rows.",
    betweenBlocks:
      "Stop and save ARC after each block. Confirm the Toptracer club label before the first ball of every block and before each holdout mini-set.",
    focusLabel: "Fairway residual validation",
    clubLabel: "5 fairway + Driver",
    blocks: [
      {
        id: "sync-check",
        balls: 10,
        title: "Sync check",
        club: "8-iron + 5W",
        camera: "5 easy 8i DTL, 5 easy 5W side view",
        cadence: "Deliberate setup. Do not chase speed.",
        how: "Confirm ARC accepts shots, watch marks increment, Toptracer sees shots, Toptracer club labels match the club in hand, and McNally emits CHS on 5W.",
        goal: "Green-light the capture before spending the bucket.",
      },
      {
        id: "5w-side-primary",
        balls: 45,
        title: "Fairway side-view primary",
        club: "5 fairway",
        camera: "Side view",
        cadence: "7-8 seconds between balls.",
        how: "Normal stock swings, not max effort. Keep setup and ball position stable. Confirm Toptracer says 5 fairway before ball one.",
        goal: "Stress the only residual bucket currently in shadow mode with clean labels.",
      },
      {
        id: "5w-dtl-check",
        balls: 25,
        title: "Fairway DTL check",
        club: "5 fairway",
        camera: "Down-the-line",
        cadence: "7-8 seconds between balls.",
        how: "Same club and speed intent as the side-view block. Confirm Toptracer still says 5 fairway.",
        goal: "Separate camera-angle effects from actual fairway-wood flight.",
      },
      {
        id: "driver-label-check",
        balls: 20,
        title: "Driver label check",
        club: "Driver",
        camera: "Down-the-line",
        cadence: "Full routine between swings.",
        how: "Normal driver swings. Confirm Toptracer says Driver before ball one and keep the same target picture.",
        goal: "Start clean Driver coverage with explicit labels rather than relying on historical default-driver rows.",
      },
      {
        id: "3h-label-check",
        balls: 20,
        title: "Hybrid label check",
        club: "3 hybrid",
        camera: "Side view",
        cadence: "7-8 seconds between balls.",
        how: "Normal stock swings. Confirm Toptracer says hybrid before the first ball.",
        goal: "Add trusted hybrid rows without distracting from the fairway test.",
      },
      {
        id: "6i-label-check",
        balls: 15,
        title: "Mid-iron label check",
        club: "6-iron",
        camera: "Down-the-line",
        cadence: "7-8 seconds between balls.",
        how: "Normal full swings. Keep setup and target picture stable.",
        goal: "Keep a mid-iron sanity label without overfeeding an already stronger bucket.",
      },
      {
        id: "holdout-mixed",
        balls: 15,
        title: "Holdout mix",
        club: "5W, Driver, 3H",
        camera: "Mixed; save as one holdout session",
        cadence: "Full routine between swings.",
        how: "Hit 5 balls with each club in order. Confirm the Toptracer club label before each 5-ball mini-set.",
        goal: "Create a clean validation set that is not used for training.",
      },
    ],
  },
  {
    id: "pre-lesson-verification",
    templateId: "dev-template-pre-lesson-verification",
    title: "36-ball pre-lesson verification",
    kind: "measurement_test",
    totalBalls: 36,
    summary:
      "A compact measurement bucket for verifying what held since the last lesson before prescribing the next change.",
    setup:
      "Start a fresh ARC session and keep the same camera angle for the whole verification unless the coach explicitly asks for a second view.",
    coachCue:
      "Do not coach mid-bucket. Let the first two blocks show what held before making any correction.",
    homework:
      "Upload the anchor media from each block and send the summary before the lesson starts.",
    betweenBlocks:
      "Pause, name the dominant miss, and keep the next block honest rather than fixing between swings.",
    focusLabel: "Pre-lesson verification",
    clubLabel: "Primary lesson club",
    blocks: [
      {
        id: "verification-baseline",
        balls: 12,
        title: "Baseline read",
        club: "Primary lesson club",
        camera: "Coach-selected angle",
        cadence: "Full routine.",
        how: "Hit stock swings with no verbal reminder after ball one.",
        goal: "See what the player owns without rescue coaching.",
      },
      {
        id: "verification-transfer",
        balls: 12,
        title: "Transfer check",
        club: "Primary lesson club + one neighboring club",
        camera: "Same angle",
        cadence: "Full routine.",
        how: "Alternate clubs every three balls while holding the same intent.",
        goal: "Check whether the pattern survives a club change.",
      },
      {
        id: "verification-finish",
        balls: 12,
        title: "Lesson handoff",
        club: "Primary lesson club",
        camera: "Same angle",
        cadence: "Full routine.",
        how: "Finish with scored swings and no extra reps.",
        goal: "Leave the coach a clean decision point for the next lesson.",
      },
    ],
  },
  {
    id: "next-lesson-homework",
    templateId: "dev-template-homework-ladder",
    title: "30-ball next-lesson homework",
    kind: "homework",
    totalBalls: 30,
    summary:
      "A small homework block for students who need structure without a full testing bucket.",
    setup:
      "Start ARC in Coach Plan mode. Keep the same target for the first two blocks.",
    coachCue:
      "Follow the plan before experimenting. Send the coach the final five swings.",
    homework:
      "Complete once before the next lesson and reply with what changed in the final block.",
    betweenBlocks: "Take one minute between blocks and write the miss before moving on.",
    focusLabel: "Next-lesson cue",
    clubLabel: "Assigned club",
    blocks: [
      {
        id: "warmup",
        balls: 6,
        title: "Warmup read",
        club: "Assigned club",
        camera: "Coach choice",
        cadence: "Comfortable pace.",
        how: "Smooth swings at 70%. Do not correct yet.",
        goal: "Find the starting pattern.",
      },
      {
        id: "cue-block",
        balls: 12,
        title: "Cue block",
        club: "Assigned club",
        camera: "Same angle",
        cadence: "8 seconds between balls.",
        how: "Use only the coach cue. Stop after two same-side misses and reset.",
        goal: "See whether the cue changes the miss.",
      },
      {
        id: "transfer",
        balls: 12,
        title: "Transfer block",
        club: "Assigned club + one longer club",
        camera: "Same angle",
        cadence: "Full routine.",
        how: "Alternate clubs every three balls while keeping the cue.",
        goal: "Check whether the pattern survives a club change.",
      },
    ],
  },
  {
    id: "wedge-distance-matrix",
    templateId: "dev-template-wedge-distance-matrix",
    title: "36-ball wedge distance matrix",
    kind: "homework",
    totalBalls: 36,
    summary:
      "A three-window wedge ladder for players who need cleaner carry control and a more reliable landing band.",
    setup:
      "Start ARC in Coach Plan mode. Set three landing windows before ball one and keep the same wedge for the full matrix.",
    coachCue:
      "Match rehearsal length to finish length. Do not change the target until the block says move.",
    homework:
      "Complete the full matrix once and send the final five swings from the hardest distance.",
    betweenBlocks:
      "Change only the landing window between blocks. Keep the same cue and setup picture.",
    focusLabel: "Wedge distance control",
    clubLabel: "Gap or sand wedge",
    blocks: [
      {
        id: "wedge-short",
        balls: 12,
        title: "Short window",
        club: "Gap or sand wedge",
        camera: "Face-on",
        cadence: "Comfortable rhythm.",
        how: "Land 12 shots into the shortest carry window before moving on.",
        goal: "Own the shortest stock window first.",
      },
      {
        id: "wedge-stock",
        balls: 12,
        title: "Stock window",
        club: "Gap or sand wedge",
        camera: "Same angle",
        cadence: "7-8 seconds between balls.",
        how: "Keep the same cue and move only to the middle landing window.",
        goal: "Carry the same strike quality into the stock yardage.",
      },
      {
        id: "wedge-long",
        balls: 12,
        title: "Long window",
        club: "Gap or sand wedge",
        camera: "Same angle",
        cadence: "Full routine.",
        how: "Finish with the longest window and no cleanup shots after a miss.",
        goal: "Leave the coach a clean long-window read.",
      },
    ],
  },
  {
    id: "fairway-transfer-bridge",
    templateId: "dev-template-fairway-transfer-bridge",
    title: "42-ball fairway transfer bridge",
    kind: "homework",
    totalBalls: 42,
    summary:
      "A fairway-wood bridge for players who can hit the stock swing in practice but lose it when the club or target changes.",
    setup:
      "Start ARC in Coach Plan mode with the fairway club you actually use on the course. Keep the same target for the first two blocks.",
    coachCue:
      "Hold the same setup checkpoint through every block. Do not jump to Driver until the fairway pattern is playable.",
    homework:
      "Complete the plan once this week and send the anchor swing from the block that transferred best.",
    betweenBlocks:
      "Pause, name whether the miss was start-line or strike-based, then continue only when the next block is selected.",
    focusLabel: "Fairway transfer",
    clubLabel: "3-wood or 5 fairway",
    blocks: [
      {
        id: "fairway-baseline",
        balls: 12,
        title: "Baseline fairway read",
        club: "3-wood or 5 fairway",
        camera: "Side view",
        cadence: "7-8 seconds between balls.",
        how: "Hit stock swings from the preferred lie/setup.",
        goal: "Find the starting fairway pattern.",
      },
      {
        id: "fairway-constraint",
        balls: 12,
        title: "Constraint set",
        club: "3-wood or 5 fairway",
        camera: "Same angle",
        cadence: "Full routine.",
        how: "Keep one setup checkpoint and stop after two repeated misses.",
        goal: "Stabilize the strike pattern before transfer.",
      },
      {
        id: "fairway-transfer",
        balls: 9,
        title: "Target transfer",
        club: "3-wood or 5 fairway",
        camera: "Same angle",
        cadence: "Full routine.",
        how: "Narrow the target picture without changing the cue.",
        goal: "See whether the fairway pattern survives a tighter task.",
      },
      {
        id: "fairway-companion",
        balls: 9,
        title: "Long-club companion",
        club: "Driver or hybrid",
        camera: "Same angle",
        cadence: "Full routine.",
        how: "Move to one companion club while holding the same setup feel.",
        goal: "Check whether the pattern generalizes beyond the fairway club.",
      },
    ],
  },
  {
    id: "contact-window-ladder",
    templateId: "dev-template-contact-window-ladder",
    title: "42-ball contact window ladder",
    kind: "coach_plan",
    totalBalls: 42,
    summary:
      "A mid-iron contact ladder for players who need a cleaner low point and tighter strike window before worrying about speed.",
    setup:
      "Start ARC in Coach Plan mode with a mid-iron. Keep ball position and target picture the same until the final block.",
    coachCue:
      "Chase strike quality first. Do not add speed until the strike window holds for a full block.",
    homework:
      "Complete once before the next lesson and note which block tightened contact the most.",
    betweenBlocks:
      "Pause, name the dominant strike miss, and keep the same cue until the next block says transfer.",
    focusLabel: "Contact window",
    clubLabel: "7-iron or 8-iron",
    blocks: [
      {
        id: "contact-baseline",
        balls: 10,
        title: "Baseline strike read",
        club: "7-iron or 8-iron",
        camera: "Face-on",
        cadence: "Comfortable rhythm.",
        how: "Hit stock swings and name the dominant strike miss.",
        goal: "Find the current low-point pattern.",
      },
      {
        id: "contact-ladder",
        balls: 14,
        title: "Contact ladder",
        club: "7-iron or 8-iron",
        camera: "Face-on",
        cadence: "7-8 seconds between balls.",
        how: "Move through three strike checkpoints without changing the target.",
        goal: "Tighten low point before speed rises.",
      },
      {
        id: "contact-transfer",
        balls: 10,
        title: "Transfer window",
        club: "7-iron or 8-iron",
        camera: "Same angle",
        cadence: "Full routine.",
        how: "Keep the same cue while the target picture narrows slightly.",
        goal: "See whether the strike window survives a sharper task.",
      },
      {
        id: "contact-finish",
        balls: 8,
        title: "Playable finish",
        club: "7-iron or 8-iron",
        camera: "Same angle",
        cadence: "Full routine.",
        how: "Finish with scored swings only and no cleanup reps.",
        goal: "Leave with an honest contact read.",
      },
    ],
  },
  {
    id: "free-practice-guardrails",
    templateId: "dev-template-free-practice-guardrails",
    title: "Free practice with guardrails",
    kind: "free_practice_guardrails",
    totalBalls: 45,
    summary:
      "Use when a student wants freedom but the coach still needs a useful read afterward.",
    setup:
      "Start ARC in Free Practice, but keep these checkpoints visible.",
    coachCue:
      "Explore, but log the club and miss after each mini-block.",
    homework:
      "Send the coach the one block that felt best and the one block that got away.",
    betweenBlocks: "Change only one variable between blocks.",
    focusLabel: "Free practice guardrails",
    clubLabel: "Primary club",
    blocks: [
      {
        id: "stock",
        balls: 15,
        title: "Stock pattern",
        club: "Primary club",
        camera: "Preferred angle",
        cadence: "Normal practice rhythm.",
        how: "Hit the natural shot without drills.",
        goal: "Establish the baseline.",
      },
      {
        id: "experiment",
        balls: 15,
        title: "One-variable test",
        club: "Primary club",
        camera: "Same angle",
        cadence: "Normal practice rhythm.",
        how: "Change one thing: cue, target, or tempo. Not all three.",
        goal: "Separate cause from noise.",
      },
      {
        id: "confirm",
        balls: 15,
        title: "Confirm",
        club: "Primary club",
        camera: "Same angle",
        cadence: "Full routine.",
        how: "Return to the best version and count playable shots.",
        goal: "Leave with a pattern the coach can review.",
      },
    ],
  },
  {
    id: "putting-pace-ladder",
    templateId: "dev-template-putting-pace-ladder",
    title: "27-ball putting pace ladder",
    kind: "homework",
    totalBalls: 27,
    summary:
      "A simple putting starter for connecting pace control across short, stock, and long-lag distances without overcoaching mechanics.",
    setup:
      "Start ARC in Coach Plan mode on the putting surface. Set one short, one stock, and one long-lag distance before ball one.",
    coachCue:
      "Hold one pace cue for the full ladder. Do not change stance or stroke thought block to block.",
    homework:
      "Complete the ladder once and send the final summary on which distance was hardest to hold.",
    betweenBlocks:
      "Move only the distance between blocks. Keep the same pace cue and same routine.",
    focusLabel: "Putting pace",
    clubLabel: "Putter",
    blocks: [
      {
        id: "putt-short",
        balls: 9,
        title: "Short pace",
        club: "Putter",
        camera: "Coach-selected angle",
        cadence: "Normal routine.",
        how: "Roll nine putts to the shortest target without changing the cue.",
        goal: "Own pace on the easiest window first.",
      },
      {
        id: "putt-stock",
        balls: 9,
        title: "Stock pace",
        club: "Putter",
        camera: "Same angle",
        cadence: "Normal routine.",
        how: "Move to the stock distance and keep the same pace intent.",
        goal: "Transfer the cue to the normal make-range window.",
      },
      {
        id: "putt-lag",
        balls: 9,
        title: "Lag finish",
        club: "Putter",
        camera: "Same angle",
        cadence: "Normal routine.",
        how: "Finish at the long-lag distance with no extra reads or cleanup rolls.",
        goal: "Leave with a clean lag-pace picture for the coach.",
      },
    ],
  },
];

function readPagesDemoBypass(): boolean {
  if (typeof window === "undefined") return false;
  if (!window.location.hostname.endsWith("pages.dev")) return false;

  const storageKey = "arc.coach.demoBypass";
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("demo") === "1") {
      window.sessionStorage?.setItem(storageKey, "1");
      return true;
    }
    return window.sessionStorage?.getItem(storageKey) === "1";
  } catch {
    return false;
  }
}

const DEV_AUTH_BYPASS =
  (import.meta.env.DEV && import.meta.env.VITE_REQUIRE_AUTH !== "1") || readPagesDemoBypass();

const DEV_COACH_USER = {
  id: "dev-coach",
  email: "coach@arc.local",
} as User;

function App() {
  const [locationKey, setLocationKey] = useState(() => window.location.href);
  // `locationKey` is the intentional invalidation key — the memo body
  // reads `window.location.pathname` directly but we want a fresh
  // parse whenever any part of the URL changes (pushState / popstate).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const route = useMemo(() => parseRoute(window.location.pathname), [locationKey]);

  useEffect(() => {
    const onPopState = () => setLocationKey(window.location.href);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    if (window.location.pathname === "/") {
      navigate("/today", { replace: true });
      setLocationKey(window.location.href);
    }
  }, []);

  function handleNavigate(path: string, replace = false) {
    navigate(path, { replace });
    setLocationKey(window.location.href);
  }

  // Auth/public/preview/404 surfaces are full-bleed (no app shell).
  // The signed-in app routes are wrapped in the editorial sidebar +
  // toolbar shell.
  const isFullBleed =
    route.kind === "authCallback" ||
    route.kind === "preview" ||
    route.kind === "publicCoach" ||
    route.kind === "notFound";

  if (isFullBleed) {
    return (
      <>
        <ThemeApply />
        <main className="arc-shell">
          {route.kind === "authCallback" ? (
            <AuthCallback onNavigate={handleNavigate} />
          ) : route.kind === "preview" ? (
            <UnauthPreviewPage token={route.token} />
          ) : route.kind === "publicCoach" ? (
            <PublicCoachProfilePage slug={route.slug} />
          ) : (
            <div className="signin-page">
              <div className="s-head">
                <span className="tag">// Route</span>
                <h1 className="display">This coach page is not available.</h1>
                <p className="lede">Open Today to get back to the dashboard.</p>
              </div>
              <a className="btn primary" href="/today" style={{ width: "fit-content" }}>
                Go to Today
              </a>
            </div>
          )}
        </main>
        <CoachBuildStamp locationKey={locationKey} />
      </>
    );
  }

  return (
    <>
      <ThemeApply />
      <div className="app-shell">
        <CoachSidebar route={route} onNavigate={handleNavigate} />
        <main className="app-main">
          <CoachToolbar route={route} onNavigate={handleNavigate} />
          <div className="app-view">
            <ProtectedRoute route={route} onNavigate={handleNavigate} />
          </div>
        </main>
      </div>
      <CoachBuildStamp locationKey={locationKey} />
    </>
  );
}

function CoachBuildStamp({ locationKey }: { locationKey: string }) {
  const [manifest, setManifest] = useState<CoachBuildManifest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const visible = useMemo(() => readBuildStampPreference(locationKey), [locationKey]);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;

    async function loadManifest() {
      try {
        const nextManifest = await fetchCoachBuildManifest();
        if (cancelled) return;
        setManifest(nextManifest);
        setError(null);
      } catch (nextError) {
        if (cancelled) return;
        setManifest(null);
        setError(nextError instanceof Error ? nextError.message : "Build manifest unavailable.");
      }
    }

    void loadManifest();
    return () => {
      cancelled = true;
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <footer className="arc-build-stamp" aria-label="Coach build information">
      <strong>Build</strong>
      {manifest ? (
        <>
          <span>{formatCoachBuildStamp(manifest)}</span>
          <small>{formatCoachBuildDetail(manifest)}</small>
          <a href="/version.json" target="_blank" rel="noreferrer">
            version.json
          </a>
        </>
      ) : (
        <small>{error ?? "Loading build metadata."}</small>
      )}
    </footer>
  );
}

function ProtectedRoute({
  route,
  onNavigate,
}: {
  route: Exclude<Route, { kind: "preview" | "publicCoach" | "authCallback" | "notFound" }>;
  onNavigate: (path: string, replace?: boolean) => void;
}) {
  const { user, loading } = useAuthUser();
  const entitlement = useAuthEntitlement();
  const coachSlug = coachSlugFromSearch(window.location.search);
  const inviteCode = inviteCodeFromSearch(window.location.search);
  const forceLogin = new URLSearchParams(window.location.search).get("login") === "1";
  const nextPath = `${routePath(route)}${window.location.search}`;

  if (loading) return <Card eyebrow="ARC Coach" title="Loading dashboard." />;
  if (!user && route.kind === "today" && coachSlug && !forceLogin) {
    return <PublicCoachProfilePage slug={coachSlug} />;
  }
  if (!user) return <SignInGate next={nextPath} coachSlug={coachSlug} inviteCode={inviteCode} />;
  if (inviteCode && !DEV_AUTH_BYPASS) {
    return <AcceptInvitePage inviteCode={inviteCode} onNavigate={onNavigate} />;
  }

  const banner = <EntitlementBanner state={entitlement} onNavigate={onNavigate} />;

  switch (route.kind) {
    case "today":
      return (
        <>
          {banner}
          <CollaborationListPage
            title="Today"
            empty="No packets yet."
            coachSlug={coachSlug}
            onNavigate={onNavigate}
          />
        </>
      );
    case "students":
      return (
        <>
          {banner}
          <CollaborationListPage
            title="Students"
            empty="Your roster is empty."
            coachSlug={coachSlug}
            onNavigate={onNavigate}
          />
        </>
      );
    case "student":
      return (
        <>
          {banner}
          <StudentDetailPage
            id={route.id}
            entitlement={entitlement}
            onNavigate={onNavigate}
          />
        </>
      );
    case "studentMedia":
      if (!entitlement.isEntitled) {
        return (
          <EntitlementHardGate
            state={entitlement}
            surfaceTitle="Student media"
            onNavigate={onNavigate}
          />
        );
      }
      return <StudentMediaPage id={route.id} />;
    case "inbox":
      return (
        <>
          {banner}
          <InboxPage onNavigate={onNavigate} />
        </>
      );
    case "library":
      if (!entitlement.isEntitled) {
        return (
          <EntitlementHardGate
            state={entitlement}
            surfaceTitle="Library"
            onNavigate={onNavigate}
          />
        );
      }
      return <LibraryPage userId={user.id} />;
    case "billing":
      return <BillingPage />;
  }
}

function AuthCallback({
  onNavigate,
}: {
  onNavigate: (path: string, replace?: boolean) => void;
}) {
  const [state, setState] = useState<{
    title: string;
    body?: string;
    actionPath?: string;
    actionLabel?: string;
  }>({ title: "Signing you in." });

  useEffect(() => {
    let cancelled = false;

    async function finishAuth() {
      const url = new URL(window.location.href);
      const code = url.searchParams.get("code");
      const next = resolvePostAuthNextPath(url.searchParams.get("next"));
      const destination = postAuthNextPath(next, window.location.origin);
      const handoffSession = sessionFromAuthCallbackHash(window.location.hash);

      if (handoffSession) {
        window.history.replaceState({}, "", authCallbackPath(next));
        const { error } = await supabase.auth.setSession(handoffSession);
        if (error) {
          if (!cancelled) {
            setState({
              title: "ARC couldn't finish the app handoff.",
              body: "Open the coach portal again from ARC. If that still fails, request a fresh sign-in link from the web page.",
              actionPath: loginPathForNext(next),
              actionLabel: "Request a fresh link",
            });
          }
          return;
        }

        if (!cancelled) onNavigate(destination, true);
        return;
      }

      if (code) {
        const { error } = await supabase.auth.exchangeCodeForSession(code);
        if (error) {
          if (!cancelled) {
            const retryPath = loginPathForNext(next);
            window.history.replaceState({}, "", authCallbackPath(next));
            setState({
              title: authCallbackErrorTitle(error),
              body: authCallbackErrorBody(error),
              actionPath: retryPath,
              actionLabel: "Request a fresh link",
            });
          }
          return;
        }
        window.history.replaceState({}, "", authCallbackPath(destination));
      } else {
        const hasSession = await waitForAuthResolution({
          waitForSession: () =>
            waitForAuthSession({
              attempts: 12,
              waitMs: 150,
              readSession: async () => {
                const {
                  data: { session },
                } = await supabase.auth.getSession();
                return Boolean(session);
              },
            }),
          waitForEventSession: () =>
            new Promise<boolean>((resolve) => {
              const timeout = window.setTimeout(() => {
                subscription.unsubscribe();
                resolve(false);
              }, 2_000);
              const {
                data: { subscription },
              } = supabase.auth.onAuthStateChange((_event, session) => {
                if (!session) return;
                window.clearTimeout(timeout);
                subscription.unsubscribe();
                resolve(true);
              });
            }),
        });
        if (authCallbackNeedsFreshSession({ code, handoffSession, hasSession })) {
          if (!cancelled) {
            const retryPath = loginPathForNext(destination);
            window.history.replaceState({}, "", authCallbackPath(destination));
            setState({
              title: "That sign-in link needs a fresh browser session.",
              body: "We did not receive a completed sign-in from Apple or Google. Start again from this browser and ARC will bring you back to the same coach page.",
              actionPath: retryPath,
              actionLabel: "Try signing in again",
            });
          }
          return;
        }
      }

      if (!cancelled) onNavigate(destination, true);
    }

    void finishAuth();
    return () => {
      cancelled = true;
    };
  }, [onNavigate]);

  return (
    <Card eyebrow="Sign In" title={state.title}>
      {state.body ? <p className="arc-muted">{state.body}</p> : null}
      {state.actionPath ? (
        <a className="btn" href={state.actionPath}>
          {state.actionLabel ?? "Try again"}
        </a>
      ) : null}
    </Card>
  );
}

function CollaborationListPage({
  title,
  empty,
  coachSlug,
  onNavigate,
}: {
  title: string;
  empty: string;
  coachSlug?: string | null;
  onNavigate: (path: string, replace?: boolean) => void;
}) {
  const [rows, setRows] = useState<Collaboration[]>([]);
  const [status, setStatus] = useState("Loading coach rows.");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (DEV_AUTH_BYPASS) {
        const data = demoCoachCollaborations();
        setRows(data);
        setStatus(data.length === 0 ? empty : "Ready.");
        return;
      }
      try {
        const data = await listAcceptedCoachCollaborations(supabase);
        if (!cancelled) {
          setRows(data);
          setStatus(data.length === 0 ? empty : "Ready.");
        }
      } catch (error) {
        if (!cancelled) setStatus((error as Error).message);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [empty]);

  const isToday = title === "Today";
  const sortedRows = [...rows].sort(compareCoachRows);
  const todayDate = new Date().toLocaleDateString(undefined, { day: "numeric", month: "short" });

  if (rows.length === 0) {
    return (
      <>
        <div className="s-head">
          <span className="tag">// {title}{isToday ? ` · ${todayDate}` : ""}{coachSlug ? ` · ${coachSlug}` : ""}</span>
          <h1 className="display">{empty}</h1>
          <p className="lede">
            {status === empty ? "Accepted player invites become student rows here." : status}
          </p>
        </div>
        <div className="card flat" style={{ padding: 28, border: "1px dashed var(--arc-color-divider)", borderRadius: "var(--arc-radius-card)" }}>
          <span className="tag">// Have an invite code?</span>
          <div style={{ marginTop: 12 }}>
            <CoachInviteJoinForm
              label="Paste it below to join the workspace."
              buttonLabel="Join workspace"
              onJoin={(code) => onNavigate(invitePath(code))}
            />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="s-head">
        <span className="tag">// {title}{isToday ? ` · ${todayDate}` : ""}{coachSlug ? ` · ${coachSlug}` : ""}</span>
        <h1 className="display">
          {isToday ? (
            <>{rows.length} brief{rows.length === 1 ? "" : "s"} <em>waiting on you</em>.</>
          ) : (
            <>Roster <em>by signal</em>.</>
          )}
        </h1>
        <p className="lede">
          {isToday
            ? "Newest first. Each card is the one-line read and three benchmark-grounded facts across body, club, impact, and ball. Click in for the full session."
            : `${rows.length} students. Ordered by who needs the next cue.`}
        </p>
      </div>

      {isToday ? (
        <div className="today-grid">
          <div className="feed">
            {sortedRows.map((row) => <CollaborationCard key={row.id} row={row} />)}
          </div>
          <CoachTodayRail rows={rows} />
        </div>
      ) : (
        <StudentsRosterTable rows={sortedRows} onOpen={(id) => onNavigate(`/students/${encodeURIComponent(id)}`)} />
      )}
    </>
  );
}

function StudentsRosterTable({
  rows,
  onOpen,
}: {
  rows: Collaboration[];
  onOpen: (id: string) => void;
}) {
  return (
    <div className="roster">
      <div className="roster-head">
        <span>Player</span>
        <span>Last session</span>
        <span>Top read</span>
        <span>Confidence</span>
        <span>Owed reply</span>
        <span aria-hidden="true"></span>
      </div>
      {rows.map((row, index) => {
        const brief = row.briefV2;
        const sessionLabel = brief?.meta.sessionLabel || "No session yet";
        const captured = brief?.meta.generatedAt
          ? formatShortDate(brief.meta.generatedAt)
          : null;
        const oneLineRead = brief?.executiveSummary.oneLineRead || "Awaiting brief";
        const confidence = brief?.meta.dataConfidence ?? null;
        const owed = collaborationOwesReply(row);
        return (
          <button
            key={row.id}
            type="button"
            className="roster-row"
            onClick={() => onOpen(row.id)}
            data-tone={index === 0 ? "feature" : undefined}
          >
            <span className="cell player">
              <span className="avatar">{playerInitials(row.title)}</span>
              <span className="name">{row.title}</span>
            </span>
            <span className="cell mono">
              {captured ? `${captured} · ${sessionLabel}` : sessionLabel}
            </span>
            <span className="cell read" title={oneLineRead}>{oneLineRead}</span>
            <span className="cell">
              {confidence ? (
                <span className={`confidence ${confidence}`}>Confidence: {confidence}</span>
              ) : (
                <span className="tag">// Awaiting</span>
              )}
            </span>
            <span className="cell">
              {owed ? (
                <span className="confidence owed-tag">// Owed</span>
              ) : (
                <span className="tag" style={{ opacity: 0.5 }}>—</span>
              )}
            </span>
            <span className="cell arrow" aria-hidden="true">→</span>
          </button>
        );
      })}
    </div>
  );
}

function collaborationOwesReply(row: Collaboration): boolean {
  // Heuristic that mirrors the prototype's "owed reply" column: a brief
  // is "owed" when there is a fresh packet without a coach turn after it.
  const brief = row.briefV2;
  if (!brief) return false;
  const generated = new Date(brief.meta.generatedAt).getTime();
  if (Number.isNaN(generated)) return false;
  const updated = row.updatedAt ? new Date(row.updatedAt).getTime() : 0;
  return generated > updated;
}

function CoachTodayRail({ rows }: { rows: Collaboration[] }) {
  const briefedCount = rows.filter((r) => Boolean(r.briefV2)).length;
  const highConfidence = rows.filter((r) => r.briefV2?.meta.dataConfidence === "high").length;
  const priorities = rows.filter((r) => Boolean(r.briefV2?.executiveSummary.topPriority?.trim())).length;
  const rangeSessions = rows.reduce((sum, r) => sum + recentPracticeHistory(r).length, 0);

  type Lesson = { row: Collaboration; at: Date };
  const upcoming: Lesson[] = rows
    .map((r) => {
      const raw = r.briefV2?.collaboration?.nextLessonAt;
      if (!raw) return null;
      const at = new Date(raw);
      if (Number.isNaN(at.getTime()) || at.getTime() < Date.now()) return null;
      return { row: r, at };
    })
    .filter((entry): entry is Lesson => entry !== null)
    .sort((a, b) => a.at.getTime() - b.at.getTime())
    .slice(0, 3);

  return (
    <aside className="rail" aria-label="Today summary">
      <div className="panel">
        <h3>This week</h3>
        <div className="panel-row"><span className="l">Briefs to review</span><span className="v">{rows.length}</span></div>
        <div className="panel-row"><span className="l">With packet</span><span className="v">{briefedCount}</span></div>
        <div className="panel-row"><span className="l">High confidence</span><span className="v">{highConfidence}</span></div>
        <div className="panel-row"><span className="l">Range sessions</span><span className="v">{rangeSessions}</span></div>
        <div className="panel-row"><span className="l">Priorities ready</span><span className="v">{priorities}</span></div>
      </div>

      {upcoming.length > 0 ? (
        <div className="upcoming">
          <h3>Upcoming lessons</h3>
          {upcoming.map(({ row, at }) => (
            <div key={row.id} className="upcoming-row">
              <span className="when">
                {at.toLocaleDateString(undefined, { weekday: "short" }).toUpperCase()}<br />
                {at.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
              </span>
              <div>
                <div className="who">{row.title}</div>
                <div className="what">{row.briefV2?.meta.sessionLabel || "Lesson scheduled"}</div>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </aside>
  );
}

function CoachDashboardHero({
  title,
  rows,
  coachSlug,
}: {
  title: string;
  rows: Collaboration[];
  coachSlug?: string | null;
}) {
  const priorities = rows.filter((row) =>
    Boolean(row.briefV2?.executiveSummary.topPriority?.trim()),
  ).length;
  const rangeSessions = rows.reduce((count, row) => count + recentPracticeHistory(row).length, 0);
  const clubsSeen = new Set(
    rows.flatMap((row) => recentPracticeHistory(row).map((session) => session.club)),
  ).size;

  return (
    <section className="arc-dashboard-hero">
      <div className="arc-dashboard-hero-copy">
        <span className="tag">// {title}{coachSlug ? ` · ${coachSlug}` : ""}</span>
        <h1 className="display" style={{ fontSize: "clamp(28px, 3vw, 40px)", margin: "8px 0 12px" }}>
          {rows.length === 0 ? "No coach reads yet." : <>Coach <em>by signal</em>.</>}
        </h1>
        <p className="arc-dashboard-lede">
          {rows.length === 0
            ? "Accepted player invites become analytical rows as soon as ARC has a shared session."
            : `${rows.length} students practiced this week. Start with the range work, then open the students who need a cue before the next lesson.`}
        </p>
      </div>
      <div className="facts-row" aria-label="Coach dashboard metrics" style={{ marginTop: 24, gridTemplateColumns: "repeat(4, 1fr)" }}>
        <div className="fact">
          <span className="lbl">// Students</span>
          <span className="v">{rows.length}</span>
          <span className="source">Accepted workspaces</span>
        </div>
        <div className="fact">
          <span className="lbl">// Range sessions</span>
          <span className="v">{rangeSessions}</span>
          <span className="source">Recent practice blocks</span>
        </div>
        <div className="fact">
          <span className="lbl">// Clubs seen</span>
          <span className="v">{clubsSeen}</span>
          <span className="source">Across the roster</span>
        </div>
        <div className="fact">
          <span className="lbl">// Ready to coach</span>
          <span className="v">{priorities}</span>
          <span className="source">Students with a next cue</span>
        </div>
      </div>
    </section>
  );
}

function CoachFocusStrip({ rows }: { rows: Collaboration[] }) {
  const focusCounts = countDriverCategories(rows);
  const total = Object.values(focusCounts).reduce((sum, value) => sum + value, 0);
  const focusRows = [
    { key: "delivery", label: "Delivery", count: focusCounts.delivery },
    { key: "body", label: "Body", count: focusCounts.body },
    { key: "setup", label: "Setup", count: focusCounts.setup },
    { key: "load", label: "Load", count: focusCounts.load },
  ];

  return (
    <section className="arc-focus-strip" aria-label="Coach focus mix">
      <div>
        <p className="tag">// Focus Mix</p>
        <h2>What the roster is really working on.</h2>
      </div>
      <div className="arc-focus-bars">
        {focusRows.map((row) => {
          const width = total > 0 ? Math.max(8, Math.round((row.count / total) * 100)) : 0;
          return (
            <div key={row.key} className="arc-focus-bar-row">
              <span>{row.label}</span>
              <div className="arc-focus-track" aria-hidden="true">
                <div style={{ width: `${width}%` }} />
              </div>
              <strong>{row.count}</strong>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function CoachDecisionQueue({
  rows,
  title,
}: {
  rows: Collaboration[];
  title: string;
}) {
  const sortedRows = [...rows].sort(compareCoachRows);
  return (
    <section className="arc-decision-queue">
      <div className="psec-head" style={{ marginBottom: 20 }}>
        <h2>What they worked on, how it went.</h2>
        <span className="tag">// {title === "Today" ? "Today board" : "Roster board"} · {sortedRows.length} rows</span>
      </div>
      <div className="feed">
        {sortedRows.map((row) => (
          <CollaborationCard key={row.id} row={row} />
        ))}
      </div>
    </section>
  );
}

function CoachRosterPlanPanel({ rows }: { rows: Collaboration[] }) {
  const readyNow = rows.filter((row) => row.briefV2?.executiveSummary.topPriority?.trim()).length;
  const reviewFirst = rows.filter((row) => row.briefV2?.meta.dataConfidence === "low").length;
  const recentSessions = rows.reduce(
    (count, row) => count + recentPracticeHistory(row).length,
    0,
  );
  const clubCount = new Set(
    rows.flatMap((row) => recentPracticeHistory(row).map((session) => session.club)),
  ).size;
  const total = Math.max(1, rows.length);

  return (
    <section className="arc-card arc-quality-panel">
      <p className="tag">// Coach Plan</p>
      <h2>Where to spend attention.</h2>
      <div className="arc-quality-stack">
        <QualityBar label="Ready now" count={readyNow} total={total} tone="positive" />
        <QualityBar label="Review first" count={reviewFirst} total={total} tone="caution" />
        <QualityBar label="Range blocks" count={recentSessions} total={Math.max(1, recentSessions)} tone="muted" />
        <QualityBar label="Clubs covered" count={clubCount} total={Math.max(1, clubCount)} tone="muted" />
      </div>
    </section>
  );
}

function CoachGroupInsightsPanel({ rows }: { rows: Collaboration[] }) {
  const focusCounts = countDriverCategories(rows);
  const focusRows = Object.entries(focusCounts).sort((a, b) => b[1] - a[1]);
  const topFocus = focusRows[0]?.[1] ? focusRows[0] : null;
  const cueReadyRows = rows
    .filter((row) => row.briefV2?.executiveSummary.topPriority?.trim())
    .sort(compareCoachRows);
  const cueReady = cueReadyRows.slice(0, 3);
  const reviewFirst = rows.filter((row) => row.briefV2?.meta.dataConfidence === "low").length;

  return (
    <section className="arc-card arc-group-panel">
      <p className="tag">// Group Pulse</p>
      <h2>{topFocus ? `${capitalize(topFocus[0])} is leading the roster.` : "Roster is ready."}</h2>
      <div className="arc-workflow-list arc-section-body">
        <WorkflowRow label="Students" value={String(rows.length)} />
        <WorkflowRow label="Ready to coach" value={String(cueReadyRows.length)} />
        <WorkflowRow label="Review first" value={String(reviewFirst)} />
      </div>
      {cueReady.length > 0 ? (
        <div className="arc-cue-list">
          <span>Next three to coach</span>
          {cueReady.map((row) => (
            <strong key={row.id}>{row.title}</strong>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function QualityBar({
  label,
  count,
  total,
  tone,
}: {
  label: string;
  count: number;
  total: number;
  tone: "positive" | "caution" | "negative" | "muted";
}) {
  const width = Math.round((count / total) * 100);
  return (
    <div className="arc-quality-row">
      <div className="arc-quality-label">
        <span>{label}</span>
        <strong>{count}</strong>
      </div>
      <div className="arc-quality-track" aria-hidden="true">
        <div className={`arc-quality-fill arc-tone-${tone}`} style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Library — reusable drill / program templates
// ---------------------------------------------------------------------------

const FOCUS_TAG_LABEL: Record<CoachDrillTemplateFocusTag, string> = {
  general: "General",
  start_line: "Start line",
  tempo: "Tempo",
  posture: "Posture",
  contact: "Contact",
  short_game: "Short game",
  putting: "Putting",
  mobility: "Mobility",
  mental: "Mental",
};

type LibraryView =
  | { kind: "list" }
  | { kind: "editing"; templateId: string | null };

function LibraryPage({ userId }: { userId: string }) {
  const [view, setView] = useState<LibraryView>({ kind: "list" });
  const [templates, setTemplates] = useState<CoachDrillTemplate[]>([]);
  const [status, setStatus] = useState("Loading drills.");
  const [includeArchived, setIncludeArchived] = useState(false);

  const load = async (showArchived: boolean) => {
    if (DEV_AUTH_BYPASS) {
      const rows = demoDrillTemplates().filter((row) => showArchived || !row.isArchived);
      setTemplates(rows);
      setStatus(rows.length === 0 ? "Create your first drill to start building the library." : "");
      return;
    }

    try {
      const rows = await listDrillTemplates(supabase, {
        includeArchived: showArchived,
      });
      setTemplates(rows);
      setStatus(
        rows.length === 0
          ? "Create your first drill to start building the library."
          : "",
      );
    } catch (error) {
      setStatus((error as Error).message);
    }
  };

  useEffect(() => {
    void load(includeArchived);
  }, [includeArchived]);

  if (view.kind === "editing") {
    const initial = view.templateId
      ? templates.find((t) => t.id === view.templateId) ?? null
      : null;
    return (
      <DrillTemplateForm
        userId={userId}
        initial={initial}
        onCancel={() => setView({ kind: "list" })}
        onSaved={async () => {
          setView({ kind: "list" });
          await load(includeArchived);
        }}
      />
    );
  }

  return (
    <>
      <div className="s-head">
        <span className="tag">// Library · {templates.length} drill{templates.length === 1 ? "" : "s"}</span>
        <h1 className="display">
          Your <em>drill templates</em>.
        </h1>
        <p className="lede">
          Reusable prescriptions — balls, drill, cue, check, club, focus, intent. Build once, prescribe in one tap from a student row.
        </p>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginTop: 8 }}>
          <button
            type="button"
            className="btn primary"
            onClick={() => setView({ kind: "editing", templateId: null })}
          >
            + New drill
          </button>
          <label style={{ display: "inline-flex", gap: 8, alignItems: "center", fontSize: 13, color: "var(--arc-color-text-secondary)" }}>
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            <span className="tag" style={{ textTransform: "none", letterSpacing: 0, fontFamily: "Inter, sans-serif" }}>Show archived</span>
          </label>
        </div>
      </div>

      {templates.length === 0 ? (
        <div className="card flat" style={{ padding: 32, border: "1px dashed var(--arc-color-divider)", borderRadius: "var(--arc-radius-card)" }}>
          <span className="tag">// Empty</span>
          <p className="arc-muted" style={{ marginTop: 12 }}>
            {status || "Create your first drill to start building the library."}
          </p>
        </div>
      ) : (
        <div className="feed">
          {templates.map((template) => (
            <DrillTemplateCard
              key={template.id}
              template={template}
              onEdit={() => setView({ kind: "editing", templateId: template.id })}
              onReload={() => load(includeArchived)}
            />
          ))}
        </div>
      )}
    </>
  );
}

function DrillTemplateCard({
  template,
  onEdit,
  onReload,
}: {
  template: CoachDrillTemplate;
  onEdit: () => void;
  onReload: () => Promise<void>;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const rangePlan = rangePlanPrescriptionForTemplate(template);
  const shouldCollapseDetail =
    template.detail.length > 0 && (rangePlan !== null || shouldCollapseTemplateDetail(template.detail));
  const templateSummary = rangePlan
    ? rangePlan.summary
    : compactTemplateDetail(template.detail);

  const toggleArchived = async () => {
    if (DEV_AUTH_BYPASS) {
      await onReload();
      return;
    }

    setBusy("archive");
    setError(null);
    try {
      await updateDrillTemplate(supabase, template.id, {
        isArchived: !template.isArchived,
      });
      await onReload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const remove = async () => {
    if (DEV_AUTH_BYPASS) {
      await onReload();
      return;
    }

    if (!window.confirm(`Delete "${template.title}"? This cannot be undone.`)) return;
    setBusy("delete");
    setError(null);
    try {
      await deleteDrillTemplate(supabase, template.id);
      await onReload();
    } catch (err) {
      setError((err as Error).message);
      setBusy(null);
    }
  };

  return (
    <section className="drill-card">
      <header className="drill-card-head">
        <span className={`tag${template.isArchived ? " caution" : ""}`}>
          // {FOCUS_TAG_LABEL[template.focusTag]}{template.isArchived ? " · Archived" : ""}
        </span>
        <h2 className="drill-card-title">{template.title}</h2>
        <p className="drill-card-meta">
          {rangePlan
            ? `${rangePlan.totalBalls} balls · ${rangePlan.blocks.length} blocks · ${RANGE_PLAN_KIND_LABEL[rangePlan.kind]}`
            : formatDrillDuration(template)}
        </p>
      </header>

      {template.detail && shouldCollapseDetail ? (
        <>
          <p className="drill-card-summary">{templateSummary}</p>
          <details className="arc-collapsible-detail">
            <summary>{rangePlan ? "View full range plan" : "View full details"}</summary>
            {rangePlan ? (
              <RangePlanDetailContent plan={rangePlan} />
            ) : (
              <p className="arc-muted arc-template-detail-pre">{template.detail}</p>
            )}
          </details>
        </>
      ) : template.detail ? (
        <p className="drill-card-summary" style={{ whiteSpace: "pre-wrap" }}>
          {template.detail}
        </p>
      ) : null}

      <div className="drill-card-actions">
        <button type="button" className="btn" onClick={onEdit} disabled={busy !== null}>
          Edit
        </button>
        <button
          type="button"
          className="btn ghost"
          onClick={toggleArchived}
          disabled={busy !== null}
        >
          {template.isArchived ? "Restore" : "Archive"}
        </button>
        <button
          type="button"
          className="btn ghost"
          onClick={remove}
          disabled={busy !== null}
        >
          Delete
        </button>
      </div>
      {error ? <p className="signin-status negative" style={{ marginTop: 12 }}>Error: {error}</p> : null}
    </section>
  );
}

function rangePlanPrescriptionForTemplate(
  template: CoachDrillTemplate,
): RangePlanPrescription | null {
  return rangePlanPrescriptionFromTemplate(template, RANGE_PLAN_PRESETS);
}

function shouldCollapseTemplateDetail(detail: string): boolean {
  return detail.length > 360 || detail.split("\n").length > 5;
}

function compactTemplateDetail(detail: string): string {
  const firstParagraph = detail
    .split("\n")
    .map((line) => line.trim())
    .find(Boolean);
  if (!firstParagraph) return "";
  if (firstParagraph.length <= 180) return firstParagraph;
  return `${firstParagraph.slice(0, 177).trimEnd()}...`;
}

function DrillTemplateForm({
  userId,
  initial,
  onCancel,
  onSaved,
}: {
  userId: string;
  initial: CoachDrillTemplate | null;
  onCancel: () => void;
  onSaved: () => Promise<void>;
}) {
  const [title, setTitle] = useState(initial?.title ?? "");
  const [detail, setDetail] = useState(initial?.detail ?? "");
  const [focusTag, setFocusTag] = useState<CoachDrillTemplateFocusTag>(
    initial?.focusTag ?? "general",
  );
  const [swings, setSwings] = useState<string>(
    initial?.defaultDurationSwings != null ? String(initial.defaultDurationSwings) : "",
  );
  const [minutes, setMinutes] = useState<string>(
    initial?.defaultDurationMinutes != null ? String(initial.defaultDurationMinutes) : "",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    if (DEV_AUTH_BYPASS) {
      await onSaved();
      return;
    }

    try {
      const draft: CoachDrillTemplateDraft = {
        title,
        detail,
        focusTag,
        defaultDurationSwings: swings.trim() ? Number(swings) : null,
        defaultDurationMinutes: minutes.trim() ? Number(minutes) : null,
      };
      if (initial) {
        await updateDrillTemplate(supabase, initial.id, draft);
      } else {
        await createDrillTemplate(supabase, userId, draft);
      }
      await onSaved();
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  return (
    <section className="arc-card">
      <p className="tag">// Library</p>
      <h1>{initial ? "Edit drill" : "New drill"}</h1>
      <form className="arc-action-form" onSubmit={submit}>
        <label className="arc-signin-label">
          Title
          <input
            type="text"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Narrow start-line gate"
          />
        </label>

        <label className="arc-signin-label">
          Focus
          <select
            value={focusTag}
            onChange={(e) => setFocusTag(e.target.value as CoachDrillTemplateFocusTag)}
            style={{
              padding: "10px 12px",
              borderRadius: "8px",
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(0,0,0,0.3)",
              color: "inherit",
              font: "inherit",
            }}
          >
            {COACH_DRILL_TEMPLATE_FOCUS_TAGS.map((tag) => (
              <option key={tag} value={tag}>
                {FOCUS_TAG_LABEL[tag]}
              </option>
            ))}
          </select>
        </label>

        <label className="arc-signin-label">
          Detail
          <textarea
            rows={4}
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            placeholder="Hit 12 balls through a narrow start-line gate. Stop if the miss starts right twice."
          />
        </label>

        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          <label className="arc-signin-label" style={{ flex: "1 1 120px" }}>
            Default swings
            <input
              type="number"
              min={1}
              value={swings}
              onChange={(e) => setSwings(e.target.value)}
              placeholder="12"
            />
          </label>
          <label className="arc-signin-label" style={{ flex: "1 1 120px" }}>
            Default minutes
            <input
              type="number"
              min={1}
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
              placeholder="10"
            />
          </label>
        </div>

        <div className="arc-signin-controls">
          <button type="submit" className="btn" disabled={busy}>
            {busy ? "Saving…" : initial ? "Save changes" : "Create drill"}
          </button>
          <button
            type="button"
            className="btn"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
        </div>

        {error ? <p className="arc-meta">Error: {error}</p> : null}
      </form>
    </section>
  );
}

function formatDrillDuration(template: CoachDrillTemplate): string {
  const parts: string[] = [];
  if (template.defaultDurationSwings != null) {
    parts.push(
      template.defaultDurationSwings === 1
        ? "1 swing"
        : `${template.defaultDurationSwings} swings`,
    );
  }
  if (template.defaultDurationMinutes != null) {
    parts.push(
      template.defaultDurationMinutes === 1
        ? "1 min"
        : `${template.defaultDurationMinutes} min`,
    );
  }
  if (parts.length === 0) return "No default duration";
  return parts.join(" · ");
}

// ---------------------------------------------------------------------------
// Billing — Stripe subscription surface
// ---------------------------------------------------------------------------

function BillingPage() {
  const [subscription, setSubscription] = useState<CoachSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState<"checkout" | "portal" | null>(null);
  const [checkoutFlash, setCheckoutFlash] = useState<string | null>(null);

  // Surface ?checkout=success / ?checkout=cancelled / ?portal=returned
  // messaging set by the edge function return URLs.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get("checkout");
    const portal = params.get("portal");
    const linked = params.get("linked");
    if (checkout === "success") {
      setCheckoutFlash("Checkout complete. Your Coach+ access is live.");
    } else if (checkout === "cancelled") {
      setCheckoutFlash("Checkout cancelled. Your existing access is unchanged.");
    } else if (portal === "returned") {
      setCheckoutFlash("Billing settings updated.");
    } else if (linked === "google" || linked === "apple") {
      setCheckoutFlash(`${providerLabel(linked)} is now linked to this coach account.`);
    }
  }, []);

  const reload = async () => {
    if (DEV_AUTH_BYPASS) {
      setSubscription(demoCoachSubscription());
      setStatus(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const row = await getCoachSubscription(supabase);
      setSubscription(row);
      setStatus(null);
    } catch (error) {
      setStatus((error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const subscribe = async () => {
    if (DEV_AUTH_BYPASS) {
      setStatus("Stripe checkout is skipped in dev.");
      return;
    }

    setBusy("checkout");
    setStatus(null);
    try {
      const url = await startCoachCheckout(supabase, { returnPath: "/billing" });
      window.location.href = url;
    } catch (error) {
      setStatus((error as Error).message);
      setBusy(null);
    }
  };

  const manage = async () => {
    if (DEV_AUTH_BYPASS) {
      setStatus("Stripe portal is skipped in dev.");
      return;
    }

    setBusy("portal");
    setStatus(null);
    try {
      const url = await openCoachBillingPortal(supabase, { returnPath: "/billing" });
      window.location.href = url;
    } catch (error) {
      setStatus((error as Error).message);
      setBusy(null);
    }
  };

  const hasCustomer = Boolean(
    subscription && subscription.status !== "pending",
  );
  const isActive = subscription?.isEntitled ?? false;

  return (
    <>
      <div className="s-head">
        <span className="tag">// Billing · Coach+</span>
        <h1 className="display">
          <em>Coach+</em> subscription.
        </h1>
        <p className="lede">
          Unlimited student workspaces, the full drill library, and shareable session
          preview links. Payment runs through Stripe — card details never touch ARC.
        </p>
        {checkoutFlash ? (
          <p className="signin-status positive" style={{ marginTop: 4 }}>{checkoutFlash}</p>
        ) : null}
      </div>

      <section className="psec" style={{ marginTop: 0 }}>
        <div className="psec-head">
          <h2>Current status.</h2>
          <span className={`tag ${isActive ? "positive" : ""}`}>
            // {isActive ? "Active" : "Inactive"}
          </span>
        </div>

        {loading ? (
          <p className="arc-muted">Loading subscription.</p>
        ) : subscription ? (
          <div className="facts-row" style={{ marginTop: 0 }}>
            <div className="fact">
              <span className="lbl">// Status</span>
              <span className="v" style={{ fontSize: 22 }}>{coachSubscriptionStatusLabel(subscription.status)}</span>
              <span className="source">{isActive ? "Entitled to Coach+" : "No active Coach+ access"}</span>
            </div>
            <div className="fact">
              <span className="lbl">// {subscription.cancelAtPeriodEnd ? "Access ends" : "Next renewal"}</span>
              <span className="v" style={{ fontSize: 22 }}>
                {subscription.currentPeriodEnd ? formatShortDate(subscription.currentPeriodEnd) : "—"}
              </span>
              <span className="source">
                {subscription.currentPeriodEnd
                  ? subscription.cancelAtPeriodEnd
                    ? "Cancellation scheduled"
                    : "Auto-renews"
                  : "No period on file"}
              </span>
            </div>
            <div className="fact">
              <span className="lbl">// Trial</span>
              <span className="v" style={{ fontSize: 22 }}>
                {subscription.trialEnd ? formatShortDate(subscription.trialEnd) : "—"}
              </span>
              <span className="source">
                {subscription.trialEnd ? "Ends" : "No trial active"}
              </span>
            </div>
          </div>
        ) : (
          <div className="card flat" style={{ padding: 28, border: "1px dashed var(--arc-color-divider)", borderRadius: "var(--arc-radius-card)" }}>
            <span className="tag">// No subscription</span>
            <p className="arc-muted" style={{ marginTop: 12 }}>
              No subscription on file. Subscribe below to unlock Coach+.
            </p>
          </div>
        )}
      </section>

      <section className="psec">
        <div className="psec-head">
          <h2>Actions.</h2>
          <span className="tag">// Stripe</span>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            type="button"
            className="btn primary"
            onClick={subscribe}
            disabled={busy !== null}
          >
            {busy === "checkout" ? "Opening Stripe…" : isActive ? "Change plan" : "Subscribe to Coach+"}
          </button>
          {hasCustomer ? (
            <button
              type="button"
              className="btn"
              onClick={manage}
              disabled={busy !== null}
            >
              {busy === "portal" ? "Opening portal…" : "Manage billing"}
            </button>
          ) : null}
          <button
            type="button"
            className="btn ghost"
            onClick={reload}
            disabled={loading || busy !== null}
          >
            Refresh
          </button>
        </div>
        {status ? <p className="signin-status negative" style={{ marginTop: 12 }}>{status}</p> : null}
      </section>

      <AccountAccessCard onAccessChanged={reload} />

      <NotificationPreferencesCard />
    </>
  );
}

function AccountAccessCard({
  onAccessChanged,
}: {
  onAccessChanged: () => Promise<void>;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [identities, setIdentities] = useState<CoachLinkedIdentity[]>([]);
  const [loading, setLoading] = useState(true);
  const [linkBusy, setLinkBusy] = useState<"google" | "apple" | null>(null);
  const [mergeBusy, setMergeBusy] = useState<"create" | "redeem" | null>(null);
  const [mergeCodeInput, setMergeCodeInput] = useState("");
  const [generatedCode, setGeneratedCode] = useState<CoachMergeCode | null>(null);
  const [mergeResult, setMergeResult] = useState<CoachMergeResult | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const linkedProviders = useMemo(
    () => new Set(identities.map((identity) => identity.provider)),
    [identities],
  );

  async function load() {
    if (DEV_AUTH_BYPASS) {
      setUser(DEV_COACH_USER);
      setIdentities([
        {
          id: DEV_COACH_USER.id,
          provider: "email",
          email: DEV_COACH_USER.email ?? null,
          createdAt: null,
          lastSignInAt: null,
        },
      ]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const {
        data: { user: currentUser },
      } = await supabase.auth.getUser();
      setUser(currentUser);
      if (!currentUser) {
        setIdentities([]);
        return;
      }
      const rows = await listCoachLinkedIdentities(supabase);
      setIdentities(rows);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function linkProvider(provider: "google" | "apple") {
    if (DEV_AUTH_BYPASS) {
      setStatus(
        `Auth linking is skipped in dev. Set VITE_REQUIRE_AUTH=1 to test ${providerLabel(provider)}.`,
      );
      return;
    }

    const auth = supabase.auth as {
      linkIdentity?: (options: {
        provider: "google" | "apple";
        options?: { redirectTo?: string };
      }) => Promise<{ error?: { message?: string | null } | null }>;
    };

    if (typeof auth.linkIdentity !== "function") {
      setError("This build does not expose Supabase identity linking yet.");
      return;
    }

    setLinkBusy(provider);
    setStatus(null);
    setError(null);

    try {
      const next = `/billing?linked=${encodeURIComponent(provider)}`;
      rememberPostAuthNextPath(next);
      const redirectTo = authProviderRedirectUrl({
        currentOrigin: window.location.origin,
        siteUrl: COACH_SITE_URL,
      });
      const { error: linkError } = await auth.linkIdentity({
        provider,
        options: { redirectTo },
      });
      if (linkError) {
        throw new Error(linkError.message ?? "Could not link that sign-in method.");
      }
    } catch (err) {
      setError(identityLinkErrorMessage(provider, err));
      setLinkBusy(null);
    }
  }

  async function createMergeCode() {
    if (DEV_AUTH_BYPASS) {
      setStatus("Account merge is skipped in dev. Set VITE_REQUIRE_AUTH=1 to test the real flow.");
      return;
    }

    setMergeBusy("create");
    setStatus(null);
    setError(null);
    setMergeResult(null);
    try {
      const code = await createCoachMergeCode(supabase);
      setGeneratedCode(code);
      setStatus(
        "Merge code ready. Sign out, open the coach account you want to keep, and redeem it there.",
      );
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setMergeBusy(null);
    }
  }

  async function redeemMergeCode() {
    if (DEV_AUTH_BYPASS) {
      setStatus("Account merge is skipped in dev. Set VITE_REQUIRE_AUTH=1 to test the real flow.");
      return;
    }
    if (!normalizeMergeCode(mergeCodeInput)) return;

    setMergeBusy("redeem");
    setStatus(null);
    setError(null);
    try {
      const result = await redeemCoachMergeCode(supabase, mergeCodeInput);
      setGeneratedCode(null);
      setMergeCodeInput("");
      setMergeResult(result);
      setStatus(
        `Merged ${result.movedCollaborations} workspace${result.movedCollaborations === 1 ? "" : "s"}, ${result.movedAssignments} assignment${result.movedAssignments === 1 ? "" : "s"}, and ${result.movedAnnotations} note${result.movedAnnotations === 1 ? "" : "s"} into this coach account.`,
      );
      await Promise.all([load(), onAccessChanged()]);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setMergeBusy(null);
    }
  }

  return (
    <section className="psec">
      <div className="psec-head">
        <h2>Keep your coach logins on one account.</h2>
        <span className="tag">// Account · auth identity</span>
      </div>
      <p className="lede" style={{ marginBottom: 20 }}>
        Link Apple or Google onto this account before switching providers. If an
        Apple relay login already split into a second coach account, merge the extra
        account here and then relink that provider onto the account you want to keep.
      </p>

      {loading ? (
        <p className="arc-muted">Loading sign-in methods…</p>
      ) : (
        <div className="arc-account-panel">
          <div className="arc-account-grid">
            <div className="arc-account-block">
              <span className="tag">// Current account</span>
              <strong>{user?.email ?? "Signed-in coach"}</strong>
              <p className="arc-meta">
                Keep Coach+, accepted students, notes, and templates pinned to this
                auth identity.
              </p>
            </div>
            <div className="arc-account-block">
              <span className="tag">// Quick links</span>
              <div className="arc-account-actions">
                {(["google", "apple"] as const).map((provider) => {
                  const linked = linkedProviders.has(provider);
                  return (
                    <button
                      key={provider}
                      type="button"
                      className="btn"
                      disabled={linked || linkBusy !== null}
                      onClick={() => void linkProvider(provider)}
                    >
                      {linked
                        ? `${providerLabel(provider)} linked`
                        : linkBusy === provider
                          ? `Opening ${providerLabel(provider)}…`
                          : `Link ${providerLabel(provider)}`}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {identities.length ? (
            <div className="identity-rows">
              {identities.map((identity) => (
                <div
                  key={`${identity.provider}-${identity.id ?? identity.email ?? "identity"}`}
                  className="identity-row"
                >
                  <span className="tag">// {providerLabel(identity.provider)}</span>
                  <span className="value">{identity.email ?? "Email hidden by provider"}</span>
                  <span className="meta">
                    {identity.lastSignInAt
                      ? `Last used ${formatDate(identity.lastSignInAt)}`
                      : identity.createdAt
                        ? `Added ${formatDate(identity.createdAt)}`
                        : "Ready to use on sign-in"}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="tag">// No linked methods reported yet.</p>
          )}

          <div className="arc-account-grid">
            <div className="arc-account-block">
              <span className="tag">// Move this account elsewhere</span>
              <p className="arc-meta">
                Use this only from the account you do not want to keep.
              </p>
              <button
                type="button"
                className="btn"
                onClick={() => void createMergeCode()}
                disabled={mergeBusy !== null}
              >
                {mergeBusy === "create" ? "Generating code…" : "Generate merge code"}
              </button>
              {generatedCode ? (
                <div className="arc-account-code-panel">
                  <strong className="arc-account-code">{generatedCode.mergeCode}</strong>
                  <p className="arc-meta">
                    Expires {formatDate(generatedCode.expiresAt)}.
                    {generatedCode.sourceProviders.length
                      ? ` This account currently uses ${generatedCode.sourceProviders
                          .map(providerLabel)
                          .join(", ")}.`
                      : ""}
                  </p>
                </div>
              ) : null}
            </div>

            <div className="arc-account-block">
              <span className="tag">// Merge another account into this one</span>
              <p className="arc-meta">
                Sign into the coach account you want to keep, then redeem the code
                from the extra account.
              </p>
              <form
                className="arc-account-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  void redeemMergeCode();
                }}
              >
                <input
                  type="text"
                  inputMode="text"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  placeholder="ABCD-EFGH-IJKL-MNOP-QRST"
                  value={mergeCodeInput}
                  onChange={(event) => setMergeCodeInput(event.target.value)}
                />
                <button
                  type="submit"
                  disabled={mergeBusy !== null || !normalizeMergeCode(mergeCodeInput)}
                >
                  {mergeBusy === "redeem" ? "Merging…" : "Merge into this account"}
                </button>
              </form>
            </div>
          </div>

          {mergeResult ? (
            <div className="arc-action-helper">
              <strong>Merge complete.</strong>
              <p className="arc-meta">
                Source account: {mergeResult.sourceEmail ?? "Unknown email"}.
                Subscription: {mergeSummaryLabel(mergeResult.subscriptionAction)}.
                Notification preferences:{" "}
                {mergeSummaryLabel(mergeResult.notificationPreferencesAction)}.
              </p>
              <p className="arc-meta">
                Moved {mergeResult.movedCollaborations} workspace
                {mergeResult.movedCollaborations === 1 ? "" : "s"},{" "}
                {mergeResult.movedAssignments} assignment
                {mergeResult.movedAssignments === 1 ? "" : "s"},{" "}
                {mergeResult.movedAnnotations} note
                {mergeResult.movedAnnotations === 1 ? "" : "s"}, and{" "}
                {mergeResult.movedTemplates} template
                {mergeResult.movedTemplates === 1 ? "" : "s"}.
              </p>
              {mergeResult.warning ? <p className="arc-error">{mergeResult.warning}</p> : null}
              {mergeResult.relinkProviders.length ? (
                <div className="arc-account-actions">
                  {mergeResult.relinkProviders.map((provider) => (
                    <button
                      key={provider}
                      type="button"
                      className="btn"
                      disabled={!mergeResult.relinkReady || linkBusy !== null}
                      onClick={() => void linkProvider(provider)}
                    >
                      {linkBusy === provider
                        ? `Opening ${providerLabel(provider)}…`
                        : `Link ${providerLabel(provider)} now`}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      )}

      {status ? <p className="arc-success">{status}</p> : null}
      {error ? <p className="arc-error">{error}</p> : null}
    </section>
  );
}

function NotificationPreferencesCard() {
  const [userId, setUserId] = useState<string | null>(null);
  const [prefs, setPrefs] = useState<CoachNotificationPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (DEV_AUTH_BYPASS) {
        setUserId(DEV_COACH_USER.id);
        setPrefs(demoNotificationPreferences());
        setLoading(false);
        return;
      }

      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (cancelled) return;
        setUserId(user?.id ?? null);
        const row = await getCoachNotificationPreferences(supabase);
        if (!cancelled) {
          setPrefs(
            user
              ? resolveCoachNotificationPreferences(row, user.id)
              : null,
          );
        }
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  async function toggleEmail(next: boolean) {
    if (!userId) return;
    if (DEV_AUTH_BYPASS) {
      setPrefs({ ...demoNotificationPreferences(), emailOnStudentReply: next });
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const updated = await upsertCoachNotificationPreferences(supabase, {
        userId,
        emailOnStudentReply: next,
      });
      setPrefs(updated);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="psec">
      <div className="psec-head">
        <h2>Email me when a student replies.</h2>
        <span className="tag">// Notifications</span>
      </div>
      <p className="lede" style={{ marginBottom: 20 }}>
        Sends a single email per reply thread inside a 10-minute window so a
        flurry of replies doesn't flood your inbox. Turn this off any time.
      </p>
      {loading ? (
        <p className="arc-muted">Loading preferences…</p>
      ) : prefs ? (
        <div className="notif-row" role="group" aria-label="Email notifications">
          <span className="tag">
            // Email on student reply{saving ? " · saving…" : ""}
          </span>
          <span className="body">
            One email per reply thread inside a 10-minute window.
          </span>
          <button
            type="button"
            className="notif-switch"
            role="switch"
            aria-checked={prefs.emailOnStudentReply}
            data-on={prefs.emailOnStudentReply ? "true" : "false"}
            onClick={() => void toggleEmail(!prefs.emailOnStudentReply)}
            disabled={saving}
          />
        </div>
      ) : (
        <p className="arc-muted">Sign in to manage preferences.</p>
      )}
      {error ? <p className="signin-status negative" style={{ marginTop: 12 }}>Error: {error}</p> : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Inbox — student replies to assignments + cues
// ---------------------------------------------------------------------------

function InboxPage({
  onNavigate,
}: {
  onNavigate: (path: string, replace?: boolean) => void;
}) {
  const [threads, setThreads] = useState<InboxThreadRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<string | null>(null);
  const [openThread, setOpenThread] = useState<InboxThreadRow | null>(null);

  const reload = async () => {
    if (DEV_AUTH_BYPASS) {
      setThreads(demoInboxThreads());
      setStatus(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const rows = await listInboxThreads(supabase);
      setThreads(rows);
      setStatus(null);
    } catch (error) {
      setStatus((error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
    const off = coachEventBus.on("inbox-refresh", () => void reload());
    return off;
  }, []);

  if (openThread) {
    return (
      <InboxThreadView
        thread={openThread}
        onClose={async () => {
          setOpenThread(null);
          await reload();
        }}
        onJumpToStudent={() => {
          onNavigate(`/students/${encodeURIComponent(openThread.collaborationId)}`);
        }}
      />
    );
  }

  const totalUnread = threads.reduce((sum, t) => sum + t.unreadCount, 0);
  return (
    <>
      <div className="s-head">
        <span className="tag">// Inbox · {threads.length} thread{threads.length === 1 ? "" : "s"}{totalUnread > 0 ? ` · ${totalUnread} unread` : ""}</span>
        <h1 className="display">
          Student <em>replies</em>.
        </h1>
        <p className="lede">
          When a student responds to a cue or range plan from their phone, the thread lands here.
        </p>
      </div>

      {loading ? (
        <p className="arc-muted" style={{ padding: 24 }}>Loading replies.</p>
      ) : threads.length === 0 ? (
        <div className="card flat" style={{ padding: 32, border: "1px dashed var(--arc-color-divider)", borderRadius: "var(--arc-radius-card)" }}>
          <span className="tag">// Empty</span>
          <p className="arc-muted" style={{ marginTop: 12 }}>
            {status ??
              "Your range plans will show up here once a student responds from their phone."}
          </p>
        </div>
      ) : (
        <div className="feed">
          {threads.map((thread) => (
            <InboxThreadRowCard
              key={thread.assignmentId}
              thread={thread}
              onOpen={() => setOpenThread(thread)}
            />
          ))}
        </div>
      )}
    </>
  );
}

function InboxThreadRowCard({
  thread,
  onOpen,
}: {
  thread: InboxThreadRow;
  onOpen: () => void;
}) {
  const when = splitDateForBriefWhen(thread.latestReplyAt);
  return (
    <div
      className="brief-card"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <div className="brief-when">
        {when ? (
          <>
            <span className="day">{when.day}</span>
            <span className="month">{when.month}</span>
            <span className="time">{when.time}</span>
          </>
        ) : (
          <span className="tag">// New</span>
        )}
      </div>
      <div className="brief-body">
        <div className="brief-meta">
          <span className="who">{thread.collaborationTitle}</span>
          <span className="dot" />
          <span>{thread.assignmentIsCue ? "Cue reply" : "Range plan reply"}</span>
          {thread.studentEmail ? (
            <>
              <span className="dot" />
              <span>{thread.studentEmail}</span>
            </>
          ) : null}
        </div>
        <div className="brief-title">{thread.assignmentTitle}</div>
        <div className="brief-read">{thread.latestReplyBody}</div>
      </div>
      <div className="brief-status">
        {thread.unreadCount > 0 ? (
          <span className="confidence high">
            // {thread.unreadCount} unread
          </span>
        ) : (
          <span className="tag">// Read</span>
        )}
        <span className="arrow">→</span>
      </div>
    </div>
  );
}

function InboxThreadView({
  thread,
  onClose,
  onJumpToStudent,
}: {
  thread: InboxThreadRow;
  onClose: () => Promise<void>;
  onJumpToStudent: () => void;
}) {
  const [replies, setReplies] = useState<CoachAssignmentReply[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<string | null>(null);
  const [markedRead, setMarkedRead] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (DEV_AUTH_BYPASS) {
        setReplies(demoInboxReplies(thread.assignmentId));
        setMarkedRead(true);
        setLoading(false);
        return;
      }

      try {
        const rows = await listAssignmentReplies(supabase, thread.assignmentId);
        if (!cancelled) setReplies(rows);
        if (thread.unreadCount > 0 && !markedRead && !cancelled) {
          await markAssignmentRepliesRead(supabase, thread.assignmentId);
          setMarkedRead(true);
          notifyInboxRefresh();
        }
      } catch (error) {
        if (!cancelled) setStatus((error as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread.assignmentId]);

  return (
    <>
      <section className="arc-card">
        <p className="tag">
          // {thread.assignmentIsCue ? "Cue" : "Range Plan"} · {thread.collaborationTitle}
        </p>
        <h1>{thread.assignmentTitle}</h1>
        {thread.studentEmail ? (
          <p className="arc-muted">{thread.studentEmail}</p>
        ) : null}
        <div
          className="arc-section-body"
          style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}
        >
          <button type="button" className="btn" onClick={onJumpToStudent}>
            Open student workspace
          </button>
          <button type="button" className="btn" onClick={() => void onClose()}>
            Back to inbox
          </button>
        </div>
      </section>

      <section className="arc-card">
        <p className="tag">// Replies</p>
        {loading ? (
          <p className="arc-muted arc-section-body">Loading thread.</p>
        ) : status ? (
          <p className="arc-meta">{status}</p>
        ) : replies.length === 0 ? (
          <p className="arc-muted arc-section-body">No replies on this thread.</p>
        ) : (
          <div className="arc-stack arc-section-body">
            {replies.map((reply) => (
              <div key={reply.id} className="arc-thread-row">
                <p className="arc-meta">
                  {formatDate(reply.createdAt)}
                  {reply.audioPath ? " · voice note" : ""}
                </p>
                {reply.body ? (
                  <p style={{ whiteSpace: "pre-wrap" }}>{reply.body}</p>
                ) : null}
                {reply.audioPath ? (
                  <VoiceNotePlayer
                    audioPath={reply.audioPath}
                    durationSeconds={reply.audioDurationSeconds}
                  />
                ) : null}
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}

function StudentDetailPage({
  id,
  entitlement,
  onNavigate,
}: {
  id: string;
  entitlement: EntitlementState;
  onNavigate: (path: string, replace?: boolean) => void;
}) {
  const [collaboration, setCollaboration] = useState<Collaboration | null>(null);
  const [annotations, setAnnotations] = useState<CoachAnnotation[]>([]);
  const [assignments, setAssignments] = useState<CoachAssignment[]>([]);
  const [status, setStatus] = useState("Loading student.");
  // Tracks the brief we've already autotriggered ARC Coach for so
  // re-renders don't re-fire. Server-side dedupe via
  // ai_coach_runs(collaboration_id, run_key) is the source of truth;
  // this ref just avoids the redundant HTTP round-trip.
  const arcCoachAutoKeyRef = useRef<string | null>(null);

  async function reload() {
    if (DEV_AUTH_BYPASS) {
      const row =
        demoCoachCollaborations().find((candidate) => candidate.id === id) ??
        null;
      setCollaboration(row);
      setAnnotations(row ? demoCoachAnnotations(row.id) : []);
      setAssignments(row ? demoCoachAssignments(row.id) : []);
      setStatus(row ? "Ready." : "Collaboration not found.");
      return;
    }

    const [row, notes, drills] = await Promise.all([
      getCollaboration(supabase, id),
      listAnnotations(supabase, id),
      listAssignments(supabase, id),
    ]);
    setCollaboration(row);
    setAnnotations(notes);
    setAssignments(drills);
    setStatus(row ? "Ready." : "Collaboration not found.");
  }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        await reload();
      } catch (error) {
        if (!cancelled) setStatus((error as Error).message);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
    // `reload` is defined inline in this component — re-binding on
    // every render would loop. The effect depends only on the
    // route's `id` param.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Autotrigger ARC Coach for newly-landed briefs. The iOS app also
  // calls the function after a brief upload; whichever surface sees
  // the packet first wins, and the (collaboration_id, run_key) UNIQUE
  // index on ai_coach_runs makes the loser a no-op.
  useEffect(() => {
    if (DEV_AUTH_BYPASS) return;
    const brief = collaboration?.briefV2;
    if (!collaboration || !brief) return;
    const key = `${collaboration.id}:${brief.meta.generatedAt}`;
    if (arcCoachAutoKeyRef.current === key) return;
    arcCoachAutoKeyRef.current = key;
    void (async () => {
      try {
        const result = await invokeArcCoachPlan(supabase, collaboration.id);
        if (result.status === "planned") {
          await reload();
        }
      } catch {
        // Silent: the manual "Run ARC Coach" button remains available.
      }
    })();
    // `reload` is inline; the effect keys on the collaboration id and
    // the brief's generatedAt so it fires once per new packet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collaboration?.id, collaboration?.briefV2?.meta.generatedAt]);

  if (!collaboration) {
    return (
      <Card eyebrow="Student" title={status}>
        <p className="arc-muted">
          Either the row does not exist or you do not have access.
        </p>
      </Card>
    );
  }

  const cues = assignments.filter(
    (assignment) =>
      assignment.isCue &&
      assignment.status !== "archived" &&
      assignment.status !== "completed",
  );
  const cueHistory = assignments.filter(
    (assignment) => assignment.isCue && assignment.status !== "archived",
  );
  const nonCueAssignments = assignments.filter((assignment) => !assignment.isCue);
  const sessionMediaEnabled = canUseSessionMedia(collaboration);
  const surfaceTitle = collaborationSurfaceTitle(collaboration);
  const brief = collaboration.briefV2;
  const activePlanAssignment = latestActiveHumanPlan(nonCueAssignments);
  const latestArcPlanAssignment = latestArcAssistPlan(nonCueAssignments);
  const latestRangePlanStatus = collaboration.latestRangePlanStatus;

  return (
    <div className="arc-student-workbench arc-player-detail-page">
      <PlayerRelationshipHeader
        collaboration={collaboration}
        activePlanAssignment={activePlanAssignment}
        activeCueCount={cues.length}
        latestRangePlanStatus={latestRangePlanStatus}
        sessionMediaEnabled={sessionMediaEnabled}
      />

      <LatestSessionVerdict
        brief={brief}
        annotations={annotations}
        collaborationId={collaboration.id}
        sessionMediaEnabled={sessionMediaEnabled}
      />
      <LatestRangeStatusSection status={latestRangePlanStatus} />
      <AssignNextSessionSection
        collaboration={collaboration}
        entitlement={entitlement}
        assignments={nonCueAssignments}
        activePlanAssignment={activePlanAssignment}
        latestArcPlanAssignment={latestArcPlanAssignment}
        cues={cues}
        onNavigate={onNavigate}
        onSaved={reload}
      />

      <CoachingHistorySection
        collaboration={collaboration}
        annotations={annotations}
        assignments={assignments}
        cues={cueHistory}
        sessionMediaEnabled={sessionMediaEnabled}
        surfaceTitle={surfaceTitle}
      />
    </div>
  );
}

function PlayerRelationshipHeader({
  collaboration,
  activePlanAssignment,
  activeCueCount,
  latestRangePlanStatus,
  sessionMediaEnabled,
}: {
  collaboration: Collaboration;
  activePlanAssignment: CoachAssignment | null;
  activeCueCount: number;
  latestRangePlanStatus: Collaboration["latestRangePlanStatus"];
  sessionMediaEnabled: boolean;
}) {
  const brief = collaboration.briefV2;
  const history = recentPracticeHistory(collaboration);
  const bag = studentBagItems(collaboration);
  const contactLine = [collaboration.ownerEmail, collaboration.ownerPhone]
    .filter(Boolean)
    .join(" · ");
  const activePlan =
    activePlanAssignment?.rangePlanPrescription ??
    (activePlanAssignment ? rangePlanPrescriptionForAssignment(activePlanAssignment) : null);
  const lastSessionLabel = brief?.meta.generatedAt
    ? formatShortDate(brief.meta.generatedAt)
    : history[0]?.practicedAt
      ? formatShortDate(history[0].practicedAt)
      : "No captured session yet";
  const focusLabel =
    brief?.playbook.weeklyTheme ||
    brief?.playbook.nextSessionObjective ||
    brief?.meta.club ||
    "No active focus";
  const bagLabel =
    bag.length > 0
      ? `${bag.length} club${bag.length === 1 ? "" : "s"} tracked`
      : "Bag not loaded yet";
  const latestRangeSummary = latestRangePlanStatus
    ? latestRangePlanStatusSummary(latestRangePlanStatus)
    : null;

  return (
    <section className="player-header">
      <div className="player-header-left">
        <div className="player-avatar" aria-hidden="true">{playerInitials(collaboration.title)}</div>
        <div>
          <span className="tag">// Player · {relationshipStatusLabel(collaboration.status)}</span>
          <h1 className="display" style={{ fontSize: "clamp(28px, 3vw, 40px)", margin: "8px 0 6px" }}>
            {collaboration.title}
          </h1>
          <p className="arc-muted" style={{ fontSize: 13 }}>{contactLine || "ARC player"}</p>
        </div>
      </div>

      <div className="hero-meta player-header-meta">
        <span className="l">// Last session</span>
        <span className="v">{lastSessionLabel}</span>
        <span className="l">// Focus</span>
        <span className="v">{focusLabel}</span>
        <span className="l">// Bag</span>
        <span className="v">{bagLabel}</span>
        {activePlan ? (<><span className="l">// Active plan</span><span className="v">{activePlan.title}</span></>) : null}
        {latestRangeSummary ? (<><span className="l">// Range status</span><span className="v">{latestRangeSummary.headline}</span></>) : null}
        <span className="l">// Active cues</span>
        <span className="v">{activeCueCount} {activeCueCount === 1 ? "cue" : "cues"}</span>
        {sessionMediaEnabled ? (
          <>
            <span className="l">// Media</span>
            <span className="v">
              <a
                style={{ color: "var(--arc-color-accent-primary)", textDecoration: "none" }}
                href={buildStudentMediaPath(collaboration.id, {
                  sessionId: brief?.meta.sessionId || collaboration.sessionId,
                })}
              >
                View all swings →
              </a>
            </span>
          </>
        ) : null}
      </div>
    </section>
  );
}

function LatestRangeStatusSection({
  status,
}: {
  status: Collaboration["latestRangePlanStatus"];
}) {
  if (!status) return null;

  const summary = latestRangePlanStatusSummary(status);
  const activeBlock =
    status.blocks.find((block) => block.state === "active") ??
    (status.state === "completed"
      ? [...status.blocks].reverse().find((block) => block.state === "completed") ?? null
      : null);

  const toneClass = summary.tone === "positive" ? "positive" : "";

  return (
    <section className="psec" style={{ marginTop: 32 }}>
      <div className="psec-head">
        <h2>{status.planTitle}</h2>
        <span className={`tag ${toneClass}`}>// {summary.headline} · updated {formatShortDate(status.updatedAt)}</span>
      </div>
      <p className="lede" style={{ maxWidth: "72ch", marginBottom: 20 }}>{summary.detail}</p>

      <div className="hero-meta range-status-meta">
        <span className="l">// Progress</span><span className="v">{summary.progressLabel}</span>
        <span className="l">// Focus</span><span className="v">{status.focusLabel}</span>
        <span className="l">// Club</span><span className="v">{status.clubLabel}</span>
        <span className="l">// State</span><span className="v">{capitalize(status.state)}</span>
      </div>

      <div className="facts-row" style={{ marginTop: 28, gridTemplateColumns: "repeat(4, 1fr)" }}>
        <div className="fact">
          <span className="lbl">// Completed</span>
          <span className="v" style={{ fontSize: 24 }}>{summary.progressLabel}</span>
          <span className="source">{status.completedAt ? `Finished ${formatShortDate(status.completedAt)}` : "Live progress"}</span>
        </div>
        <div className="fact">
          <span className="lbl">// Current block</span>
          <span className="v" style={{ fontSize: 18 }}>{status.currentBlockTitle || activeBlock?.title || "No active block"}</span>
          <span className="source">{activeBlock ? `${activeBlock.completedBalls} of ${activeBlock.totalBalls} balls` : "No in-progress block"}</span>
        </div>
        <div className="fact">
          <span className="lbl">// Next block</span>
          <span className="v" style={{ fontSize: 18 }}>{status.nextBlockTitle || (status.state === "completed" ? "Plan complete" : "Finish current block")}</span>
          <span className="source">{status.nextBlockTitle ? "Next segment queued" : "No remaining blocks"}</span>
        </div>
        <div className="fact">
          <span className="lbl">// Session link</span>
          <span className="v" style={{ fontSize: 18 }}>{status.sessionId ? "Captured" : "Awaiting"}</span>
          <span className="source">{status.sessionId ? "Live captured session" : "No session attached yet"}</span>
        </div>
      </div>

      <div className="range-blocks" aria-label="Range block progress" style={{ marginTop: 24 }}>
        {status.blocks.map((block) => {
          const blockTone = block.state === "completed" ? "positive"
            : block.state === "active" ? "accent"
            : "";
          return (
            <article key={block.id} className="range-block">
              <div className="range-block-head">
                <div>
                  <span className="tag">// {block.club} · {block.camera}</span>
                  <strong className="range-block-title">{block.title}</strong>
                </div>
                <span className={`tag ${blockTone}`} style={{ fontSize: 11, fontWeight: 500 }}>
                  {block.completedBalls} / {block.totalBalls}
                </span>
              </div>
              <p className="arc-muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
                {block.state === "completed"
                  ? "Completed at the range."
                  : block.state === "active"
                    ? "Player is currently working this block."
                    : "Queued in the assigned plan."}
              </p>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function LatestSessionVerdict({
  brief,
  annotations,
  collaborationId,
  sessionMediaEnabled,
}: {
  brief: CoachBriefV2 | null;
  annotations: CoachAnnotation[];
  collaborationId: string;
  sessionMediaEnabled: boolean;
}) {
  if (!brief) {
    return (
      <section className="arc-card arc-latest-verdict">
        <div className="arc-section-heading">
          <div>
            <p className="tag">// Latest Session Verdict</p>
            <h2>No captured sessions yet.</h2>
          </div>
          <span className="arc-confidence arc-tone-muted">Awaiting evidence</span>
        </div>
        <p className="arc-muted arc-section-body">
          The next captured range session will land here as the coach&apos;s fastest read on
          what happened, what held up, and what the player should do next.
        </p>
      </section>
    );
  }

  const facts = verdictTopFacts(brief);
  const measurements = measurementHighlights(brief, 3);
  const samples = brief.representativeSamples.slice(0, 3);
  const stale = isBriefStale(brief);
  const verdictOverride = latestCoachVerdictOverride(annotations);
  const verdictRead = verdictOverride?.body.trim() || brief.executiveSummary.oneLineRead;
  const verdictSourceLabel = verdictOverride ? "Coach verdict" : "ARC verdict";
  const verdictSourceMeta = verdictOverride
    ? `${verdictOverride.title || "Coach restatement"} · ${formatShortDate(verdictOverride.updatedAt)}`
    : `${brief.executiveSummary.title} · ARC packet`;

  const drivers = brief.causalDrivers;
  const playbookSteps = brief.playbook.steps;
  const playbookMinutes = playbookSteps.reduce((sum, step) => sum + (step.durationMinutes ?? 0), 0);
  const coverageList = coverageLabels(brief.meta.dataCoverage);
  const nextLesson = brief.collaboration?.nextLessonAt
    ? new Date(brief.collaboration.nextLessonAt)
    : null;
  const dispersionPoints = dispersionPointsForBrief(brief);

  return (
    <section className={`brief-surface brief-surface-${brief.meta.dataConfidence}`}>
      <div className="brief-hero">
        <div>
          <span className="tag" style={{ marginBottom: 14, display: "inline-block" }}>
            // Brief · {brief.meta.packetId || verdictSourceLabel}
          </span>
          <div className="read">{verdictRead}</div>
          <p className="arc-muted" style={{ marginTop: 14, fontSize: 13 }}>{verdictSourceMeta}</p>
        </div>
        <div className="hero-side">
          <span className={`confidence ${brief.meta.dataConfidence}`}>
            Confidence: {brief.meta.dataConfidence}
          </span>
          {stale ? (
            <span className="tag caution" style={{ alignSelf: "flex-start" }}>// Stale packet</span>
          ) : null}
          <div className="hero-meta">
            <span className="l">// Session</span>
            <span className="v">{brief.meta.sessionLabel}</span>
            <span className="l">// Club</span>
            <span className="v">{brief.meta.club || "All clubs"}</span>
            <span className="l">// Captured</span>
            <span className="v">
              {new Date(brief.meta.generatedAt).toLocaleString(undefined, {
                day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
              })}
            </span>
            <span className="l">// Practice</span>
            <span className="v">{practiceModeLabel(brief.meta.practiceMode)}</span>
            <span className="l">// Coverage</span>
            <span className="v">{coverageList.length > 0 ? coverageList.join(" · ") : "Awaiting coverage"}</span>
            <span className="l">// Next lesson</span>
            <span className="v">
              {nextLesson
                ? `${nextLesson.toLocaleDateString(undefined, { weekday: "short" })} ${nextLesson.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`
                : "Not scheduled"}
            </span>
          </div>
        </div>
      </div>

      {brief.executiveSummary.topPriority ? (
        <div className="brief-priority">
          <span className="tag accent">// Next-session prescription</span>
          <p>{brief.executiveSummary.topPriority}</p>
        </div>
      ) : null}

      {verdictOverride ? (
        <div className="brief-override">
          <span className="tag">// ARC packet read</span>
          <p>{brief.executiveSummary.oneLineRead}</p>
        </div>
      ) : null}

      {facts.length > 0 ? (
        <>
          <span className="tag" style={{ display: "inline-block", marginBottom: 12 }}>
            // Benchmarks · BODY · CLUB · IMPACT · BALL
          </span>
          <div className="facts-row">
            {facts.map((fact, index) => {
              const provenanceLabel = provenanceDisplayLabel(fact.provenance);
              return (
                <div className="fact" key={`${fact.label}-${index}`}>
                  <span className="lbl">// {fact.label}</span>
                  <span className="v">{fact.value || "—"}</span>
                  {fact.delta ? <span className="delta">{fact.delta}</span> : null}
                  <span className="source">{provenanceLabel ? `${provenanceLabel} · ` : ""}{fact.source}</span>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <p className="arc-muted arc-section-body">
          No grounded facts in this packet yet. Review footage before making a stronger change.
        </p>
      )}

      {drivers.length > 0 ? (
        <section className="psec">
          <div className="psec-head">
            <h2>What's driving the result.</h2>
            <span className="tag">// Causal · {drivers.length} of {drivers.length}</span>
          </div>
          <div className="drivers">
            {drivers.map((d, i) => (
              <div className="driver" key={d.id}>
                <div className="num">
                  {String(i + 1).padStart(2, "0")}
                  <small>// {driverCategoryLabel(d.category)}</small>
                </div>
                <div className="meat">
                  <div className="ttl">{d.title}</div>
                  <p className="impact">{d.impactSummary}</p>
                  {d.evidence.length > 0 ? (
                    <ul className="evidence">
                      {d.evidence.map((e, idx) => <li key={idx}>{e}</li>)}
                    </ul>
                  ) : null}
                </div>
                {d.correctiveCue ? (
                  <div className="cue">
                    <span className="lbl">// Send cue</span>
                    <span className="text">{d.correctiveCue}</span>
                    <span className="meta">Confidence: {d.confidence}</span>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {dispersionPoints.length > 0 ? (
        <section className="psec">
          <div className="psec-head">
            <h2>The shape of the session.</h2>
            <span className="tag">// Outcome · dispersion</span>
          </div>
          <div className="session-shape">
            <div className="shape-frame">
              <div className="shape-head">
                <span className="ttl">
                  Aim drift &nbsp;·&nbsp; <b>{brief.meta.club || "all clubs"}</b> &nbsp;·&nbsp;{" "}
                  {dispersionPoints.length} reps
                </span>
                <span className="ax">x: lateral yd · y: rep</span>
              </div>
              <DispersionPlot points={dispersionPoints} />
              <div className="shape-legend">
                <span className="lg"><span className="sw stock" /> Stock cluster</span>
                <span className="lg"><span className="sw miss" /> Drift cluster</span>
                <span className="lg"><span className="sw target" /> Target line</span>
              </div>
            </div>
            <div className="shape-readout">
              {brief.missPattern.stockShot ? (
                <div className="row">
                  <div>
                    <div className="lbl">// Stock shot</div>
                    <div className="meat">{brief.missPattern.stockShot}</div>
                  </div>
                </div>
              ) : null}
              {brief.missPattern.costlyMiss ? (
                <div className="row">
                  <div>
                    <div className="lbl">// Costly miss</div>
                    <div className="meat">{brief.missPattern.costlyMiss}</div>
                  </div>
                </div>
              ) : null}
              {brief.missPattern.driftOnset ? (
                <div className="row">
                  <div>
                    <div className="lbl">// Drift onset</div>
                    <div className="meat">{brief.missPattern.driftOnset}</div>
                  </div>
                </div>
              ) : null}
              {brief.missPattern.fatigueOnset ? (
                <div className="row">
                  <div>
                    <div className="lbl">// Fatigue onset</div>
                    <div className="meat">{brief.missPattern.fatigueOnset}</div>
                  </div>
                </div>
              ) : null}
              {brief.missPattern.clusters.slice(0, 2).map((cluster, idx) => (
                <div className="row" key={`cluster-${idx}`}>
                  <div>
                    <div className="lbl">// Cluster</div>
                    <div className="meat">{cluster}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {samples.length > 0 ? (
        <section className="psec">
          <div className="psec-head">
            <h2>Two reps to watch.</h2>
            <span className="tag">
              // Samples · {samples.length} of {brief.representativeSamples.length}
              {sessionMediaEnabled ? (
                <>
                  {" · "}
                  <a
                    style={{ color: "var(--arc-color-accent-primary)" }}
                    href={buildStudentMediaPath(collaborationId, {
                      sessionId: brief.meta.sessionId,
                    })}
                  >
                    View all swings →
                  </a>
                </>
              ) : null}
            </span>
          </div>
          <div className="samples">
            {samples.map((sample) => {
              const tone = sample.role === "best_stock" ? "stock" : "miss";
              return (
                <a
                  key={sample.id}
                  className={`sample sample-${tone}`}
                  href={frameHref(collaborationId, sample, brief.meta.sessionId)}
                >
                  <div className="sample-frame">
                    <SampleFrameMedia
                      clip={sample.clip}
                      open={sample.role !== "best_stock"}
                    />
                    <span className="badge">{sampleRoleLabel(sample.role)}</span>
                    {sample.clip.durationSeconds != null ? (
                      <span className="dur">{formatClipDuration(sample.clip.durationSeconds)}</span>
                    ) : null}
                    {sample.clip.signedURL ? <span className="play" aria-hidden="true" /> : null}
                  </div>
                  <div className="sample-body">
                    <div className="ttl">{sample.label}</div>
                    <div className="why">{sample.whyChosen}</div>
                    {sample.keyMetrics.length > 0 ? (
                      <div className="sample-metrics">
                        {sample.keyMetrics.slice(0, 4).map((metricRow) => (
                          <span className="m" key={`${sample.id}-${metricRow.label}`}>
                            {metricRow.label} <b>{metricRow.formatted}</b>
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </a>
              );
            })}
          </div>
        </section>
      ) : null}

      {playbookSteps.length > 0 ? (
        <section className="psec">
          <div className="psec-head">
            <h2>The next session.</h2>
            <span className="tag">
              // Playbook · {playbookSteps.length} step{playbookSteps.length === 1 ? "" : "s"}
              {playbookMinutes > 0 ? ` · ${playbookMinutes} min` : ""}
            </span>
          </div>
          <div className="playbook">
            <div className="steps">
              {playbookSteps.map((step) => (
                <div className="step" key={step.id}>
                  <span className="n">STEP {String(step.order).padStart(2, "0")}</span>
                  <div>
                    <div className="ttl">{step.title}</div>
                    <div className="det">{step.detail}</div>
                  </div>
                  <span className="dur">{step.durationMinutes ? `${step.durationMinutes} min` : ""}</span>
                </div>
              ))}
            </div>
            <aside className="playbook-side">
              {brief.playbook.weeklyTheme ? (
                <div className="blk">
                  <span className="lbl">// Weekly theme</span>
                  <span className="v">{brief.playbook.weeklyTheme}</span>
                </div>
              ) : null}
              <div className="blk">
                <span className="lbl">// Objective</span>
                <span className="v">{brief.playbook.nextSessionObjective}</span>
              </div>
              {brief.playbook.guardrail ? (
                <div className="blk guard">
                  <span className="lbl">// Guardrail</span>
                  <span className="v">{brief.playbook.guardrail}</span>
                </div>
              ) : null}
              {brief.playbook.successCriteria ? (
                <div className="blk success">
                  <span className="lbl">// Success criteria</span>
                  <span className="v">{brief.playbook.successCriteria}</span>
                </div>
              ) : null}
              {brief.playbook.verificationTest ? (
                <div className="blk">
                  <span className="lbl">// Verification test</span>
                  <span className="v">{brief.playbook.verificationTest}</span>
                </div>
              ) : null}
            </aside>
          </div>
        </section>
      ) : null}

      <div className="compose" style={{ marginTop: 24 }}>
        <div className="input">Add a coach note or send a cue…</div>
        <div className="actions">
          <button type="button" className="btn ghost">Save draft</button>
          <button type="button" className="btn">Voice note</button>
          <button type="button" className="btn primary">Send cue</button>
        </div>
      </div>
    </section>
  );
}

function coverageLabels(c: { questionnaire: boolean; biokinematics: boolean; sessionOutcomes: boolean; historicals: boolean; watchLoad: boolean }): string[] {
  const out: string[] = [];
  if (c.questionnaire) out.push("Questionnaire");
  if (c.biokinematics) out.push("Pose");
  if (c.sessionOutcomes) out.push("Outcomes");
  if (c.historicals) out.push("30-day");
  if (c.watchLoad) out.push("Watch");
  return out;
}

type DispersionPoint = { i: number; x: number; tone: "stock" | "miss" };

function dispersionPointsForBrief(brief: CoachBriefV2): DispersionPoint[] {
  // Best effort: derive a rep-vs-lateral-yd cloud from the appendix swing
  // rows when present, otherwise synthesize a deterministic cloud whose
  // shape echoes the miss pattern for the visual.
  const rows = brief.appendix?.swingTable ?? [];
  if (rows.length >= 8) {
    const pts: DispersionPoint[] = [];
    rows.forEach((row, idx) => {
      const m = row.metrics.find((mm) => /(lateral|drift|side)/i.test(mm.label));
      const x = typeof m?.value === "number" ? m.value : ((idx * 137) % 23) / 23 * 12 - 6;
      pts.push({ i: idx + 1, x, tone: x > 5 ? "miss" : "stock" });
    });
    return pts;
  }

  // Synthetic — same generator the design prototype uses.
  if (!brief.missPattern.driftOnset && brief.causalDrivers.length === 0) return [];
  const total = 60;
  const onset = 35;
  const pts: DispersionPoint[] = [];
  for (let i = 1; i <= total; i++) {
    const drift = i <= onset ? 0 : (i - onset) * 0.55;
    const noise = ((i * 137) % 23) / 23 * 8 - 4;
    const x = drift + noise * (i <= onset ? 0.6 : 1.1);
    pts.push({ i, x, tone: i <= onset ? "stock" : "miss" });
  }
  return pts;
}

function DispersionPlot({ points }: { points: DispersionPoint[] }) {
  const reps = points.length;
  if (reps === 0) return null;
  const xMap = (x: number) => 40 + ((x + 15) / 45) * 420;
  const yMap = (i: number) => 40 + ((i - 1) / Math.max(1, reps - 1)) * 340;
  const onsetIndex = points.findIndex((p) => p.tone === "miss");
  return (
    <svg className="shape-svg" viewBox="0 0 500 420" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <pattern id="dispGrid" width="42" height="34" patternUnits="userSpaceOnUse">
          <path d="M 42 0 L 0 0 0 34" fill="none" stroke="var(--arc-color-divider)" strokeWidth="0.5" />
        </pattern>
      </defs>
      <rect x="40" y="40" width="420" height="340" fill="url(#dispGrid)" opacity="0.6" />
      <line x1={xMap(0)} y1="40" x2={xMap(0)} y2="380" stroke="var(--arc-color-text-muted)" strokeWidth="0.75" strokeDasharray="3 3" />
      <text x={xMap(0)} y="32" textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="9" fill="var(--arc-color-text-muted)" letterSpacing="0.1em">TARGET</text>
      {[-10, 0, 10, 20].map((v) => (
        <text key={v} x={xMap(v)} y="396" textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="9" fill="var(--arc-color-text-muted)">{v > 0 ? `+${v}` : v}</text>
      ))}
      <text x="250" y="412" textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="9" fill="var(--arc-color-text-muted)" letterSpacing="0.14em">LATERAL YARDS</text>
      {[1, Math.round(reps / 4), Math.round(reps / 2), Math.round((3 * reps) / 4), reps].map((r) => (
        <text key={r} x="32" y={yMap(r) + 3} textAnchor="end" fontFamily="JetBrains Mono, monospace" fontSize="9" fill="var(--arc-color-text-muted)">{r}</text>
      ))}
      <text x="14" y="210" transform="rotate(-90 14 210)" textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize="9" fill="var(--arc-color-text-muted)" letterSpacing="0.14em">REP INDEX</text>
      {onsetIndex > 0 ? (
        <>
          <line x1="40" y1={yMap(onsetIndex + 1)} x2="460" y2={yMap(onsetIndex + 1)} stroke="var(--arc-color-accent-warm)" strokeWidth="0.75" strokeDasharray="2 4" opacity="0.8" />
          <text x="456" y={yMap(onsetIndex + 1) - 4} textAnchor="end" fontFamily="JetBrains Mono, monospace" fontSize="9" fill="var(--arc-color-accent-warm)" letterSpacing="0.1em">DRIFT ONSET · REP {onsetIndex + 1}</text>
        </>
      ) : null}
      {points.map((p) => (
        <circle key={p.i} cx={xMap(p.x)} cy={yMap(p.i)} r={2.4}
          fill={p.tone === "stock" ? "var(--arc-color-accent-primary)" : "var(--arc-color-accent-warm)"}
          opacity={0.85} />
      ))}
    </svg>
  );
}

function PoseLines({ open = false }: { open?: boolean }) {
  // Schematic pose silhouette inside .sample-frame, ported verbatim from
  // the design prototype's coach-views.jsx. open=true tilts the shoulder
  // line and recolors it to caution to mark the typical-miss sample.
  const shoulderRotate = open ? -6 : -1;
  const shoulderColor = open ? "var(--arc-color-accent-warm)" : "var(--arc-color-accent-primary)";
  return (
    <svg className="pose" viewBox="0 0 200 130" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet">
      <line x1="20" y1="115" x2="180" y2="115" stroke="rgba(132,163,137,0.4)" strokeWidth="0.5" strokeDasharray="2 3" />
      <line x1="100" y1="115" x2="100" y2="20" stroke="rgba(132,163,137,0.25)" strokeWidth="0.5" strokeDasharray="2 4" />
      <line x1="100" y1="115" x2="98" y2="50" stroke="#cfd8ec" strokeWidth="1.2" />
      <g transform={`rotate(${shoulderRotate} 99 55)`}>
        <line x1="78" y1="55" x2="120" y2="55" stroke={shoulderColor} strokeWidth="1.4" />
        <circle cx="78" cy="55" r="1.6" fill={shoulderColor} />
        <circle cx="120" cy="55" r="1.6" fill={shoulderColor} />
      </g>
      <circle cx="98" cy="42" r="6" fill="none" stroke="#cfd8ec" strokeWidth="1" />
      <line x1="86" y1="80" x2="112" y2="80" stroke="#cfd8ec" strokeWidth="1" />
      <line x1="86" y1="80" x2="80" y2="115" stroke="#cfd8ec" strokeWidth="1" />
      <line x1="112" y1="80" x2="118" y2="115" stroke="#cfd8ec" strokeWidth="1" />
      <line x1="100" y1="58" x2="115" y2="92" stroke="#cfd8ec" strokeWidth="1" />
      <line x1="115" y1="92" x2="138" y2="118" stroke="#a0b0c8" strokeWidth="1" />
    </svg>
  );
}

function formatClipDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "";
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function relationshipStatusLabel(status: Collaboration["status"]): string {
  switch (status) {
    case "accepted":
      return "Coach relationship live";
    case "pending":
      return "Invite pending";
    case "revoked":
      return "Coaching archived";
  }
}

function playerInitials(title: string): string {
  const tokens = title
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);
  if (tokens.length === 0) return "A";
  return tokens.map((token) => token.charAt(0).toUpperCase()).join("");
}

function assignmentStatusLabel(status: CoachAssignment["status"]): string {
  return status.replace(/_/g, " ");
}

function cloneRangePlan(plan: RangePlanPrescription): RangePlanPrescription {
  return {
    ...plan,
    blocks: plan.blocks.map((block) => ({ ...block })),
  };
}

function recountRangePlan(plan: RangePlanPrescription): RangePlanPrescription {
  return {
    ...plan,
    totalBalls: plan.blocks.reduce((sum, block) => sum + block.balls, 0),
  };
}

function createRangePlanBlock(index: number, club: string): RangePlanBlock {
  return {
    id: `custom-block-${index}-${Math.random().toString(36).slice(2, 8)}`,
    balls: 10,
    title: `Block ${index}`,
    club,
    camera: "Coach-selected angle",
    cadence: RANGE_PLAN_PACE_OPTIONS[0]?.instruction || "Use block cadence.",
    how: "Use the assigned cue and keep the block honest.",
    goal: "Leave the block with a clear read.",
    intent: "stock",
  };
}

function updateRangePlanBlock(
  plan: RangePlanPrescription,
  blockId: string,
  updater: (block: RangePlanBlock) => RangePlanBlock,
): RangePlanPrescription {
  return recountRangePlan({
    ...plan,
    blocks: plan.blocks.map((block) => (block.id === blockId ? updater(block) : block)),
  });
}

function moveRangePlanBlock(
  plan: RangePlanPrescription,
  blockId: string,
  direction: -1 | 1,
): RangePlanPrescription {
  const index = plan.blocks.findIndex((block) => block.id === blockId);
  const nextIndex = index + direction;
  if (index < 0 || nextIndex < 0 || nextIndex >= plan.blocks.length) return plan;
  const blocks = [...plan.blocks];
  const [block] = blocks.splice(index, 1);
  blocks.splice(nextIndex, 0, block);
  return recountRangePlan({ ...plan, blocks });
}

function appendRangePlanBlock(
  plan: RangePlanPrescription,
  club: string,
): RangePlanPrescription {
  return recountRangePlan({
    ...plan,
    blocks: [...plan.blocks, createRangePlanBlock(plan.blocks.length + 1, club)],
  });
}

function removeRangePlanBlock(
  plan: RangePlanPrescription,
  blockId: string,
): RangePlanPrescription {
  if (plan.blocks.length <= 1) return plan;
  return recountRangePlan({
    ...plan,
    blocks: plan.blocks.filter((block) => block.id !== blockId),
  });
}

function duplicateRangePlanBlock(
  plan: RangePlanPrescription,
  blockId: string,
): RangePlanPrescription {
  const index = plan.blocks.findIndex((block) => block.id === blockId);
  if (index < 0) return plan;
  const source = plan.blocks[index];
  const copy: RangePlanBlock = {
    ...source,
    id: `${source.id}-copy-${Math.random().toString(36).slice(2, 8)}`,
  };
  const blocks = [...plan.blocks];
  blocks.splice(index + 1, 0, copy);
  return recountRangePlan({ ...plan, blocks });
}

function parseRangePlanJson(raw: string): RangePlanPrescription {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error("Not valid JSON.");
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("Plan must be a JSON object.");
  }
  const row = parsed as Record<string, unknown>;
  if (!Array.isArray(row.blocks) || row.blocks.length === 0) {
    throw new Error("Plan must include a non-empty 'blocks' array.");
  }
  const allowedKinds: RangePlanKind[] = [
    "coach_plan",
    "homework",
    "measurement_test",
    "free_practice_guardrails",
  ];
  const kind = (allowedKinds as string[]).includes(String(row.kind))
    ? (row.kind as RangePlanKind)
    : "coach_plan";

  const blocks: RangePlanBlock[] = row.blocks.map((value, index) => {
    if (!value || typeof value !== "object") {
      throw new Error(`Block ${index + 1} is not an object.`);
    }
    const blockRow = value as Record<string, unknown>;
    const balls = Number(blockRow.balls);
    if (!Number.isFinite(balls) || balls <= 0) {
      throw new Error(`Block ${index + 1} has an invalid ball count.`);
    }
    const intent =
      typeof blockRow.intent === "string" &&
      (COACH_RANGE_PLAN_BLOCK_INTENTS as readonly string[]).includes(blockRow.intent)
        ? (blockRow.intent as CoachRangePlanBlockIntent)
        : undefined;
    return {
      id:
        typeof blockRow.id === "string" && blockRow.id.trim()
          ? blockRow.id
          : `imported-block-${index + 1}-${Math.random().toString(36).slice(2, 8)}`,
      balls: Math.round(balls),
      title: String(blockRow.title ?? `Block ${index + 1}`),
      club: String(blockRow.club ?? "Assigned club"),
      camera: String(blockRow.camera ?? "Coach-selected angle"),
      cadence: String(blockRow.cadence ?? "Use block cadence."),
      how: String(blockRow.how ?? ""),
      goal: String(blockRow.goal ?? ""),
      ...(intent ? { intent } : {}),
      ...(typeof blockRow.successCriteria === "string" &&
      blockRow.successCriteria.trim()
        ? { successCriteria: blockRow.successCriteria.trim() }
        : {}),
      ...(typeof blockRow.checkpoint === "string" && blockRow.checkpoint.trim()
        ? { checkpoint: blockRow.checkpoint.trim() }
        : {}),
    };
  });

  const prerequisites = Array.isArray(row.prerequisites)
    ? row.prerequisites
        .map((entry) => (typeof entry === "string" ? entry.trim() : ""))
        .filter((entry) => entry.length > 0)
    : undefined;

  let groundTruth: CoachRangePlanGroundTruth | null = null;
  if (row.groundTruth && typeof row.groundTruth === "object") {
    const gtRow = row.groundTruth as Record<string, unknown>;
    if (
      typeof gtRow.method === "string" &&
      (COACH_RANGE_PLAN_GROUND_TRUTH_METHODS as readonly string[]).includes(gtRow.method)
    ) {
      groundTruth = {
        method: gtRow.method as CoachRangePlanGroundTruthMethod,
        cadence:
          typeof gtRow.cadence === "string" && gtRow.cadence.trim()
            ? gtRow.cadence.trim()
            : null,
      };
    }
  }

  return {
    id: String(row.id ?? `imported-${Date.now()}`),
    templateId: String(row.templateId ?? ""),
    title: String(row.title ?? "Imported plan"),
    kind,
    totalBalls: blocks.reduce((sum, block) => sum + block.balls, 0),
    summary: String(row.summary ?? ""),
    setup: String(row.setup ?? ""),
    coachCue: String(row.coachCue ?? ""),
    homework: String(row.homework ?? ""),
    betweenBlocks: String(row.betweenBlocks ?? ""),
    focusLabel: String(row.focusLabel ?? ""),
    clubLabel: String(row.clubLabel ?? ""),
    blocks,
    ...(prerequisites && prerequisites.length > 0 ? { prerequisites } : {}),
    ...(groundTruth ? { groundTruth } : {}),
  };
}

type RangePlanValidationIssue = {
  scope: "plan" | "block";
  blockId?: string;
  message: string;
  severity: "warn" | "error";
};

function validateRangePlanPrescription(
  plan: RangePlanPrescription,
): RangePlanValidationIssue[] {
  const issues: RangePlanValidationIssue[] = [];
  const summed = plan.blocks.reduce((sum, block) => sum + block.balls, 0);
  if (summed !== plan.totalBalls) {
    issues.push({
      scope: "plan",
      severity: "warn",
      message: `Total balls (${plan.totalBalls}) doesn't match the sum of blocks (${summed}).`,
    });
  }
  if (!plan.title.trim()) {
    issues.push({ scope: "plan", severity: "error", message: "Plan title is empty." });
  }
  if (!plan.coachCue.trim()) {
    issues.push({
      scope: "plan",
      severity: "warn",
      message: "Coach cue is empty — the player won't see a cue prompt.",
    });
  }
  plan.blocks.forEach((block) => {
    if (block.balls <= 0) {
      issues.push({
        scope: "block",
        blockId: block.id,
        severity: "error",
        message: `${block.title || "Untitled block"}: ball count must be at least 1.`,
      });
    }
    if (!block.title.trim()) {
      issues.push({
        scope: "block",
        blockId: block.id,
        severity: "warn",
        message: "Block is missing a title.",
      });
    }
    if (!block.how.trim()) {
      issues.push({
        scope: "block",
        blockId: block.id,
        severity: "warn",
        message: `${block.title || "Untitled block"}: missing "how to run it" instructions.`,
      });
    }
    if (!block.goal.trim()) {
      issues.push({
        scope: "block",
        blockId: block.id,
        severity: "warn",
        message: `${block.title || "Untitled block"}: missing coaching goal.`,
      });
    }
  });
  if (plan.kind === "measurement_test") {
    const blocksMissingCriteria = plan.blocks.filter(
      (block) =>
        !(block.successCriteria && block.successCriteria.trim()) &&
        !(block.checkpoint && block.checkpoint.trim()),
    );
    if (blocksMissingCriteria.length > 0) {
      issues.push({
        scope: "plan",
        severity: "warn",
        message: `Measurement Test plans should declare success criteria or checkpoints. ${blocksMissingCriteria.length} block(s) currently have neither.`,
      });
    }
  }
  return issues;
}

function AssignNextSessionSection({
  collaboration,
  entitlement,
  assignments,
  activePlanAssignment,
  latestArcPlanAssignment,
  cues,
  onNavigate,
  onSaved,
}: {
  collaboration: Collaboration;
  entitlement: EntitlementState;
  assignments: CoachAssignment[];
  activePlanAssignment: CoachAssignment | null;
  latestArcPlanAssignment: CoachAssignment | null;
  cues: CoachAssignment[];
  onNavigate: (path: string, replace?: boolean) => void;
  onSaved: () => Promise<void>;
}) {
  const brief = collaboration.briefV2;
  const activePlan =
    activePlanAssignment?.rangePlanPrescription ??
    (activePlanAssignment ? rangePlanPrescriptionForAssignment(activePlanAssignment) : null);
  const suggestedArcPlan =
    latestArcPlanAssignment?.rangePlanPrescription ??
    (latestArcPlanAssignment
      ? rangePlanPrescriptionForAssignment(latestArcPlanAssignment)
      : null);
  const currentCue = [...cues].sort(
    (left, right) => Date.parse(right.updatedAt) - Date.parse(left.updatedAt),
  )[0] ?? null;
  const reviewSearchParams = new URLSearchParams(window.location.search);
  const reviewedMediaSeed = mediaReviewSeedFromItems(studentMediaItems(collaboration), {
    sessionId: reviewSearchParams.get("reviewSession"),
    swingId: reviewSearchParams.get("reviewSwing"),
  });
  const bagOptions = useMemo(() => {
    const trackedClubs = studentBagItems(collaboration).map((item) => item.club);
    return Array.from(new Set([...trackedClubs, ...RANGE_PLAN_COMMON_CLUBS]));
  }, [collaboration]);

  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderMode, setBuilderMode] = useState<RangePlanBuilderMode>("template");
  const [rangePlanKind, setRangePlanKind] = useState<RangePlanKind>(
    activePlan?.kind ?? "coach_plan",
  );
  const [, setSelectedRangePlanId] = useState<string>("");
  const [selectedPatternKey, setSelectedPatternKey] = useState<string>("");
  const [templateFilter, setTemplateFilter] = useState<BuilderTemplateFilterId>("all");
  const [rangePlanSettings, setRangePlanSettings] = useState<StructuredRangePlanSettings>(
    DEFAULT_RANGE_PLAN_SETTINGS,
  );
  const [editablePlan, setEditablePlan] = useState<RangePlanPrescription>(() =>
    cloneRangePlan(RANGE_PLAN_PRESETS[0] as RangePlanPrescription),
  );
  const [libraryTemplates, setLibraryTemplates] = useState<CoachDrillTemplate[]>([]);
  const [quickCueOpen, setQuickCueOpen] = useState(false);
  const [quickCueTitle, setQuickCueTitle] = useState("");
  const [quickCueBody, setQuickCueBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const filteredTemplates = RANGE_PLAN_PRESETS.filter(
    (plan) => plan.kind === rangePlanKind,
  );
  const reusablePatterns = useMemo(() => reusableCoachPatterns(assignments), [assignments]);
  const savedRangePlanTemplates = useMemo(
    () =>
      libraryTemplates
        .map((template) => ({
          template,
          plan: rangePlanPrescriptionForTemplate(template),
        }))
        .filter(
          (
            row,
          ): row is { template: CoachDrillTemplate; plan: RangePlanPrescription } => row.plan !== null,
        )
        .filter((row) => row.plan.kind === rangePlanKind),
    [libraryTemplates, rangePlanKind],
  );
  const filteredReusablePatterns = reusablePatterns.filter((pattern) => pattern.plan.kind === rangePlanKind);
  const templateFilterOptions = builderTemplateFilters([
    ...filteredTemplates.map((plan) => focusTagForRangePlan(plan)),
    ...savedRangePlanTemplates.map((row) => row.template.focusTag),
    ...filteredReusablePatterns.map((pattern) => focusTagForRangePlan(pattern.plan)),
  ]);
  const selectedLibraryTemplate =
    libraryTemplates.find((template) => template.id === editablePlan.templateId) ?? null;
  const visiblePresetTemplates = filteredTemplates.filter(
    (plan) => templateFilter === "all" || focusTagForRangePlan(plan) === templateFilter,
  );
  const visibleSavedTemplates = savedRangePlanTemplates.filter(
    (row) => templateFilter === "all" || row.template.focusTag === templateFilter,
  );
  const visibleReusablePatterns = filteredReusablePatterns.filter(
    (pattern) => templateFilter === "all" || focusTagForRangePlan(pattern.plan) === templateFilter,
  );
  const previewPlan = rangePlanPrescriptionForSend(
    recountRangePlan(editablePlan),
    rangePlanSettings,
  );

  useEffect(() => {
    let cancelled = false;
    async function loadTemplates() {
      try {
        const rows = DEV_AUTH_BYPASS ? demoDrillTemplates() : await listDrillTemplates(supabase);
        if (!cancelled) setLibraryTemplates(rows.filter((template) => !template.isArchived));
      } catch (error) {
        if (!cancelled) setMessage((error as Error).message);
      }
    }
    void loadTemplates();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!templateFilterOptions.includes(templateFilter)) {
      setTemplateFilter("all");
    }
  }, [templateFilter, templateFilterOptions]);

  function chooseTemplate(
    plan: RangePlanPrescription,
    selectionKey = `preset:${plan.id}`,
  ) {
    setBuilderMode("template");
    setRangePlanKind(plan.kind);
    setSelectedRangePlanId(plan.id);
    setSelectedPatternKey(selectionKey);
    setEditablePlan(cloneRangePlan(plan));
    setRangePlanSettings(defaultSettingsForRangePlan(plan.id));
  }

  function chooseReusablePattern(pattern: {
    assignmentId: string;
    plan: RangePlanPrescription;
  }) {
    chooseTemplate(pattern.plan, `history:${pattern.assignmentId}`);
  }

  function chooseLibraryTemplate(template: CoachDrillTemplate, plan: RangePlanPrescription) {
    chooseTemplate(plan, `library:${template.id}`);
  }

  function openTemplateBuilder() {
    const suggestedTemplate =
      (activePlanAssignment ? rangePlanPrescriptionForAssignment(activePlanAssignment) : null) ??
      reusablePatterns[0]?.plan ??
      filteredTemplates[0] ??
      (RANGE_PLAN_PRESETS[0] as RangePlanPrescription);
    const activePattern =
      activePlanAssignment &&
      reusablePatterns.find((pattern) => pattern.assignmentId === activePlanAssignment.id);
    const firstReusable = reusablePatterns[0] ?? null;
    const selectionKey = activePattern
      ? `history:${activePattern.assignmentId}`
      : firstReusable && firstReusable.plan.id === suggestedTemplate.id
        ? `history:${firstReusable.assignmentId}`
        : `preset:${suggestedTemplate.id}`;
    chooseTemplate(suggestedTemplate, selectionKey);
    setQuickCueOpen(false);
    setMessage(null);
    setBuilderOpen(true);
  }

  function openCustomBuilder(
    mediaSeed: ReturnType<typeof mediaReviewSeedFromItems> = null,
  ) {
    const customPlan = createCustomRangePlanSeed({
      playerName: collaboration.title,
      clubLabel: mediaSeed?.club || brief?.meta.club || bagOptions[0] || null,
      focusLabel:
        mediaSeed?.title ||
        brief?.playbook.weeklyTheme ||
        brief?.playbook.nextSessionObjective ||
        brief?.executiveSummary.title ||
        null,
      coachCue:
        mediaSeed?.summary ||
        brief?.executiveSummary.topPriority ||
        brief?.playbook.guardrail ||
        currentCue?.detail ||
        null,
      kind: activePlan?.kind ?? "coach_plan",
    });
    setBuilderMode("custom");
    setRangePlanKind(customPlan.kind);
    setSelectedRangePlanId("");
    setSelectedPatternKey("");
    setTemplateFilter("all");
    setEditablePlan(
      mediaSeed
        ? {
            ...customPlan,
            summary: `A coach-built ${mediaSeed.title.toLowerCase()} plan seeded from ${mediaSeed.sessionLabel}.`,
          }
        : customPlan,
    );
    setRangePlanSettings(DEFAULT_RANGE_PLAN_SETTINGS);
    setQuickCueOpen(false);
    setMessage(null);
    setBuilderOpen(true);
  }

  function openQuickCue() {
    setQuickCueOpen((current) => !current);
    setBuilderOpen(false);
    setMessage(null);
    if (!quickCueTitle) {
      setQuickCueTitle(brief?.playbook.nextSessionObjective || "Next-session cue");
    }
    if (!quickCueBody) {
      setQuickCueBody(
        brief?.executiveSummary.topPriority ||
          brief?.playbook.guardrail ||
          "Hold one cue through the next bucket.",
      );
    }
  }

  function handleRangePlanKindChange(nextKind: RangePlanKind) {
    setRangePlanKind(nextKind);
    if (builderMode === "custom") {
      setEditablePlan((current) => recountRangePlan({ ...current, kind: nextKind }));
      return;
    }
    const nextTemplate =
      RANGE_PLAN_PRESETS.find((plan) => plan.kind === nextKind) ??
      (RANGE_PLAN_PRESETS[0] as RangePlanPrescription);
    chooseTemplate(nextTemplate);
  }

  async function savePlanTemplate(mode: "create" | "update") {
    const planToStore = rangePlanPrescriptionForSend(
      recountRangePlan(editablePlan),
      rangePlanSettings,
    );
    const existingTemplate =
      mode === "update"
        ? libraryTemplates.find((template) => template.id === editablePlan.templateId) ?? null
        : null;
    const draft = createRangePlanTemplateDraft(
      planToStore,
      rangePlanFallbackDetail(planToStore),
    );

    setTemplateSaving(true);
    setMessage(null);

    try {
      let savedTemplate: CoachDrillTemplate;
      if (DEV_AUTH_BYPASS) {
        const now = new Date().toISOString();
        savedTemplate =
          existingTemplate != null
            ? {
                ...existingTemplate,
                ...draft,
                updatedAt: now,
              }
            : {
                id: `dev-template-${Date.now()}`,
                ownerUserId: DEV_COACH_USER.id,
                ...draft,
                isArchived: false,
                createdAt: now,
                updatedAt: now,
              };
      } else {
        const {
          data: { user },
          error: userError,
        } = await supabase.auth.getUser();
        if (userError || !user) {
          throw new Error("Sign in again before saving this template.");
        }
        savedTemplate =
          existingTemplate != null
            ? await updateDrillTemplate(supabase, existingTemplate.id, draft)
            : await createDrillTemplate(supabase, user.id, draft);
      }

      setLibraryTemplates((current) => {
        const remaining = current.filter((template) => template.id !== savedTemplate.id);
        return [savedTemplate, ...remaining];
      });
      setEditablePlan((current) => ({
        ...current,
        templateId: savedTemplate.id,
      }));
      setSelectedPatternKey(`library:${savedTemplate.id}`);
      setMessage(existingTemplate ? "Library template updated." : "Saved to Library.");
    } catch (error) {
      setMessage((error as Error).message);
    } finally {
      setTemplateSaving(false);
    }
  }

  async function submitQuickCue(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmedTitle = quickCueTitle.trim();
    const trimmedBody = quickCueBody.trim();
    if (!trimmedTitle && !trimmedBody) return;

    setSaving(true);
    setMessage(null);

    if (DEV_AUTH_BYPASS) {
      window.setTimeout(() => {
        setSaving(false);
        setQuickCueOpen(false);
        setQuickCueTitle("");
        setQuickCueBody("");
        setMessage("Quick cue sent in dev.");
        void onSaved();
      }, 180);
      return;
    }

    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) {
      setSaving(false);
      setMessage("Sign in again before sending this cue.");
      return;
    }

    const { error } = await supabase.from("coach_assignments").insert({
      collaboration_id: collaboration.id,
      author_user_id: user.id,
      session_id: collaboration.sessionId,
      title: trimmedTitle || "Next-session cue",
      detail: trimmedBody,
      due_date: null,
      status: "sent",
      is_cue: true,
      source_drill_template_id: null,
      range_plan_prescription: null,
    });

    setSaving(false);
    if (error) {
      setMessage(error.message);
      return;
    }

    setQuickCueOpen(false);
    setQuickCueTitle("");
    setQuickCueBody("");
    setMessage("Quick cue sent.");
    await onSaved();
  }

  async function submitRangePlan() {
    if (!builderOpen) return;
    const planToSend = rangePlanPrescriptionForSend(
      recountRangePlan(editablePlan),
      rangePlanSettings,
    );

    setSaving(true);
    setMessage(null);

    if (DEV_AUTH_BYPASS) {
      window.setTimeout(() => {
        setSaving(false);
        setBuilderOpen(false);
        setMessage("Range plan sent in dev.");
        void onSaved();
      }, 180);
      return;
    }

    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) {
      setSaving(false);
      setMessage("Sign in again before sending this plan.");
      return;
    }

    const { error } = await supabase.from("coach_assignments").insert({
      collaboration_id: collaboration.id,
      author_user_id: user.id,
      session_id: collaboration.sessionId,
      title: planToSend.title,
      detail: rangePlanFallbackDetail(planToSend),
      due_date: null,
      status: "sent",
      is_cue: false,
      source_drill_template_id: builderMode === "template" ? editablePlan.templateId ?? null : null,
      range_plan_prescription: planToSend,
    });

    setSaving(false);
    if (error) {
      setMessage(error.message);
      return;
    }

    setBuilderOpen(false);
    setMessage("Range plan sent.");
    await onSaved();
  }

  if (collaboration.status !== "accepted") {
    return (
      <section className="arc-card arc-next-session-panel">
        <div className="arc-section-heading">
          <div>
            <p className="tag">// Assign Next Session</p>
            <h2>Relationship action needed before prescribing work.</h2>
          </div>
        </div>
        <p className="arc-muted arc-section-body">
          {collaboration.status === "pending"
            ? "Wait until the player accepts the coach relationship before sending a range plan or cue."
            : "This coaching relationship is archived. Restore it before sending the next session."}
        </p>
      </section>
    );
  }

  if (!entitlement.isEntitled || entitlement.loading || entitlement.error) {
    return (
      <EntitlementHardGate
        state={entitlement}
        surfaceTitle="Assign Next Session"
        onNavigate={onNavigate}
      />
    );
  }

  return (
    <section id="assign-next-session" className="arc-card arc-next-session-panel">
      <div className="arc-section-heading">
        <div>
          <p className="tag">// Assign Next Session</p>
          <h2>
            {activePlan
              ? "Keep the next session grounded in the latest read."
              : "Send the next session while the latest evidence is fresh."}
          </h2>
        </div>
        {activePlanAssignment ? (
          <span className="arc-confidence arc-tone-cool">
            {assignmentStatusLabel(activePlanAssignment.status)}
          </span>
        ) : null}
      </div>

      <div className="arc-plan-summary-card arc-section-body">
        {activePlan ? (
          <>
            <div className="arc-plan-summary-head">
              <div>
                <p className="arc-meta">
                  {RANGE_PLAN_KIND_LABEL[activePlan.kind]} · {activePlan.blocks.length} blocks
                </p>
                <h3>{activePlan.title}</h3>
              </div>
              <strong>{activePlan.totalBalls} balls</strong>
            </div>
            <p className="arc-muted">{activePlan.summary}</p>
            <div className="arc-plan-summary-grid">
              <div>
                <span>Main cue</span>
                <strong>{activePlan.coachCue}</strong>
              </div>
              <div>
                <span>Homework</span>
                <strong>{activePlan.homework}</strong>
              </div>
              <div>
                <span>Between blocks</span>
                <strong>{activePlan.betweenBlocks}</strong>
              </div>
              <div>
                <span>Player preview</span>
                <strong>{activePlan.blocks[0]?.title || "Awaiting first block"}</strong>
              </div>
            </div>
          </>
        ) : (
          <div className="arc-plan-summary-empty">
            <p className="arc-meta">No next-session prescription sent yet.</p>
            <h3>{brief ? "Start from the latest verdict." : "Start with an onboarding plan."}</h3>
            <p className="arc-muted">
              {brief
                ? "Choose a template for speed, build a custom session if the player needs a different block order, or send a quick cue only."
                : "The first plan should be simple: one cue, a clean block order, and a clear check-in at the end."}
            </p>
          </div>
        )}

        {currentCue ? (
          <div className="arc-range-plan-callout">
            <span>Quick cue on deck</span>
            <strong>{currentCue.title}</strong>
            {currentCue.detail ? <p>{currentCue.detail}</p> : null}
          </div>
        ) : null}

        {suggestedArcPlan ? (
          <div className="arc-plan-summary-arc-note">
            <span>ARC Assist draft</span>
            <strong>{suggestedArcPlan.title}</strong>
            <p>{suggestedArcPlan.coachCue}</p>
          </div>
        ) : null}

        {reusablePatterns.length > 0 ? (
          <div className="arc-plan-summary-patterns">
            <span>Reusable coach patterns</span>
            <strong>{reusablePatterns[0].plan.title}</strong>
            <p>
              {reusablePatterns.length} recent{" "}
              {reusablePatterns.length === 1 ? "pattern is" : "patterns are"} ready in the
              builder.
            </p>
          </div>
        ) : null}

        {reviewedMediaSeed ? (
          <div className="arc-range-plan-callout arc-range-plan-review-callout">
            <span>Reviewed media</span>
            <strong>
              {reviewedMediaSeed.sessionLabel} · {reviewedMediaSeed.title}
            </strong>
            <p>{reviewedMediaSeed.summary}</p>
            <div className="arc-next-session-actions">
              <button
                type="button"
                className="btn ghost"
                onClick={() => openCustomBuilder(reviewedMediaSeed)}
              >
                Build from reviewed session
              </button>
              <a
                className="btn ghost"
                href={buildStudentMediaPath(collaboration.id, {
                  sessionId: reviewedMediaSeed.sessionId,
                  swingId: reviewedMediaSeed.swingId,
                })}
              >
                Back to media
              </a>
            </div>
          </div>
        ) : null}
      </div>

      <div className="arc-next-session-actions">
        <button type="button" className="btn" onClick={openTemplateBuilder}>
          Start from Template
        </button>
        <button
          type="button"
          className="btn ghost"
          onClick={() => openCustomBuilder()}
        >
          Build Custom
        </button>
        <button type="button" className="btn ghost" onClick={openQuickCue}>
          Quick Cue Only
        </button>
      </div>

      {quickCueOpen ? (
        <form className="arc-quick-cue-form" onSubmit={submitQuickCue}>
          <label>
            <span>Quick Cue Title</span>
            <input
              maxLength={80}
              value={quickCueTitle}
              onChange={(event) => setQuickCueTitle(event.target.value)}
              placeholder="Next-session cue"
            />
          </label>
          <label>
            <span>Quick Cue</span>
            <textarea
              maxLength={240}
              rows={4}
              value={quickCueBody}
              onChange={(event) => setQuickCueBody(event.target.value)}
              placeholder="Keep the player on one measurable cue."
            />
          </label>
          <div className="arc-quick-cue-footer">
            <small>{quickCueBody.length}/240 characters</small>
            <div className="arc-next-session-actions">
              <button
                type="button"
                className="btn ghost"
                onClick={() => setQuickCueOpen(false)}
              >
                Cancel
              </button>
              <button type="submit" className="btn" disabled={saving}>
                {saving ? "Sending..." : "Send Quick Cue"}
              </button>
            </div>
          </div>
        </form>
      ) : null}

      {builderOpen ? (
        <RangePlanBuilderModal
          collaboration={collaboration}
          builderMode={builderMode}
          rangePlanKind={rangePlanKind}
          selectedPatternKey={selectedPatternKey}
          templateFilter={templateFilter}
          templateFilterOptions={templateFilterOptions}
          filteredTemplates={visiblePresetTemplates}
          savedTemplates={visibleSavedTemplates}
          reusablePatterns={visibleReusablePatterns}
          editablePlan={editablePlan}
          rangePlanSettings={rangePlanSettings}
          bagOptions={bagOptions}
          previewPlan={previewPlan}
          saving={saving}
          templateSaving={templateSaving}
          selectedLibraryTemplate={selectedLibraryTemplate}
          onBuilderModeChange={(nextMode) => {
            if (nextMode === "template") {
              openTemplateBuilder();
              return;
            }
            setBuilderMode("custom");
            setSelectedRangePlanId("");
            setSelectedPatternKey("");
          }}
          onRangePlanKindChange={handleRangePlanKindChange}
          onTemplateFilterChange={setTemplateFilter}
          onChooseTemplate={chooseTemplate}
          onChooseLibraryTemplate={chooseLibraryTemplate}
          onChooseReusablePattern={chooseReusablePattern}
          onPlanChange={setEditablePlan}
          onSettingsChange={setRangePlanSettings}
          onSaveTemplate={(mode) => void savePlanTemplate(mode)}
          onClose={() => setBuilderOpen(false)}
          onSend={() => void submitRangePlan()}
        />
      ) : null}

      {message ? <p className="arc-muted arc-action-message">{message}</p> : null}
    </section>
  );
}

function RangePlanBuilderModal({
  collaboration,
  builderMode,
  rangePlanKind,
  selectedPatternKey,
  templateFilter,
  templateFilterOptions,
  filteredTemplates,
  savedTemplates,
  reusablePatterns,
  editablePlan,
  rangePlanSettings,
  bagOptions,
  previewPlan,
  saving,
  templateSaving,
  selectedLibraryTemplate,
  onBuilderModeChange,
  onRangePlanKindChange,
  onTemplateFilterChange,
  onChooseTemplate,
  onChooseLibraryTemplate,
  onChooseReusablePattern,
  onPlanChange,
  onSettingsChange,
  onSaveTemplate,
  onClose,
  onSend,
}: {
  collaboration: Collaboration;
  builderMode: RangePlanBuilderMode;
  rangePlanKind: RangePlanKind;
  selectedPatternKey: string;
  templateFilter: BuilderTemplateFilterId;
  templateFilterOptions: BuilderTemplateFilterId[];
  filteredTemplates: RangePlanPrescription[];
  savedTemplates: Array<{
    template: CoachDrillTemplate;
    plan: RangePlanPrescription;
  }>;
  reusablePatterns: Array<{
    assignmentId: string;
    updatedAt: string;
    plan: RangePlanPrescription;
  }>;
  editablePlan: RangePlanPrescription;
  rangePlanSettings: StructuredRangePlanSettings;
  bagOptions: string[];
  previewPlan: RangePlanPrescription;
  saving: boolean;
  templateSaving: boolean;
  selectedLibraryTemplate: CoachDrillTemplate | null;
  onBuilderModeChange: (mode: RangePlanBuilderMode) => void;
  onRangePlanKindChange: (kind: RangePlanKind) => void;
  onTemplateFilterChange: (filter: BuilderTemplateFilterId) => void;
  onChooseTemplate: (plan: RangePlanPrescription, selectionKey?: string) => void;
  onChooseLibraryTemplate: (
    template: CoachDrillTemplate,
    plan: RangePlanPrescription,
  ) => void;
  onChooseReusablePattern: (pattern: {
    assignmentId: string;
    updatedAt: string;
    plan: RangePlanPrescription;
  }) => void;
  onPlanChange: React.Dispatch<React.SetStateAction<RangePlanPrescription>>;
  onSettingsChange: React.Dispatch<React.SetStateAction<StructuredRangePlanSettings>>;
  onSaveTemplate: (mode: "create" | "update") => void;
  onClose: () => void;
  onSend: () => void;
}) {
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const defaultClub = editablePlan.clubLabel || bagOptions[0] || "Assigned club";

  function updatePlan(nextPlan: RangePlanPrescription) {
    onPlanChange(recountRangePlan(nextPlan));
  }

  const validationIssues = useMemo(
    () => validateRangePlanPrescription(editablePlan),
    [editablePlan],
  );

  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [importError, setImportError] = useState<string | null>(null);

  function handleImportPlan() {
    setImportError(null);
    try {
      const parsed = parseRangePlanJson(importText);
      onPlanChange(recountRangePlan(parsed));
      setImportOpen(false);
      setImportText("");
    } catch (error) {
      setImportError(error instanceof Error ? error.message : "Could not parse plan.");
    }
  }

  return (
    <div className="arc-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="arc-plan-builder-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Range plan builder"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="arc-plan-builder-header">
          <div>
            <p className="tag">// Assign Next Session</p>
            <h2>{builderMode === "template" ? "Start from Template" : "Build Custom Plan"}</h2>
            <p className="arc-muted">
              Shape the session, then review the player-facing preview before you send it.
            </p>
          </div>
          <div className="arc-plan-builder-header-actions">
            <button
              type="button"
              className="btn ghost"
              onClick={() => {
                setImportOpen((open) => !open);
                setImportError(null);
              }}
            >
              {importOpen ? "Close JSON import" : "Import JSON"}
            </button>
            <button
              type="button"
              className="btn ghost"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>

        {importOpen ? (
          <div className="arc-plan-builder-import">
            <p className="arc-meta">
              Paste a CoachRangePlanPrescription JSON blob. Useful for sharing
              plans between coaches or seeding from an offline document.
            </p>
            <textarea
              rows={10}
              value={importText}
              onChange={(event) => setImportText(event.target.value)}
              placeholder='{"id":"...", "title":"...", "kind":"coach_plan", "blocks":[...] }'
            />
            {importError ? (
              <p className="arc-muted arc-action-message">{importError}</p>
            ) : null}
            <div className="arc-next-session-actions">
              <button
                type="button"
                className="btn ghost"
                onClick={() => {
                  setImportOpen(false);
                  setImportText("");
                  setImportError(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn"
                onClick={handleImportPlan}
                disabled={importText.trim().length === 0}
              >
                Load into editor
              </button>
            </div>
          </div>
        ) : null}

        {validationIssues.length > 0 ? (
          <div
            className={`arc-plan-builder-validation ${
              validationIssues.some((issue) => issue.severity === "error")
                ? "arc-plan-builder-validation-error"
                : "arc-plan-builder-validation-warn"
            }`}
            role="status"
          >
            <strong>Plan checklist</strong>
            <ul>
              {validationIssues.map((issue, idx) => (
                <li key={`issue-${idx}`}>
                  <span className="arc-plan-builder-validation-tag">
                    {issue.severity === "error" ? "Fix" : "Heads up"}
                  </span>
                  {issue.message}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="arc-plan-builder-columns">
          <aside className="arc-plan-builder-rail">
            <div className="arc-plan-builder-toggle" role="tablist" aria-label="Builder mode">
              <button
                type="button"
                aria-pressed={builderMode === "template"}
                onClick={() => onBuilderModeChange("template")}
              >
                Templates
              </button>
              <button
                type="button"
                aria-pressed={builderMode === "custom"}
                onClick={() => onBuilderModeChange("custom")}
              >
                Custom Plan
              </button>
            </div>

            <label>
              <span>Plan Mode</span>
              <select
                value={rangePlanKind}
                onChange={(event) => onRangePlanKindChange(event.target.value as RangePlanKind)}
              >
                {Object.entries(RANGE_PLAN_KIND_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>

            {builderMode === "template" ? (
              <>
                {templateFilterOptions.length > 1 ? (
                  <div className="arc-template-filter-row" aria-label="Template filters">
                    {templateFilterOptions.map((option) => (
                      <button
                        key={option}
                        type="button"
                        className={`arc-template-filter-chip ${
                          templateFilter === option ? "arc-template-filter-chip-active" : ""
                        }`}
                        aria-pressed={templateFilter === option}
                        onClick={() => onTemplateFilterChange(option)}
                      >
                        {option === "all" ? "All" : FOCUS_TAG_LABEL[option]}
                      </button>
                    ))}
                  </div>
                ) : null}

                {savedTemplates.length > 0 ? (
                  <div className="arc-template-rail-section">
                    <div className="arc-template-rail-heading">
                      <strong>Saved in Library</strong>
                      <span>{savedTemplates.length}</span>
                    </div>
                    <div className="arc-template-rail-list">
                      {savedTemplates.map(({ template, plan }) => (
                        <button
                          key={template.id}
                          type="button"
                          className={`arc-template-rail-card ${
                            selectedPatternKey === `library:${template.id}`
                              ? "arc-template-rail-card-active"
                              : ""
                          }`}
                          onClick={() => onChooseLibraryTemplate(template, plan)}
                        >
                          <div className="arc-template-rail-card-meta-row">
                            <p className="arc-meta">Coach template</p>
                            <span>{FOCUS_TAG_LABEL[template.focusTag]}</span>
                          </div>
                          <strong>{plan.title}</strong>
                          <p className="arc-template-rail-submeta">
                            {plan.focusLabel} · {plan.clubLabel}
                          </p>
                          <p>{plan.summary}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div className="arc-template-rail-section">
                  <div className="arc-template-rail-heading">
                    <strong>Preset templates</strong>
                    <span>{filteredTemplates.length}</span>
                  </div>
                  <div className="arc-template-rail-list">
                    {filteredTemplates.map((plan) => (
                      <button
                        key={plan.id}
                        type="button"
                        className={`arc-template-rail-card ${
                          selectedPatternKey === `preset:${plan.id}`
                            ? "arc-template-rail-card-active"
                            : ""
                        }`}
                          onClick={() => onChooseTemplate(plan, `preset:${plan.id}`)}
                      >
                        <div className="arc-template-rail-card-meta-row">
                          <p className="arc-meta">
                            {RANGE_PLAN_KIND_LABEL[plan.kind]} · {plan.totalBalls} balls
                          </p>
                          <span>{FOCUS_TAG_LABEL[focusTagForRangePlan(plan)]}</span>
                        </div>
                        <strong>{plan.title}</strong>
                        <p className="arc-template-rail-submeta">
                          {plan.focusLabel} · {plan.clubLabel}
                        </p>
                        <p>{plan.summary}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {reusablePatterns.length > 0 ? (
                  <div className="arc-template-rail-section">
                    <div className="arc-template-rail-heading">
                      <strong>Reusable coach patterns</strong>
                      <span>{reusablePatterns.length}</span>
                    </div>
                    <div className="arc-template-rail-list">
                      {reusablePatterns.map((pattern) => (
                        <button
                          key={pattern.assignmentId}
                          type="button"
                          className={`arc-template-rail-card ${
                            selectedPatternKey === `history:${pattern.assignmentId}`
                              ? "arc-template-rail-card-active"
                              : ""
                          }`}
                          onClick={() => onChooseReusablePattern(pattern)}
                        >
                          <div className="arc-template-rail-card-meta-row">
                            <p className="arc-meta">
                              Reused from history · {formatShortDate(pattern.updatedAt)}
                            </p>
                            <span>{FOCUS_TAG_LABEL[focusTagForRangePlan(pattern.plan)]}</span>
                          </div>
                          <strong>{pattern.plan.title}</strong>
                          <p className="arc-template-rail-submeta">
                            {pattern.plan.focusLabel} · {pattern.plan.clubLabel}
                          </p>
                          <p>{pattern.plan.summary}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}

                {savedTemplates.length === 0 &&
                filteredTemplates.length === 0 &&
                reusablePatterns.length === 0 ? (
                  <div className="arc-template-rail-empty">
                    <strong>No templates in this lane yet.</strong>
                    <p>
                      Switch the filter, change plan mode, or build a custom plan and save it
                      back into the Library.
                    </p>
                  </div>
                ) : null}
              </>
            ) : (
              <div className="arc-template-rail-note">
                <strong>Custom plans stay structured.</strong>
                <p>
                  Use the editable blocks in the center column to set the ball count,
                  clubs, camera context, goals, and player preview.
                </p>
                <p className="arc-plan-builder-save-note">
                  When this shape feels reusable, save it back into the Library so it becomes
                  a starting point for the next player.
                </p>
              </div>
            )}
          </aside>

          <div className="arc-plan-builder-editor">
            <section className="arc-plan-builder-panel">
              <div className="arc-section-heading">
                <div>
                  <p className="tag">// Plan Structure</p>
                  <h3>{editablePlan.title}</h3>
                </div>
                <p className="arc-meta">{editablePlan.totalBalls} total balls</p>
              </div>
              <div className="arc-plan-form-grid">
                <label>
                  <span>Plan title</span>
                  <input
                    value={editablePlan.title}
                    onChange={(event) =>
                      updatePlan({ ...editablePlan, title: event.target.value })
                    }
                  />
                </label>
                <label>
                  <span>Focus label</span>
                  <input
                    value={editablePlan.focusLabel}
                    onChange={(event) =>
                      updatePlan({ ...editablePlan, focusLabel: event.target.value })
                    }
                  />
                </label>
                <label className="arc-plan-form-wide">
                  <span>Summary</span>
                  <textarea
                    rows={3}
                    value={editablePlan.summary}
                    onChange={(event) =>
                      updatePlan({ ...editablePlan, summary: event.target.value })
                    }
                  />
                </label>
                <label className="arc-plan-form-wide">
                  <span>Coach cue</span>
                  <textarea
                    rows={3}
                    value={editablePlan.coachCue}
                    onChange={(event) =>
                      updatePlan({ ...editablePlan, coachCue: event.target.value })
                    }
                  />
                </label>
                <label className="arc-plan-form-wide">
                  <span>Before Ball 1</span>
                  <textarea
                    rows={3}
                    value={editablePlan.setup}
                    onChange={(event) =>
                      updatePlan({ ...editablePlan, setup: event.target.value })
                    }
                  />
                </label>
                <label className="arc-plan-form-wide">
                  <span>Between blocks</span>
                  <textarea
                    rows={3}
                    value={editablePlan.betweenBlocks}
                    onChange={(event) =>
                      updatePlan({ ...editablePlan, betweenBlocks: event.target.value })
                    }
                  />
                </label>
                <label className="arc-plan-form-wide">
                  <span>Homework / follow-through</span>
                  <textarea
                    rows={3}
                    value={editablePlan.homework}
                    onChange={(event) =>
                      updatePlan({ ...editablePlan, homework: event.target.value })
                    }
                  />
                </label>
              </div>
            </section>

            <RangePlanPrerequisitesPanel
              plan={editablePlan}
              onPlanChange={updatePlan}
            />

            <section className="arc-plan-builder-panel">
              <div className="arc-section-heading">
                <div>
                  <p className="tag">// Structured Controls</p>
                  <h3>Guardrails the player will hear.</h3>
                </div>
              </div>
              <div className="arc-structured-plan-grid">
                <label>
                  <span>Practice window</span>
                  <select
                    value={rangePlanSettings.practiceWindowId}
                    onChange={(event) =>
                      onSettingsChange((current) => ({
                        ...current,
                        practiceWindowId: event.target.value as RangePlanPracticeWindowId,
                      }))
                    }
                  >
                    {RANGE_PLAN_PRACTICE_WINDOW_OPTIONS.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Ball pacing</span>
                  <select
                    value={rangePlanSettings.paceId}
                    onChange={(event) =>
                      onSettingsChange((current) => ({
                        ...current,
                        paceId: event.target.value as RangePlanPaceId,
                      }))
                    }
                  >
                    {RANGE_PLAN_PACE_OPTIONS.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Verbal guardrail</span>
                  <select
                    value={rangePlanSettings.guardrailId}
                    onChange={(event) =>
                      onSettingsChange((current) => ({
                        ...current,
                        guardrailId: event.target.value as RangePlanGuardrailId,
                      }))
                    }
                  >
                    {RANGE_PLAN_GUARDRAIL_OPTIONS.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Media requirement</span>
                  <select
                    value={rangePlanSettings.mediaCaptureId}
                    onChange={(event) =>
                      onSettingsChange((current) => ({
                        ...current,
                        mediaCaptureId: event.target.value as RangePlanMediaCaptureId,
                      }))
                    }
                  >
                    {RANGE_PLAN_MEDIA_CAPTURE_OPTIONS.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Check-in requirement</span>
                  <select
                    value={rangePlanSettings.checkInId}
                    onChange={(event) =>
                      onSettingsChange((current) => ({
                        ...current,
                        checkInId: event.target.value as RangePlanCheckInId,
                      }))
                    }
                  >
                    {RANGE_PLAN_CHECK_IN_OPTIONS.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </section>

            <section className="arc-plan-builder-panel">
              <div className="arc-section-heading">
                <div>
                  <p className="tag">// Block Editor</p>
                  <h3>Build the session block by block.</h3>
                </div>
                <button
                  type="button"
                  className="btn ghost"
                  onClick={() => updatePlan(appendRangePlanBlock(editablePlan, defaultClub))}
                >
                  Add Block
                </button>
              </div>

              <datalist id="arc-range-plan-clubs">
                {bagOptions.map((option) => (
                  <option key={option} value={option} />
                ))}
              </datalist>

              <div className="arc-plan-block-editor-list">
                {editablePlan.blocks.map((block, index) => {
                  const cadenceOptions = block.cadence
                    ? Array.from(
                        new Set([
                          block.cadence,
                          ...RANGE_PLAN_PACE_OPTIONS.map((option) => option.instruction),
                        ]),
                      )
                    : RANGE_PLAN_PACE_OPTIONS.map((option) => option.instruction);
                  const cameraOptions = block.camera
                    ? Array.from(new Set([block.camera, ...RANGE_PLAN_CAMERA_OPTIONS]))
                    : [...RANGE_PLAN_CAMERA_OPTIONS];

                  return (
                    <article key={block.id} className="arc-plan-block-editor">
                      <div className="arc-plan-block-editor-head">
                        <div>
                          <p className="arc-meta">Block {index + 1}</p>
                          <strong>{block.title}</strong>
                        </div>
                        <div className="arc-plan-block-editor-actions">
                          <button
                            type="button"
                            className="btn ghost"
                            onClick={() => updatePlan(moveRangePlanBlock(editablePlan, block.id, -1))}
                            disabled={index === 0}
                          >
                            Up
                          </button>
                          <button
                            type="button"
                            className="btn ghost"
                            onClick={() => updatePlan(moveRangePlanBlock(editablePlan, block.id, 1))}
                            disabled={index === editablePlan.blocks.length - 1}
                          >
                            Down
                          </button>
                          <button
                            type="button"
                            className="btn ghost"
                            onClick={() =>
                              updatePlan(duplicateRangePlanBlock(editablePlan, block.id))
                            }
                          >
                            Duplicate
                          </button>
                          <button
                            type="button"
                            className="btn ghost"
                            onClick={() => updatePlan(removeRangePlanBlock(editablePlan, block.id))}
                            disabled={editablePlan.blocks.length <= 1}
                          >
                            Remove
                          </button>
                        </div>
                      </div>

                      <div className="arc-plan-form-grid">
                        <label>
                          <span>Block title</span>
                          <input
                            value={block.title}
                            onChange={(event) =>
                              updatePlan(
                                updateRangePlanBlock(editablePlan, block.id, (current) => ({
                                  ...current,
                                  title: event.target.value,
                                })),
                              )
                            }
                          />
                        </label>
                        <label>
                          <span>Balls</span>
                          <input
                            type="number"
                            min={1}
                            value={block.balls}
                            onChange={(event) =>
                              updatePlan(
                                updateRangePlanBlock(editablePlan, block.id, (current) => ({
                                  ...current,
                                  balls: Math.max(1, Number(event.target.value) || 1),
                                })),
                              )
                            }
                          />
                        </label>
                        <label>
                          <span>Club</span>
                          <input
                            list="arc-range-plan-clubs"
                            value={block.club}
                            onChange={(event) =>
                              updatePlan(
                                updateRangePlanBlock(editablePlan, block.id, (current) => ({
                                  ...current,
                                  club: event.target.value,
                                })),
                              )
                            }
                          />
                        </label>
                        <label>
                          <span>Camera / setup</span>
                          <select
                            value={block.camera}
                            onChange={(event) =>
                              updatePlan(
                                updateRangePlanBlock(editablePlan, block.id, (current) => ({
                                  ...current,
                                  camera: event.target.value,
                                })),
                              )
                            }
                          >
                            {cameraOptions.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          <span>Pacing</span>
                          <select
                            value={block.cadence}
                            onChange={(event) =>
                              updatePlan(
                                updateRangePlanBlock(editablePlan, block.id, (current) => ({
                                  ...current,
                                  cadence: event.target.value,
                                })),
                              )
                            }
                          >
                            {cadenceOptions.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="arc-plan-form-wide">
                          <span>How the player should run it</span>
                          <textarea
                            rows={2}
                            value={block.how}
                            onChange={(event) =>
                              updatePlan(
                                updateRangePlanBlock(editablePlan, block.id, (current) => ({
                                  ...current,
                                  how: event.target.value,
                                })),
                              )
                            }
                          />
                        </label>
                        <label className="arc-plan-form-wide">
                          <span>Coaching goal</span>
                          <textarea
                            rows={2}
                            value={block.goal}
                            onChange={(event) =>
                              updatePlan(
                                updateRangePlanBlock(editablePlan, block.id, (current) => ({
                                  ...current,
                                  goal: event.target.value,
                                })),
                              )
                            }
                          />
                        </label>
                        <label>
                          <span>Block intent</span>
                          <select
                            value={block.intent ?? "stock"}
                            onChange={(event) =>
                              updatePlan(
                                updateRangePlanBlock(editablePlan, block.id, (current) => ({
                                  ...current,
                                  intent: event.target.value as CoachRangePlanBlockIntent,
                                })),
                              )
                            }
                          >
                            {COACH_RANGE_PLAN_BLOCK_INTENTS.map((option) => (
                              <option key={option} value={option}>
                                {RANGE_PLAN_BLOCK_INTENT_LABEL[option]}
                              </option>
                            ))}
                          </select>
                          <small className="arc-plan-block-hint">
                            {RANGE_PLAN_BLOCK_INTENT_HINT[
                              (block.intent ?? "stock") as CoachRangePlanBlockIntent
                            ]}
                          </small>
                        </label>
                        <label className="arc-plan-form-wide">
                          <span>Success criteria <em>(optional)</em></span>
                          <textarea
                            rows={2}
                            value={block.successCriteria ?? ""}
                            placeholder="e.g. Land 8 of 12 inside the 20-yard window before moving on."
                            onChange={(event) =>
                              updatePlan(
                                updateRangePlanBlock(editablePlan, block.id, (current) => ({
                                  ...current,
                                  successCriteria: event.target.value || null,
                                })),
                              )
                            }
                          />
                        </label>
                        <label className="arc-plan-form-wide">
                          <span>Checkpoint / abort <em>(optional)</em></span>
                          <textarea
                            rows={2}
                            value={block.checkpoint ?? ""}
                            placeholder="e.g. If 5+ same-side misses in a row, stop and message the coach."
                            onChange={(event) =>
                              updatePlan(
                                updateRangePlanBlock(editablePlan, block.id, (current) => ({
                                  ...current,
                                  checkpoint: event.target.value || null,
                                })),
                              )
                            }
                          />
                        </label>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          </div>

          <div className="arc-plan-builder-preview">
            <section className="arc-plan-builder-panel arc-plan-player-preview">
              <div className="arc-section-heading">
                <div>
                  <p className="tag">// Player-Facing Preview</p>
                  <h3>{previewPlan.title}</h3>
                </div>
                <p className="arc-meta">
                  {previewPlan.totalBalls} balls
                  {builderMode === "template" && selectedPatternKey.startsWith("history:")
                    ? " · history pattern"
                    : ""}
                </p>
              </div>
              <p className="arc-muted">
                {collaboration.title} will receive this exactly as a structured range plan.
              </p>
              <div className="arc-range-plan-callout">
                <span>Coach cue</span>
                <strong>{previewPlan.coachCue}</strong>
              </div>
              <div className="arc-player-pill-row">
                <span className="arc-player-pill">
                  Mode <strong>{RANGE_PLAN_KIND_LABEL[previewPlan.kind]}</strong>
                </span>
                <span className="arc-player-pill">
                  Focus <strong>{previewPlan.focusLabel}</strong>
                </span>
                <span className="arc-player-pill">
                  Club <strong>{previewPlan.clubLabel}</strong>
                </span>
              </div>
              <RangePlanDetailContent plan={previewPlan} />
            </section>
          </div>
        </div>

        <div className="arc-plan-builder-footer">
          {selectedLibraryTemplate ? (
            <button
              type="button"
              className="btn ghost"
              onClick={() => onSaveTemplate("update")}
              disabled={saving || templateSaving}
            >
              {templateSaving ? "Saving..." : "Update template"}
            </button>
          ) : null}
          <button
            type="button"
            className="btn ghost"
            onClick={() => onSaveTemplate("create")}
            disabled={saving || templateSaving}
          >
            {templateSaving
              ? "Saving..."
              : selectedLibraryTemplate
                ? "Save as new template"
                : "Save to Library"}
          </button>
          <button
            type="button"
            className="btn ghost"
            onClick={onClose}
            disabled={saving || templateSaving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            onClick={onSend}
            disabled={saving || templateSaving}
          >
            {saving ? "Sending..." : "Send Range Plan"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ArcAssistRail({
  collaboration,
  entitlement,
  latestArcPlanAssignment,
  assignments,
  onSaved,
}: {
  collaboration: Collaboration;
  entitlement: EntitlementState;
  latestArcPlanAssignment: CoachAssignment | null;
  assignments: CoachAssignment[];
  onSaved: () => Promise<void>;
}) {
  const [arcCoachRunning, setArcCoachRunning] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const latestArcCue =
    [...assignments]
      .filter(
        (assignment) =>
          assignment.isCue &&
          assignment.authorKind === "arc_ai" &&
          assignment.status !== "archived" &&
          assignment.status !== "completed",
      )
      .sort((left, right) => Date.parse(right.updatedAt) - Date.parse(left.updatedAt))[0] ??
    null;
  const suggestedArcPlan =
    latestArcPlanAssignment?.rangePlanPrescription ??
    (latestArcPlanAssignment
      ? rangePlanPrescriptionForAssignment(latestArcPlanAssignment)
      : null);
  const fallbackDraft = buildArcCoachActionDraft(collaboration);

  async function runArcCoach() {
    if (!entitlement.isEntitled) return;
    if (DEV_AUTH_BYPASS) {
      setMessage("ARC Assist draft loaded in dev.");
      return;
    }

    setArcCoachRunning(true);
    setMessage(null);
    try {
      const result = await invokeArcCoachPlan(supabase, collaboration.id);
      setMessage(
        result.status === "already_planned"
          ? "ARC Assist already drafted this packet."
          : result.status === "already_running"
            ? "ARC Assist is already drafting this packet."
            : "ARC Assist refreshed the plan, cue, and note drafts.",
      );
      await onSaved();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "ARC Assist failed.");
    } finally {
      setArcCoachRunning(false);
    }
  }

  return (
    <div className="arc-stack">
      <section className="arc-card arc-assist-card">
        <div className="arc-section-heading">
          <div>
            <p className="tag">// ARC Assist</p>
            <h2>Keep ARC visible, but secondary.</h2>
          </div>
          <span className="arc-meta">Human coach stays primary</span>
        </div>
        <p className="arc-muted arc-section-body">
          ARC can draft a plan, cue, and note from the latest packet. Use it as a second
          opinion before you prescribe the next session.
        </p>
        {entitlement.isEntitled ? (
          <div className="arc-action-helper arc-section-body">
            <button
              type="button"
              onClick={() => void runArcCoach()}
              disabled={arcCoachRunning}
            >
              {arcCoachRunning ? "Running ARC Assist..." : "Refresh ARC Assist"}
            </button>
            <span>
              Writes a draft plan, cue, and note from the latest session packet.
            </span>
          </div>
        ) : (
          <p className="arc-meta arc-section-body">
            Coach+ unlocks ARC Assist drafting and coaching recommendations.
          </p>
        )}

        <div className="arc-assist-summary-grid arc-section-body">
          <div className="arc-plan-summary-card arc-plan-summary-card-subtle">
            <p className="arc-meta">Latest ARC plan prompt</p>
            <strong>{suggestedArcPlan?.title || fallbackDraft.title}</strong>
            <p>{suggestedArcPlan?.coachCue || fallbackDraft.body}</p>
          </div>
          <div className="arc-plan-summary-card arc-plan-summary-card-subtle">
            <p className="arc-meta">Suggested next cue</p>
            <strong>{latestArcCue?.title || "ARC has not sent a cue yet."}</strong>
            <p>{latestArcCue?.detail || fallbackDraft.body}</p>
          </div>
        </div>

        {message ? <p className="arc-muted arc-action-message">{message}</p> : null}
      </section>

      <StudentWorkflowPanel assignments={assignments} collaboration={collaboration} />
    </div>
  );
}

function CoachingHistorySection({
  collaboration,
  annotations,
  assignments,
  cues,
  sessionMediaEnabled,
  surfaceTitle,
}: {
  collaboration: Collaboration;
  annotations: CoachAnnotation[];
  assignments: CoachAssignment[];
  cues: CoachAssignment[];
  sessionMediaEnabled: boolean;
  surfaceTitle: string;
}) {
  return (
    <section className="arc-history-band" aria-label="Coaching history">
      <div className="arc-history-heading">
        <div>
          <p className="tag">// Coaching History</p>
          <h2>What the player has seen, heard, and worked on.</h2>
        </div>
        <p className="arc-meta">History is continuity, not the primary action.</p>
      </div>

      <div className="arc-history-grid">
        <PracticeHistoryPanel collaboration={collaboration} />
        <CueHistoryPanel cues={cues} />
      </div>

      <ReviewFramesPanel
        collaboration={collaboration}
        brief={collaboration.briefV2}
        sessionMediaEnabled={sessionMediaEnabled}
        surfaceTitle={surfaceTitle}
      />

      <div className="arc-history-grid">
        <RangePlansPanel assignments={assignments.filter((assignment) => !assignment.isCue)} />
        <StudentBagPanel collaboration={collaboration} />
      </div>

      <ThreadSection title="Coach Notes" empty="No coach notes yet.">
        {annotations
          .sort((left, right) => Date.parse(right.createdAt) - Date.parse(left.createdAt))
          .map((note) => (
            <div key={note.id} className="arc-thread-row">
              <p className="arc-meta">
                {coachAuthorKindLabel(note.authorKind)} note · {formatShortDate(note.createdAt)}
              </p>
              {note.title ? <strong>{note.title}</strong> : null}
              {note.body ? <p>{note.body}</p> : null}
              {note.audioPath ? (
                <VoiceNotePlayer
                  audioPath={note.audioPath}
                  durationSeconds={note.audioDurationSeconds}
                />
              ) : null}
            </div>
          ))}
      </ThreadSection>
    </section>
  );
}

function CueHistoryPanel({ cues }: { cues: CoachAssignment[] }) {
  if (cues.length === 0) return null;

  return (
    <section className="arc-card">
      <div className="arc-section-heading">
        <div>
          <p className="tag">// Quick Cues</p>
          <h2>What the player has heard over time.</h2>
        </div>
        <p className="arc-meta">
          {cues.length} delivered {cues.length === 1 ? "cue" : "cues"}
        </p>
      </div>
      <div className="arc-stack arc-section-body">
        {[...cues]
          .sort((left, right) => Date.parse(right.updatedAt) - Date.parse(left.updatedAt))
          .map((cue) => (
            <div key={cue.id} className="arc-thread-row">
              <p className="arc-meta">
                {coachAuthorKindLabel(cue.authorKind)} · {assignmentStatusLabel(cue.status)}
              </p>
              <strong>{cue.title}</strong>
              {cue.detail ? <p>{cue.detail}</p> : null}
            </div>
          ))}
      </div>
    </section>
  );
}

function StudentMediaPage({ id }: { id: string }) {
  const [collaboration, setCollaboration] = useState<Collaboration | null>(null);
  const [status, setStatus] = useState("Loading student media.");
  const searchParams = new URLSearchParams(window.location.search);
  const selectedSwingId = searchParams.get("swing");
  const selectedSessionId = searchParams.get("session");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const row = DEV_AUTH_BYPASS
          ? demoCoachCollaborations().find((candidate) => candidate.id === id) ?? null
          : await getCollaboration(supabase, id);
        if (!cancelled) {
          setCollaboration(row);
          setStatus(row ? "Ready." : "Collaboration not found.");
        }
      } catch (error) {
        if (!cancelled) setStatus((error as Error).message);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (!collaboration) {
    return (
      <Card eyebrow="Student Media" title={status}>
        <p className="arc-muted">
          Either the row does not exist or you do not have access.
        </p>
      </Card>
    );
  }

  const sessionMediaEnabled = canUseSessionMedia(collaboration);
  const surfaceTitle = collaborationSurfaceTitle(collaboration);
  if (!sessionMediaEnabled) {
    return (
      <Card eyebrow={surfaceTitle} title="Choose a focus session to unlock student media.">
        <p className="arc-muted">
          This coaching connection does not have a focus session yet, so ARC has no clip
          library to open from the coach web. Choose a focus session on the player&apos;s
          phone when you want coach-side clip review.
        </p>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "12px" }}>
          <a className="btn" href={`/students/${encodeURIComponent(collaboration.id)}`}>
            Back to coaching
          </a>
          <a className="btn" href="/today">
            Back to Today
          </a>
        </div>
      </Card>
    );
  }

  const media = studentMediaItems(collaboration);
  const mediaTimeline = groupStudentMediaByDateAndSession(media);
  const sessionCount = mediaTimeline.reduce((count, dateGroup) => count + dateGroup.sessions.length, 0);
  const activeReviewSeed =
    mediaReviewSeedFromItems(media, {
      sessionId: selectedSessionId,
      swingId: selectedSwingId,
    }) ??
    (media.length > 0
      ? mediaReviewSeedFromItems(media, {
          sessionId: media[0]?.sessionId ?? null,
        })
      : null);
  const activeSessionId = activeReviewSeed?.sessionId ?? null;
  const activeSession =
    mediaTimeline
      .flatMap((dateGroup) => dateGroup.sessions)
      .find((session) => session.id === activeSessionId) ?? null;
  const timelineSummary =
    media.length > 0
      ? `${mediaTimeline.length} ${mediaTimeline.length === 1 ? "date" : "dates"} · ${sessionCount} ${
          sessionCount === 1 ? "session" : "sessions"
        } · ${media.length} ${media.length === 1 ? "swing" : "swings"}`
      : "No student media yet.";

  return (
    <div className="arc-student-workbench">
      <section className="arc-card arc-media-hero">
        <div>
          <p className="tag">// Student Media</p>
          <h1>{collaboration.title}</h1>
          <p className="arc-muted">
            Swings, saved frames, and coach review moments for this student.
          </p>
        </div>
        <a className="arc-frame-link" href={`/students/${encodeURIComponent(collaboration.id)}`}>
          Back to workspace
        </a>
      </section>

      {activeSession ? (
        <section className="arc-card arc-media-focus-card">
          <div className="arc-section-heading">
            <div>
              <p className="tag">// Focus Session</p>
              <h2>{activeSession.label}</h2>
              <p className="arc-muted">
                {activeReviewSeed?.title
                  ? `${activeReviewSeed.title} · ${activeReviewSeed.summary}`
                  : activeSession.outcome}
              </p>
            </div>
            <div className="arc-section-actions">
              <span className="arc-player-pill">
                Club <strong>{activeSession.club}</strong>
              </span>
              <span className="arc-player-pill">
                Swings <strong>{activeSession.swings.length}</strong>
              </span>
            </div>
          </div>
          <div className="arc-next-session-actions arc-section-body">
            <a
              className="btn"
              href={buildReviewedSessionCoachPath(collaboration.id, {
                sessionId: activeSession.id,
                swingId: activeReviewSeed?.swingId ?? activeSession.swings[0]?.swingId ?? null,
              })}
            >
              Use this session for next plan
            </a>
            {activeReviewSeed?.swingId ? (
              <a
                className="btn ghost"
                href={buildStudentMediaPath(collaboration.id, {
                  sessionId: activeSession.id,
                  swingId: activeReviewSeed.swingId,
                })}
              >
                Keep this swing in view
              </a>
            ) : null}
          </div>
        </section>
      ) : null}

      <section className="arc-card">
        <div className="arc-section-heading">
          <div>
            <p className="tag">// Media Timeline</p>
            <h2>{timelineSummary}</h2>
          </div>
          {collaboration.briefV2?.meta.club ? (
            <p className="arc-meta">{collaboration.briefV2.meta.club}</p>
          ) : null}
        </div>
        {media.length === 0 ? (
          <p className="arc-muted arc-section-body">
            Once swing uploads are indexed, coaches will see the student's full media history here.
          </p>
        ) : (
          <div className="arc-media-timeline arc-section-body">
            {mediaTimeline.map((dateGroup) => (
              <section key={dateGroup.dateKey} className="arc-media-date-group">
                <div className="arc-media-date-heading">
                  <div>
                    <p className="tag">// Date</p>
                    <strong>{dateGroup.dateLabel}</strong>
                  </div>
                  <span>
                    {dateGroup.sessions.length}{" "}
                    {dateGroup.sessions.length === 1 ? "session" : "sessions"}
                  </span>
                </div>

                {dateGroup.sessions.map((session) => (
                  <section
                    key={session.id}
                    className={`arc-media-session ${
                      session.id === activeSessionId ? "arc-media-session-selected" : ""
                    }`}
                  >
                    <div className="arc-media-session-head">
                      <div>
                        <p className="tag">// Session</p>
                        <h3>{session.label}</h3>
                        {session.outcome ? <p className="arc-muted">{session.outcome}</p> : null}
                      </div>
                      <div className="arc-media-meta-row">
                        <span>{session.club}</span>
                        <span>
                          {session.swings.length}{" "}
                          {session.swings.length === 1 ? "swing" : "swings"}
                        </span>
                        <a
                          className="arc-frame-link"
                          href={buildStudentMediaPath(collaboration.id, {
                            sessionId: session.id,
                          })}
                        >
                          Focus session
                        </a>
                      </div>
                    </div>

                    <div className="arc-media-swing-list" aria-label={`${session.label} swings`}>
                      {session.swings.map((item) => (
                        <article
                          key={item.swingId}
                          className={`arc-media-swing-row ${
                            selectedSwingId === item.swingId ? "arc-media-swing-row-selected" : ""
                          }`}
                        >
                          <div>
                            <p className="arc-meta">{item.roleLabel}</p>
                            <a
                              className="arc-media-swing-title"
                              href={item.reviewHref}
                            >
                              {item.title}
                            </a>
                          </div>
                          <div className="arc-media-swing-read">
                            <p>{item.summary}</p>
                            {item.metrics.length > 0 ? (
                              <div className="arc-frame-metrics">
                                {item.metrics.slice(0, 3).map((metricRow) => (
                                  <span key={`${item.swingId}-${metricRow.label}`}>
                                    {metricRow.label}: {metricRow.formatted}
                                  </span>
                                ))}
                              </div>
                            ) : null}
                          </div>
                          <a
                            className="arc-frame-link"
                            href={item.url ?? item.reviewHref}
                            {...(item.url ? { target: "_blank", rel: "noreferrer" } : {})}
                          >
                            {item.url ? "Open footage" : "Open swing record"}
                          </a>
                        </article>
                      ))}
                    </div>
                  </section>
                ))}
              </section>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function StudentWorkflowPanel({
  assignments,
  collaboration,
}: {
  assignments: CoachAssignment[];
  collaboration: Collaboration;
}) {
  const brief = collaboration.briefV2;
  const cueCount = assignments.filter((assignment) => assignment.isCue).length;
  const planCount = assignments.filter((assignment) => !assignment.isCue).length;
  const recurringIssue =
    brief?.historicalContext.recurringFailureModes[0] ||
    brief?.missPattern.costlyMiss ||
    "No repeating miss yet";
  const returnWindow =
    brief?.missPattern.driftOnset ||
    brief?.missPattern.fatigueOnset ||
    "Watch the next range block";
  const bestFix =
    brief?.causalDrivers[0]?.correctiveCue ||
    brief?.playbook.steps[0]?.title ||
    "Set the next cue after review";
  return (
    <section className="arc-card arc-workflow-panel">
      <p className="tag">// Pattern History</p>
      <h2>What tends to return.</h2>
      <div className="arc-workflow-list">
        <WorkflowRow label="Cues / plans" value={`${cueCount} / ${planCount}`} />
        <WorkflowRow label="Recurring issue" value={recurringIssue} />
        <WorkflowRow label="When it returns" value={returnWindow} />
        <WorkflowRow label="Best fix so far" value={bestFix} />
      </div>
    </section>
  );
}

function WorkflowRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="arc-workflow-row">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function AcceptInvitePage({
  inviteCode,
  onNavigate,
}: {
  inviteCode: string;
  onNavigate: (path: string, replace?: boolean) => void;
}) {
  const [message, setMessage] = useState("Joining the coach workspace.");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function acceptInvite() {
      setMessage("Joining the coach workspace.");
      setErrorMessage(null);
      try {
        const collaboration = await acceptCoachCollaboration(supabase, inviteCode);
        if (cancelled) return;
        setMessage("Workspace joined. Opening the brief.");
        onNavigate(`/students/${encodeURIComponent(collaboration.id)}`, true);
      } catch (error) {
        if (!cancelled) {
          setMessage("We could not join that workspace.");
          setErrorMessage(inviteErrorMessage(error));
        }
      }
    }

    void acceptInvite();
    return () => {
      cancelled = true;
    };
  }, [inviteCode, onNavigate]);

  return (
    <Card eyebrow="Coach Invite" title={message}>
      <p className="arc-muted">Invite code: {inviteCode}</p>
      {errorMessage ? (
        <>
          <p className="arc-error">{errorMessage}</p>
          <CoachInviteJoinForm
            label="Try another invite code"
            buttonLabel="Try Code"
            initialCode={inviteCode}
            onJoin={(code) => onNavigate(invitePath(code), true)}
          />
        </>
      ) : null}
    </Card>
  );
}

function UnauthPreviewPage({ token }: { token: string }) {
  const [preview, setPreview] = useState<UnauthPreview | null>(null);
  const [status, setStatus] = useState("Loading preview.");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await fetchUnauthPreview(supabase, token);
        if (!cancelled) {
          setPreview(data);
          setStatus(data ? "Ready." : "This brief is not available.");
        }
      } catch (error) {
        if (!cancelled) setStatus((error as Error).message);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (!preview) {
    return (
      <div className="arc-stack">
        <Card eyebrow="Preview" title={status}>
          <p className="arc-muted">
            The link may be wrong or the collaboration was revoked. Ask the player to send a new link.
          </p>
        </Card>
        <SignInGate next="/today" />
      </div>
    );
  }

  return (
    <div className="arc-stack">
      <section className="arc-card">
        <ArcBrandLockup size="compact" className="preview-brand-lockup" />
        <h1>{preview.title}</h1>
        <p className="arc-muted">
          {preview.studentDisplayName} / {preview.sessionLabel}
        </p>
        <p className="arc-section-body">{preview.oneLineRead}</p>
        <p className="arc-meta">
          Confidence: {preview.dataConfidence}
          {preview.club ? ` / Club: ${preview.club}` : ""}
        </p>
        {preview.topFacts.length > 0 ? (
          <div className="arc-stack arc-section-body">
            {preview.topFacts.map((fact, index) => {
              const provenanceLabel = provenanceDisplayLabel(fact.provenance);
              return (
                <div key={`${fact.label}-${index}`}>
                  {provenanceLabel ? (
                    <p className="arc-meta">{provenanceLabel.toUpperCase()}</p>
                  ) : null}
                  <strong>{fact.label}</strong>
                  {fact.value ? <p>{fact.value}</p> : null}
                </div>
              );
            })}
          </div>
        ) : null}
      </section>
      <SignInGate next="/today" />
      <p className="arc-muted arc-footnote">
        You are seeing a limited preview. Sign in to comment, hear voice notes, view clips, and see the full brief.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Voice — record + upload + playback
// ---------------------------------------------------------------------------

function VoiceRecorder({
  userId,
  onRecorded,
  onDiscard,
  recorded,
}: {
  userId: string;
  onRecorded: (note: UploadedVoiceNote) => void;
  onDiscard: () => void;
  recorded: UploadedVoiceNote | null;
}) {
  const [state, setState] = useState<
    "idle" | "recording" | "uploading" | "error"
  >("idle");
  const [error, setError] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef<number | null>(null);
  const tickRef = useRef<number | null>(null);

  const stopTicking = () => {
    if (tickRef.current != null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
  };

  const releaseStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  useEffect(() => {
    return () => {
      stopTicking();
      releaseStream();
    };
  }, []);

  if (!voiceRecordingSupported()) {
    return (
      <p className="arc-meta">
        Browser doesn't support recording. Use a different browser or attach
        audio later from the iOS app.
      </p>
    );
  }

  async function start() {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = preferredVoiceRecordingMime();
      const recorder = mime
        ? new MediaRecorder(stream, { mimeType: mime })
        : new MediaRecorder(stream);
      recorderRef.current = recorder;
      chunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      recorder.onstop = async () => {
        stopTicking();
        releaseStream();
        const elapsed = startedAtRef.current
          ? (Date.now() - startedAtRef.current) / 1000
          : 0;
        startedAtRef.current = null;
        const blob = new Blob(chunksRef.current, {
          type: recorder.mimeType || mime || "audio/webm",
        });
        if (blob.size === 0) {
          setState("idle");
          setError("No audio captured. Try again.");
          return;
        }
        setState("uploading");
        try {
          const uploaded = await uploadVoiceNote(supabase, {
            userId,
            blob,
            durationSeconds: Math.max(0.5, elapsed),
          });
          onRecorded(uploaded);
          setState("idle");
          setElapsedSeconds(0);
        } catch (err) {
          setError((err as Error).message);
          setState("error");
        }
      };
      recorder.start(100);
      startedAtRef.current = Date.now();
      setElapsedSeconds(0);
      tickRef.current = window.setInterval(() => {
        if (startedAtRef.current) {
          setElapsedSeconds((Date.now() - startedAtRef.current) / 1000);
        }
      }, 200);
      setState("recording");
    } catch (err) {
      setError((err as Error).message || "Mic permission denied.");
      setState("error");
      releaseStream();
    }
  }

  function stop() {
    recorderRef.current?.stop();
  }

  return (
    <div
      style={{
        display: "flex",
        gap: "8px",
        alignItems: "center",
        flexWrap: "wrap",
      }}
    >
      {recorded ? (
        <>
          <span className="arc-meta">
            Voice note ready · {formatDuration(recorded.durationSeconds)}
          </span>
          <button type="button" className="btn" onClick={onDiscard}>
            Discard
          </button>
        </>
      ) : state === "recording" ? (
        <>
          <span className="arc-meta" style={{ color: "rgba(255, 120, 120, 0.9)" }}>
            ● Recording · {formatDuration(elapsedSeconds)}
          </span>
          <button type="button" className="btn" onClick={stop}>
            Stop
          </button>
        </>
      ) : state === "uploading" ? (
        <span className="arc-meta">Uploading voice note…</span>
      ) : (
        <button type="button" className="btn" onClick={() => void start()}>
          Record voice note
        </button>
      )}
      {error ? <p className="arc-meta">Error: {error}</p> : null}
    </div>
  );
}

function VoiceNotePlayer({
  audioPath,
  durationSeconds,
}: {
  audioPath: string;
  durationSeconds?: number | null;
}) {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [state, setState] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setState("loading");
    setError(null);
    try {
      const { url } = await signVoiceNoteUrl(supabase, audioPath);
      setSignedUrl(url);
      setState("ready");
    } catch (err) {
      setError((err as Error).message);
      setState("error");
    }
  };

  const durationLabel =
    typeof durationSeconds === "number" && durationSeconds > 0
      ? formatDuration(durationSeconds)
      : null;

  if (state === "ready" && signedUrl) {
    return (
      <div style={{ marginTop: "4px" }}>
        <audio controls src={signedUrl} style={{ width: "100%" }} />
        {durationLabel ? (
          <p className="arc-meta" style={{ margin: "4px 0 0" }}>
            {durationLabel}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", gap: "8px", alignItems: "center", marginTop: "4px" }}>
      <button
        type="button"
        className="btn"
        onClick={() => void load()}
        disabled={state === "loading"}
      >
        {state === "loading"
          ? "Loading…"
          : `▶ Play voice note${durationLabel ? ` (${durationLabel})` : ""}`}
      </button>
      {error ? <span className="arc-meta">Error: {error}</span> : null}
    </div>
  );
}

function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const whole = Math.floor(seconds);
  const mins = Math.floor(whole / 60);
  const secs = whole % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

function coachAuthorKindLabel(kind: CoachAuthorKind): string {
  switch (kind) {
    case "player":
      return "Player";
    case "coach":
      return "Coach";
    case "arc_ai":
      return "ARC Coach";
  }
}

function RangePlansPanel({ assignments }: { assignments: CoachAssignment[] }) {
  const plans = assignments.filter(
    (assignment) => assignment.status !== "archived",
  );
  if (plans.length === 0) return null;

  return (
    <section className="arc-card arc-range-plan-panel">
      <div className="arc-section-heading">
        <div>
          <p className="tag">// Range Plan History</p>
          <h2>What has already been prescribed.</h2>
        </div>
        <p className="arc-meta">
          {plans.length} saved {plans.length === 1 ? "plan" : "plans"}
        </p>
      </div>
      <div className="arc-range-plan-stack arc-section-body">
        {plans.map((assignment) => (
          <RangePlanCard key={assignment.id} assignment={assignment} />
        ))}
      </div>
    </section>
  );
}

function RangePlanCard({ assignment }: { assignment: CoachAssignment }) {
  const prescription =
    assignment.rangePlanPrescription ?? rangePlanPrescriptionForAssignment(assignment);
  const blocks = prescription?.blocks ?? parseRangePlanBlocks(assignment.detail);
  const totalBalls =
    prescription?.totalBalls ??
    blocks.reduce((sum, block) => sum + block.balls, 0);
  const planKind = prescription?.kind ?? "coach_plan";
  const homework = prescription?.homework ?? homeworkLineFromDetail(assignment.detail);

  return (
    <article className="arc-range-plan-card">
      <div className="arc-range-plan-head">
        <div>
          <p className="arc-meta">
            {coachAuthorKindLabel(assignment.authorKind)} · {RANGE_PLAN_KIND_LABEL[planKind]} · {assignment.status}
          </p>
          <h3>{assignment.title}</h3>
        </div>
        {totalBalls > 0 ? (
          <strong className="arc-range-plan-total">{totalBalls} balls</strong>
        ) : null}
      </div>

      {prescription ? (
        <>
          <p className="arc-muted">{prescription.summary}</p>
          <div className="arc-range-plan-callout">
            <span>Coach cue</span>
            <strong>{prescription.coachCue}</strong>
          </div>
        </>
      ) : (
        <p className="arc-muted">{assignment.detail}</p>
      )}

      {blocks.length > 0 ? (
        <details className="arc-range-plan-details">
          <summary>View {blocks.length} plan blocks</summary>
          <RangePlanBlockGrid blocks={blocks} />
        </details>
      ) : null}

      {homework ? (
        <div className="arc-range-plan-homework">
          <span>Homework</span>
          <p>{homework}</p>
        </div>
      ) : null}
    </article>
  );
}

function RangePlanDetailContent({ plan }: { plan: RangePlanPrescription }) {
  const prerequisites = plan.prerequisites?.filter((entry) => entry.trim().length > 0) ?? [];
  const groundTruth = plan.groundTruth ?? null;
  return (
    <div className="arc-range-plan-detail-stack">
      {prerequisites.length > 0 ? (
        <div className="arc-range-plan-context">
          <span>Prerequisites</span>
          <ul className="arc-range-plan-prereq-list">
            {prerequisites.map((entry, idx) => (
              <li key={`prereq-${idx}`}>{entry}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="arc-range-plan-context">
        <span>Before Ball 1</span>
        <p>{plan.setup}</p>
      </div>
      {groundTruth && groundTruth.method !== "none" ? (
        <div className="arc-range-plan-context">
          <span>Ground truth</span>
          <p>
            <strong>{RANGE_PLAN_GROUND_TRUTH_LABEL[groundTruth.method]}</strong>
            {groundTruth.cadence ? ` · ${groundTruth.cadence}` : ""}
          </p>
        </div>
      ) : null}
      <RangePlanBlockGrid blocks={plan.blocks} />
      <div className="arc-range-plan-context">
        <span>Between blocks</span>
        <p>{plan.betweenBlocks}</p>
      </div>
      <div className="arc-range-plan-homework">
        <span>Homework</span>
        <p>{plan.homework}</p>
      </div>
    </div>
  );
}

function RangePlanPrerequisitesPanel({
  plan,
  onPlanChange,
}: {
  plan: RangePlanPrescription;
  onPlanChange: (next: RangePlanPrescription) => void;
}) {
  const prerequisites = plan.prerequisites ?? [];
  const groundTruth: CoachRangePlanGroundTruth =
    plan.groundTruth ?? { method: "none", cadence: null };

  function setPrerequisite(index: number, value: string) {
    const next = [...prerequisites];
    next[index] = value;
    onPlanChange({ ...plan, prerequisites: next });
  }

  function addPrerequisite() {
    onPlanChange({ ...plan, prerequisites: [...prerequisites, ""] });
  }

  function removePrerequisite(index: number) {
    const next = prerequisites.filter((_, idx) => idx !== index);
    onPlanChange({
      ...plan,
      prerequisites: next.length > 0 ? next : undefined,
    });
  }

  function setGroundTruthMethod(method: CoachRangePlanGroundTruthMethod) {
    if (method === "none") {
      onPlanChange({ ...plan, groundTruth: null });
      return;
    }
    onPlanChange({
      ...plan,
      groundTruth: { method, cadence: groundTruth.cadence ?? null },
    });
  }

  function setGroundTruthCadence(cadence: string) {
    onPlanChange({
      ...plan,
      groundTruth: {
        method: groundTruth.method === "none" ? "manual" : groundTruth.method,
        cadence: cadence || null,
      },
    });
  }

  return (
    <section className="arc-plan-builder-panel">
      <div className="arc-section-heading">
        <div>
          <p className="tag">// Prerequisites &amp; Ground Truth</p>
          <h3>Set what has to be true before the first ball.</h3>
        </div>
      </div>

      <div className="arc-plan-prereq-list">
        {prerequisites.length === 0 ? (
          <p className="arc-muted">
            No prerequisites yet. Add the setup steps a player must complete
            before swinging — pairing devices, confirming labels, calibrating.
          </p>
        ) : (
          prerequisites.map((entry, index) => (
            <div key={`prereq-row-${index}`} className="arc-plan-prereq-row">
              <input
                value={entry}
                placeholder={
                  index === 0
                    ? "Pair the launch monitor and confirm shot data."
                    : "Add a prerequisite step…"
                }
                onChange={(event) => setPrerequisite(index, event.target.value)}
              />
              <button
                type="button"
                className="btn ghost"
                onClick={() => removePrerequisite(index)}
              >
                Remove
              </button>
            </div>
          ))
        )}
        <button type="button" className="btn ghost" onClick={addPrerequisite}>
          Add prerequisite
        </button>
      </div>

      <div className="arc-plan-form-grid arc-plan-prereq-ground-truth">
        <label>
          <span>Ground-truth method</span>
          <select
            value={groundTruth.method}
            onChange={(event) =>
              setGroundTruthMethod(
                event.target.value as CoachRangePlanGroundTruthMethod,
              )
            }
          >
            {COACH_RANGE_PLAN_GROUND_TRUTH_METHODS.map((option) => (
              <option key={option} value={option}>
                {RANGE_PLAN_GROUND_TRUTH_LABEL[option]}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Capture cadence</span>
          <input
            value={groundTruth.cadence ?? ""}
            disabled={groundTruth.method === "none"}
            placeholder="e.g. 'shot N driver' before each ball"
            onChange={(event) => setGroundTruthCadence(event.target.value)}
          />
        </label>
      </div>
    </section>
  );
}

function RangePlanBlockGrid({ blocks }: { blocks: RangePlanBlock[] }) {
  return (
    <div className="arc-range-block-grid">
      {blocks.map((block) => {
        const intent = (block.intent ?? "stock") as CoachRangePlanBlockIntent;
        return (
          <div key={block.id} className="arc-range-block-card">
            <div className="arc-range-block-count">
              <strong>{block.balls}</strong>
              <span>balls</span>
            </div>
            <div>
              <div className="arc-range-block-meta-row">
                <span>{block.club} · {block.camera}</span>
                <span className="arc-range-block-intent-pill">
                  {RANGE_PLAN_BLOCK_INTENT_LABEL[intent]}
                </span>
              </div>
              <strong>{block.title}</strong>
              <p>{block.how}</p>
              <small>{block.cadence}</small>
              <small>{block.goal}</small>
              {block.successCriteria ? (
                <small className="arc-range-block-success">
                  Pass: {block.successCriteria}
                </small>
              ) : null}
              {block.checkpoint ? (
                <small className="arc-range-block-checkpoint">
                  Checkpoint: {block.checkpoint}
                </small>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function rangePlanPrescriptionForAssignment(
  assignment: CoachAssignment,
): RangePlanPrescription | null {
  return (
    RANGE_PLAN_PRESETS.find((preset) => preset.templateId === assignment.sourceDrillTemplateId) ??
    RANGE_PLAN_PRESETS.find((preset) => assignment.title.includes(preset.title)) ??
    null
  );
}

function parseRangePlanBlocks(detail: string): RangePlanBlock[] {
  const lines = detail.split("\n");
  const blocks: RangePlanBlock[] = [];
  const blockPattern = /^\s*(\d+)\s+balls?\s+[—:-]\s+(.+?)\s+·\s+(.+?)\s+·\s+(.+)$/i;
  lines.forEach((line, index) => {
    const match = line.match(blockPattern);
    if (!match) return;
    blocks.push({
      id: `parsed-${index}`,
      balls: Number(match[1]),
      title: match[2].trim(),
      club: match[3].trim(),
      camera: match[4].trim(),
      cadence: "Follow coach cadence.",
      how: "Run this block as written.",
      goal: "Complete the prescribed reps.",
    });
  });
  return blocks;
}

function homeworkLineFromDetail(detail: string): string | null {
  const line = detail
    .split("\n")
    .find((candidate) => candidate.toLowerCase().startsWith("homework:"));
  return line ? line.replace(/^homework:\s*/i, "").trim() : null;
}

function rangePlanOptionById<T extends string>(
  options: RangePlanStructuredOption<T>[],
  id: T,
): RangePlanStructuredOption<T> {
  return (
    options.find((option) => option.id === id) ??
    (options[0] as RangePlanStructuredOption<T>)
  );
}

function rangePlanPresetById(id: string): RangePlanPrescription {
  return (
    RANGE_PLAN_PRESETS.find((preset) => preset.id === id) ??
    (RANGE_PLAN_PRESETS[0] as RangePlanPrescription)
  );
}

function isUUID(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value,
  );
}

function defaultSettingsForRangePlan(planId: string): StructuredRangePlanSettings {
  return RANGE_PLAN_DEFAULT_SETTINGS[planId] ?? DEFAULT_RANGE_PLAN_SETTINGS;
}

function planDetailFromPrescription(
  plan: RangePlanPrescription,
  settings: StructuredRangePlanSettings = defaultSettingsForRangePlan(plan.id),
): string {
  return rangePlanFallbackDetail(rangePlanPrescriptionForSend(plan, settings));
}

function rangePlanFallbackDetail(plan: RangePlanPrescription): string {
  if (plan.id === "mcnally-5w-validation") {
    return "Structured 150-ball range plan for fairway validation with a clean Driver block.";
  }
  return `Structured ${plan.totalBalls}-ball range plan for ${plan.focusLabel}.`;
}

function rangePlanPrescriptionForSend(
  plan: RangePlanPrescription,
  settings: StructuredRangePlanSettings,
): RangePlanPrescription {
  const defaultSettings = defaultSettingsForRangePlan(plan.id);
  const pace = rangePlanOptionById(RANGE_PLAN_PACE_OPTIONS, settings.paceId);
  const guardrail = rangePlanOptionById(
    RANGE_PLAN_GUARDRAIL_OPTIONS,
    settings.guardrailId,
  );
  const mediaCapture = rangePlanOptionById(
    RANGE_PLAN_MEDIA_CAPTURE_OPTIONS,
    settings.mediaCaptureId,
  );
  const checkIn = rangePlanOptionById(
    RANGE_PLAN_CHECK_IN_OPTIONS,
    settings.checkInId,
  );
  const shouldAppendPace = settings.paceId !== defaultSettings.paceId;
  const shouldAppendGuardrail = settings.guardrailId !== defaultSettings.guardrailId;
  const shouldAppendMedia = settings.mediaCaptureId !== defaultSettings.mediaCaptureId;
  const shouldAppendCheckIn = settings.checkInId !== defaultSettings.checkInId;
  const planWithPracticeWindow = applyPracticeWindowToPlan(plan, settings.practiceWindowId);

  return {
    ...planWithPracticeWindow,
    setup: shouldAppendPace
      ? `${planWithPracticeWindow.setup} ${pace.instruction}`
      : planWithPracticeWindow.setup,
    coachCue: shouldAppendGuardrail
      ? `${planWithPracticeWindow.coachCue} ${guardrail.instruction}`
      : planWithPracticeWindow.coachCue,
    homework: [
      planWithPracticeWindow.homework,
      shouldAppendMedia ? mediaCapture.instruction : null,
      shouldAppendCheckIn ? checkIn.instruction : null,
    ]
      .filter(Boolean)
      .join(" "),
    betweenBlocks: shouldAppendGuardrail
      ? `${planWithPracticeWindow.betweenBlocks} ${guardrail.instruction}`
      : planWithPracticeWindow.betweenBlocks,
    blocks: planWithPracticeWindow.blocks.map((block) => ({
      ...block,
      cadence:
        !shouldAppendPace || settings.paceId === "plan_cadence"
          ? block.cadence
          : pace.instruction,
    })),
  };
}

function RangePlanPreview({
  plan,
  settings,
}: {
  plan: RangePlanPrescription | null;
  settings?: StructuredRangePlanSettings;
}) {
  if (!plan) return null;
  const activeSettings = settings ?? defaultSettingsForRangePlan(plan.id);
  const practiceWindow = rangePlanOptionById(
    RANGE_PLAN_PRACTICE_WINDOW_OPTIONS,
    activeSettings.practiceWindowId,
  );
  const pace = rangePlanOptionById(RANGE_PLAN_PACE_OPTIONS, activeSettings.paceId);
  const guardrail = rangePlanOptionById(
    RANGE_PLAN_GUARDRAIL_OPTIONS,
    activeSettings.guardrailId,
  );
  const mediaCapture = rangePlanOptionById(
    RANGE_PLAN_MEDIA_CAPTURE_OPTIONS,
    activeSettings.mediaCaptureId,
  );
  const checkIn = rangePlanOptionById(
    RANGE_PLAN_CHECK_IN_OPTIONS,
    activeSettings.checkInId,
  );
  return (
    <div className="arc-range-plan-preview" aria-label="Range plan preview">
      <div className="arc-range-plan-head">
        <div>
          <span>{RANGE_PLAN_KIND_LABEL[plan.kind]}</span>
          <strong>{plan.totalBalls} balls prescribed</strong>
        </div>
        <span>{plan.blocks.length} blocks</span>
      </div>
      <p className="arc-muted">{plan.summary}</p>
      <div className="arc-range-plan-rule-grid">
        <div>
          <span>Verbal guardrail</span>
          <strong>{guardrail.label}</strong>
        </div>
        <div>
          <span>Practice window</span>
          <strong>{practiceWindow.label}</strong>
        </div>
        <div>
          <span>Ball pacing</span>
          <strong>{pace.label}</strong>
        </div>
        <div>
          <span>Media</span>
          <strong>{mediaCapture.label}</strong>
        </div>
        <div>
          <span>Check-in</span>
          <strong>{checkIn.label}</strong>
        </div>
      </div>
      <details className="arc-range-plan-details">
        <summary>View block outline</summary>
        <div className="arc-range-plan-mini-grid">
          {plan.blocks.map((block) => (
            <div key={block.id}>
              <strong>{block.balls}</strong>
              <span>{block.club}</span>
              <small>{block.title}</small>
            </div>
          ))}
        </div>
      </details>
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function CoachActions({
  collaboration,
  collaborationId,
  sessionId,
  onSaved,
}: {
  collaboration: Collaboration;
  collaborationId: string;
  sessionId: string | null;
  onSaved: () => Promise<void>;
}) {
  const [mode, setMode] = useState<CoachActionMode>("note");
  const [rangePlanKind, setRangePlanKind] = useState<RangePlanKind>("coach_plan");
  const [selectedRangePlanId, setSelectedRangePlanId] = useState<string>("");
  const [rangePlanSettings, setRangePlanSettings] = useState<StructuredRangePlanSettings>(
    DEFAULT_RANGE_PLAN_SETTINGS,
  );
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [arcPlanDraft, setArcPlanDraft] = useState<{
    title: string;
    body: string;
    rangePlanPrescription: CoachRangePlanPrescription | null;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [arcCoachRunning, setArcCoachRunning] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  // Voice-note state. Lives only in Note mode; switching modes clears
  // it so an unrelated cue/assignment doesn't carry audio provenance
  // it wasn't authored with.
  const [voiceNote, setVoiceNote] = useState<UploadedVoiceNote | null>(null);
  const [voiceUserId, setVoiceUserId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function loadUser() {
      if (DEV_AUTH_BYPASS) {
        setVoiceUserId(DEV_COACH_USER.id);
        return;
      }

      const { data } = await supabase.auth.getUser();
      if (!cancelled) setVoiceUserId(data.user?.id ?? null);
    }
    void loadUser();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredRangePlans = RANGE_PLAN_PRESETS.filter(
    (plan) => plan.kind === rangePlanKind,
  );
  const selectedRangePlan =
    RANGE_PLAN_PRESETS.find((preset) => preset.id === selectedRangePlanId) ?? null;
  const selectedRangePlanPrescription = selectedRangePlan
    ? rangePlanPrescriptionForSend(selectedRangePlan, rangePlanSettings)
    : null;
  const selectedRangePlanDetail = selectedRangePlan
    ? planDetailFromPrescription(selectedRangePlan, rangePlanSettings)
    : "";
  const selectedRangePlanSourceTemplateId =
    selectedRangePlan?.templateId && isUUID(selectedRangePlan.templateId)
      ? selectedRangePlan.templateId
      : null;

  function handleSelectRangePlan(nextId: string) {
    setSelectedRangePlanId(nextId);
    if (nextId) setArcPlanDraft(null);
    if (nextId) setRangePlanSettings(defaultSettingsForRangePlan(nextId));
  }

  function handleRangePlanKind(nextKind: RangePlanKind) {
    setRangePlanKind(nextKind);
    setSelectedRangePlanId("");
  }

  // Switching modes should clear the template pick AND any in-progress
  // voice note so a stale provenance cannot attach to a Note or Send
  // Cue that was authored from scratch.
  function switchMode(next: CoachActionMode) {
    setMode(next);
    if (next !== "plan") {
      setSelectedRangePlanId("");
      setArcPlanDraft(null);
    }
    if (next !== "note") setVoiceNote(null);
  }

  const hasSubmitPayload =
    mode === "plan"
      ? Boolean(selectedRangePlan || arcPlanDraft)
      : Boolean(title.trim() || body.trim()) || Boolean(voiceNote);

  function applyArcCoachDraft() {
    const draft = buildArcCoachActionDraft(collaboration);
    setMode(draft.mode);
    setTitle("");
    setBody("");
    setSelectedRangePlanId("");
    setRangePlanKind("homework");
    setRangePlanSettings(defaultSettingsForRangePlan(draft.suggestedRangePlanId ?? "next-lesson-homework"));
    setArcPlanDraft({
      title: draft.title,
      body: draft.body,
      rangePlanPrescription: draft.rangePlanPrescription,
    });
    setVoiceNote(null);
    setMessage("ARC draft loaded. Review it, then send through the coach workflow.");
  }

  async function runArcCoach() {
    if (DEV_AUTH_BYPASS) {
      applyArcCoachDraft();
      return;
    }

    setArcCoachRunning(true);
    setMessage(null);
    try {
      const result = await invokeArcCoachPlan(supabase, collaborationId);
      const nextMessage =
        result.status === "already_planned"
          ? "ARC Coach already planned this packet."
          : result.status === "already_running"
            ? "ARC Coach is already planning this packet."
            : "ARC Coach wrote the weekly note, cue, and range plan.";
      setMessage(nextMessage);
      await onSaved();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "ARC Coach plan failed.");
    } finally {
      setArcCoachRunning(false);
    }
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    const trimmedTitle =
      mode === "plan"
        ? selectedRangePlan?.title ?? arcPlanDraft?.title ?? ""
        : title.trim();
    const trimmedBody =
      mode === "plan"
        ? selectedRangePlanDetail.trim() || arcPlanDraft?.body.trim() || ""
        : body.trim();
    if (!hasSubmitPayload) return;

    setSaving(true);
    setMessage(null);

    if (DEV_AUTH_BYPASS) {
      window.setTimeout(() => {
        setSaving(false);
        setTitle("");
        setBody("");
        setSelectedRangePlanId("");
        setArcPlanDraft(null);
        setVoiceNote(null);
        setMessage(
          mode === "cue"
            ? "Quick cue sent in dev."
            : mode === "plan"
              ? "Range plan sent in dev."
              : voiceNote
                ? "Voice note sent in dev."
                : "Private note saved in dev.",
        );
        void onSaved();
      }, 180);
      return;
    }

    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();
    if (userError || !user) {
      setSaving(false);
      setMessage("Sign in again before sending this.");
      return;
    }

    const query =
      mode === "note"
        ? supabase.from("coach_annotations").insert({
            collaboration_id: collaborationId,
            author_user_id: user.id,
            session_id: sessionId,
            swing_id: null,
            title: trimmedTitle,
            body: trimmedBody,
            tag: voiceNote ? "voice" : "note",
            scope: "session",
            audio_path: voiceNote?.audioPath ?? null,
            audio_duration_seconds: voiceNote?.durationSeconds ?? null,
          })
        : supabase.from("coach_assignments").insert({
            collaboration_id: collaborationId,
            author_user_id: user.id,
            session_id: sessionId,
            title: trimmedTitle || (mode === "cue" ? "Next-session cue" : "Range plan"),
            detail: trimmedBody,
            due_date: null,
            status: "sent",
            is_cue: mode === "cue",
            source_drill_template_id:
              mode === "plan" ? selectedRangePlanSourceTemplateId : null,
            range_plan_prescription:
              mode === "plan"
                ? selectedRangePlanPrescription ?? arcPlanDraft?.rangePlanPrescription ?? null
                : null,
          });

    const { error } = await query;
    setSaving(false);

    if (error) {
      setMessage(error.message);
      return;
    }

    setTitle("");
    setBody("");
    setSelectedRangePlanId("");
    setArcPlanDraft(null);
    setVoiceNote(null);
    setMessage(
      mode === "cue"
        ? "Quick cue sent."
        : mode === "plan"
          ? "Range plan sent."
          : voiceNote
            ? "Voice note sent."
            : "Private note saved.",
    );
    await onSaved();
  }

  return (
    <section className="arc-card">
      <p className="tag">// Coach Action</p>
      <div className="arc-action-helper">
        <button type="button" onClick={() => void runArcCoach()} disabled={arcCoachRunning || saving}>
          {arcCoachRunning ? "Running ARC Coach..." : "Run ARC Coach"}
        </button>
        <span>Writes the weekly note, cue, and range plan into this coach workspace.</span>
      </div>
      <div className="arc-action-tabs" role="tablist" aria-label="Coach action type">
        <button type="button" aria-pressed={mode === "note"} onClick={() => switchMode("note")}>
          Private Note
        </button>
        <button type="button" aria-pressed={mode === "plan"} onClick={() => switchMode("plan")}>
          Range Plan
        </button>
        <button type="button" aria-pressed={mode === "cue"} onClick={() => switchMode("cue")}>
          Quick Cue
        </button>
      </div>
      <form onSubmit={submit} className="arc-action-form">
        {mode === "plan" ? (
          <>
            <label>
              <span>Plan Mode</span>
              <select
                value={rangePlanKind}
                onChange={(event) => handleRangePlanKind(event.target.value as RangePlanKind)}
              >
                {Object.entries(RANGE_PLAN_KIND_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Start from Range Plan</span>
              <select
                value={selectedRangePlanId}
                onChange={(event) => handleSelectRangePlan(event.target.value)}
              >
                <option value="">Choose a structured plan.</option>
                {filteredRangePlans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.title} · {plan.totalBalls} balls
                  </option>
                ))}
              </select>
            </label>
            <div className="arc-structured-plan-grid">
              <label>
                <span>Practice Window</span>
                <select
                  value={rangePlanSettings.practiceWindowId}
                  onChange={(event) =>
                    setRangePlanSettings((current) => ({
                      ...current,
                      practiceWindowId: event.target.value as RangePlanPracticeWindowId,
                    }))
                  }
                >
                  {RANGE_PLAN_PRACTICE_WINDOW_OPTIONS.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Verbal Guardrail</span>
                <select
                  value={rangePlanSettings.guardrailId}
                  onChange={(event) =>
                    setRangePlanSettings((current) => ({
                      ...current,
                      guardrailId: event.target.value as RangePlanGuardrailId,
                    }))
                  }
                >
                  {RANGE_PLAN_GUARDRAIL_OPTIONS.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Ball Pacing</span>
                <select
                  value={rangePlanSettings.paceId}
                  onChange={(event) =>
                    setRangePlanSettings((current) => ({
                      ...current,
                      paceId: event.target.value as RangePlanPaceId,
                    }))
                  }
                >
                  {RANGE_PLAN_PACE_OPTIONS.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Media Capture</span>
                <select
                  value={rangePlanSettings.mediaCaptureId}
                  onChange={(event) =>
                    setRangePlanSettings((current) => ({
                      ...current,
                      mediaCaptureId: event.target.value as RangePlanMediaCaptureId,
                    }))
                  }
                >
                  {RANGE_PLAN_MEDIA_CAPTURE_OPTIONS.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Student Check-in</span>
                <select
                  value={rangePlanSettings.checkInId}
                  onChange={(event) =>
                    setRangePlanSettings((current) => ({
                      ...current,
                      checkInId: event.target.value as RangePlanCheckInId,
                    }))
                  }
                >
                  {RANGE_PLAN_CHECK_IN_OPTIONS.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {selectedRangePlan ? (
              <RangePlanPreview plan={selectedRangePlan} settings={rangePlanSettings} />
            ) : arcPlanDraft?.rangePlanPrescription ? (
              <RangePlanPreview
                plan={arcPlanDraft.rangePlanPrescription}
                settings={rangePlanSettings}
              />
            ) : (
              <p className="arc-meta">Select a plan before sending.</p>
            )}
            {selectedRangePlan ? (
              <input type="hidden" name="rangePlanDetail" value={selectedRangePlanDetail} />
            ) : null}
          </>
        ) : null}
        {mode !== "plan" ? (
          <>
            <label>
              <span>Title</span>
              <input
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder={
                  mode === "note"
                    ? "Private note title"
                    : "Cue for the next swing"
                }
              />
            </label>
            <label>
              <span>{mode === "note" ? "Private note" : "Cue"}</span>
              <textarea
                value={body}
                onChange={(event) => setBody(event.target.value)}
                placeholder={
                  mode === "note"
                    ? "What should you remember before the next lesson?"
                    : "What should the player think about on the next swing?"
                }
                rows={4}
              />
            </label>
          </>
        ) : null}
        {mode === "note" && voiceUserId ? (
          <label>
            <span>Voice note (optional)</span>
            <VoiceRecorder
              userId={voiceUserId}
              recorded={voiceNote}
              onRecorded={setVoiceNote}
              onDiscard={() => setVoiceNote(null)}
            />
          </label>
        ) : null}
        <button type="submit" disabled={saving || arcCoachRunning || !hasSubmitPayload}>
          {saving
            ? "Sending..."
            : mode === "cue"
              ? "Send Quick Cue"
              : mode === "plan"
                ? "Send Range Plan"
                : voiceNote
                  ? "Send Voice Note"
                  : "Save Private Note"}
        </button>
      </form>
      {message ? <p className="arc-muted arc-action-message">{message}</p> : null}
    </section>
  );
}

function MiniMetric({
  label,
  value,
  tone = "cool",
}: {
  label: string;
  value: string;
  tone?: "cool" | "positive" | "caution" | "negative" | "muted";
}) {
  return (
    <div className={`arc-mini-metric arc-tone-${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function MeasurementCard({ measurement }: { measurement: Measurement }) {
  const provenanceLabel = provenanceDisplayLabel(measurement.provenance);
  return (
    <div className="arc-measure-card">
      {provenanceLabel ? <span>{provenanceLabel}</span> : null}
      <strong>{measurement.formatted || "No value"}</strong>
      <p>{measurement.label}</p>
      {measurement.delta ? <small>{measurement.delta}</small> : null}
    </div>
  );
}

function CollaborationCard({ row }: { row: Collaboration }) {
  const brief = row.briefV2;
  const history = recentPracticeHistory(row);
  const summaryText = sanitizeSummary(row.summary);
  const oneLineRead =
    brief?.executiveSummary?.oneLineRead || summaryText || null;
  const topFacts = brief?.executiveSummary.topFacts.slice(0, 3) ?? [];
  const confidence = brief?.meta.dataConfidence ?? null;
  const sessionLabel = brief?.meta.sessionLabel || (history.length > 0 ? clubSummary(history) : null);
  const when = splitDateForBriefWhen(row.updatedAt);
  const titleText = brief?.executiveSummary?.title || row.title;
  const hasBrief = !!brief;

  return (
    <a href={`/students/${row.id}`} className="brief-card">
      <div className="brief-when">
        {when ? (
          <>
            <span className="day">{when.day}</span>
            <span className="month">{when.month}</span>
            <span className="time">{when.time}</span>
          </>
        ) : (
          <span className="tag">// New</span>
        )}
      </div>

      <div className="brief-body">
        <div className="brief-meta">
          <span className="who">{row.title}</span>
          {sessionLabel ? (
            <>
              <span className="dot" />
              <span>{sessionLabel}</span>
            </>
          ) : null}
          {row.ownerEmail ? (
            <>
              <span className="dot" />
              <span>{row.ownerEmail}</span>
            </>
          ) : null}
        </div>
        <div className="brief-title">{titleText}</div>
        {oneLineRead ? <div className="brief-read">{oneLineRead}</div> : null}
        {topFacts.length > 0 ? (
          <div className="brief-facts">
            {topFacts.map((fact, index) => {
              const isNegDelta = typeof fact.delta === "string" && fact.delta.trim().startsWith("-");
              return (
                <div className="f" key={`${fact.label}-${index}`}>
                  <span className="lbl">// {fact.label}</span>
                  <span className="val">
                    {fact.value || fact.delta || fact.source}
                    {fact.delta && fact.value ? (
                      <span className={`delta${isNegDelta ? " neg" : ""}`}>{fact.delta}</span>
                    ) : null}
                  </span>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>

      <div className="brief-status">
        {hasBrief && confidence ? (
          <span className={`confidence ${confidence}`}>Confidence: {confidence}</span>
        ) : (
          <span className="tag">// Awaiting capture</span>
        )}
        <span className="arrow">→</span>
      </div>
    </a>
  );
}

/** A summary that begins with "Jan 1, 1970" or "1970-01-01" is the
 * backend's default for missing timestamps; suppress so the brief-read
 * doesn't lead with the epoch. */
function sanitizeSummary(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^Jan\s*1,?\s*1970/i.test(trimmed)) return null;
  if (/^1970[-/]/.test(trimmed)) return null;
  return trimmed;
}

function splitDateForBriefWhen(value: string | Date): { day: string; month: string; time: string } | null {
  if (!value) return null;
  const date = new Date(value);
  const t = date.getTime();
  if (Number.isNaN(t)) return null;
  // Treat epoch-zero / pre-2000 timestamps as "no real date" — these are
  // backend defaults for missing data, and rendering "Jan 1 1970" in the
  // when column reads as a bug.
  if (t < new Date("2000-01-01T00:00:00Z").getTime()) return null;
  return {
    day: String(date.getDate()),
    month: date.toLocaleDateString(undefined, { month: "short" }),
    time: date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }),
  };
}

function SessionOutcomeStrip({
  brief,
  compact = false,
}: {
  brief: CoachBriefV2;
  compact?: boolean;
}) {
  return (
    <div className={`arc-session-outcome ${compact ? "arc-session-outcome-compact" : ""}`}>
      <div>
        <span>Worked on</span>
        <strong>{brief.meta.sessionLabel}</strong>
      </div>
      <div>
        <span>How it went</span>
        <strong>{brief.executiveSummary.title}</strong>
      </div>
    </div>
  );
}

function PracticeHistoryPanel({ collaboration }: { collaboration: Collaboration }) {
  const sessions = recentPracticeHistory(collaboration);
  if (sessions.length === 0) return null;

  return (
    <section className="arc-card">
      <p className="tag">// Recent Range Work</p>
      <h2>{clubSummary(sessions)}</h2>
      <PracticeHistoryStrip sessions={sessions} />
    </section>
  );
}

function StudentBagPanel({ collaboration }: { collaboration: Collaboration }) {
  const bag = studentBagItems(collaboration);
  if (bag.length === 0) return null;
  const firstName = collaboration.title.split(/\s+/)[0] || "Student";

  return (
    <section className="arc-card arc-bag-panel">
      <div className="arc-section-heading">
        <div>
          <p className="tag">// {firstName}'s Bag</p>
          <h2>
            {bag[0]
              ? bag.length > 1
                ? `${bag[0].displayName} + ${bag.length - 1} clubs`
                : bag[0].displayName
              : "Current clubs"}
          </h2>
        </div>
        <p className="arc-meta">{bag.length} clubs tracked</p>
      </div>
      <div className="arc-bag-grid arc-section-body">
        {bag.map((item) => (
          <article key={item.club} className={`arc-bag-item arc-bag-${item.status}`}>
            <span>{item.statusLabel}</span>
            <strong>{item.displayName}</strong>
            <p>{item.note}</p>
            <small>{item.fitNote}</small>
          </article>
        ))}
      </div>
    </section>
  );
}

function ReviewFramesPanel({
  collaboration,
  brief,
  sessionMediaEnabled,
  surfaceTitle,
}: {
  collaboration: Collaboration;
  brief: CoachBriefV2 | null;
  sessionMediaEnabled: boolean;
  surfaceTitle: string;
}) {
  const samples = brief?.representativeSamples.slice(0, 4) ?? [];
  return (
    <section className="arc-card arc-frame-panel">
      <div className="arc-section-heading">
        <div>
          <p className="tag">// Review Footage / Frames</p>
          <h2>
            {sessionMediaEnabled
              ? (samples.length > 0 ? "Frames worth checking." : "No frames attached yet.")
              : "Choose a focus session to unlock media review."}
          </h2>
        </div>
        <div className="arc-section-actions">
          {brief?.meta.club ? <p className="arc-meta">{brief.meta.club}</p> : null}
          {sessionMediaEnabled ? (
            <a
              className="arc-frame-link"
              href={buildStudentMediaPath(collaboration.id, {
                sessionId: brief?.meta.sessionId || collaboration.sessionId,
              })}
            >
              View all swings
            </a>
          ) : null}
        </div>
      </div>
      {!sessionMediaEnabled ? (
        <p className="arc-muted arc-section-body">
          {surfaceTitle} is connected at the player level. Choose a focus session on the
          player&apos;s phone when you want coach-side clip review.
        </p>
      ) : samples.length === 0 ? (
        <p className="arc-muted arc-section-body">
          Upload or attach swings to review frame-level evidence here.
        </p>
      ) : (
        <div className="arc-frame-grid arc-section-body">
          {samples.map((sample) => (
            <article key={sample.id} className="arc-frame-card">
              <a
                className="arc-frame-title"
                href={frameHref(collaboration.id, sample, brief?.meta.sessionId || collaboration.sessionId)}
              >
                {sample.label}
              </a>
              <div className="arc-frame-copy">
                <p className="arc-meta">{sampleRoleLabel(sample.role)}</p>
                <strong>{sample.whyChosen}</strong>
                {sample.keyMetrics.length > 0 ? (
                  <div className="arc-frame-metrics">
                    {sample.keyMetrics.slice(0, 2).map((metricRow) => (
                      <span key={`${sample.id}-${metricRow.label}`}>
                        {metricRow.label}: {metricRow.formatted}
                      </span>
                    ))}
                  </div>
                ) : null}
                {sample.coachNote ? <small>{sample.coachNote}</small> : null}
                {sample.clip.signedURL ? (
                  <a className="arc-frame-link" href={sample.clip.signedURL}>
                    Open footage
                  </a>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function PracticeHistoryStrip({ sessions }: { sessions: CoachPracticeSession[] }) {
  return (
    <div className="arc-practice-history" aria-label="Recent practice sessions">
      {sessions.slice(0, 3).map((session, index) => (
        <div key={session.id} className={index === 0 ? "arc-practice-latest" : undefined}>
          <span>{index === 0 ? "Latest" : formatShortDate(session.practicedAt)}</span>
          <strong>{session.club}</strong>
          <p>{session.label}</p>
          <small>{session.outcome}</small>
        </div>
      ))}
    </div>
  );
}

interface PublicCoachProfile {
  slug: string;
  displayName: string;
  headline: string;
  summary: string;
  stats: PublicCoachStat[];
  bullets: PublicCoachBullet[];
  signatureFixes: string[];
  proofLine: string;
}

interface PublicCoachStat {
  label: string;
  value: string;
  detail: string;
}

interface PublicCoachBullet {
  eyebrow: string;
  title: string;
  value: string;
  detail: string;
  meta: string;
  tone: "cool" | "positive" | "caution";
}

function PublicCoachProfilePage({ slug }: { slug: string }) {
  const profile = buildPublicCoachProfile(slug);
  const dashboardPath = `/today?coach=${encodeURIComponent(profile.slug)}&login=1`;

  return (
    <div className="arc-public-coach">
      <section className="public-hero">
        <div className="left">
          <div className="who-line">
            <span>// {profile.slug}</span>
            <span style={{ opacity: 0.4 }}>·</span>
            <span>Coach board · {profile.displayName}</span>
          </div>
          <h1
            dangerouslySetInnerHTML={{
              __html: "I review golf swings. <em>I don't sell mystery.</em>",
            }}
          />
          <p className="public-lede">
            Iron play and short game, mostly async. You send sessions from ARC. I send back two
            voice notes, one cue, and a guardrail.
          </p>
          {profile.signatureFixes.length > 0 ? (
            <div className="credentials">
              {profile.signatureFixes.slice(0, 4).map((fix) => (
                <div className="cred" key={fix}>
                  <div className="lbl">// Signature</div>
                  <div className="v">{fix}</div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
        <div className="right">
          <span className="ttl">// This week &nbsp;·&nbsp; <b>{profile.headline}</b></span>
          {profile.stats.length > 0 ? (
            <div className="public-numbers">
              {profile.stats.slice(0, 4).map((stat) => (
                <div className="pn" key={stat.label}>
                  <span className="v">{stat.value}</span>
                  <span className="lbl">{stat.label}</span>
                </div>
              ))}
            </div>
          ) : null}
          <p className="arc-muted" style={{ fontSize: 13, lineHeight: 1.5 }}>{profile.proofLine}</p>
          <a className="btn primary" href={dashboardPath} style={{ display: "inline-block", textAlign: "center" }}>
            Open coach dashboard
          </a>
        </div>
      </section>

      <section className="public-section">
        <h2 className="ttl">What this coach shipped this week.</h2>
        <div className="proof-grid">
          {profile.bullets.map((bullet) => (
            <div className="proof" key={bullet.eyebrow}>
              <span className="week">// {bullet.eyebrow}</span>
              <div className="head">{bullet.title}</div>
              <p className="body">{bullet.detail}</p>
              <span className="source">{bullet.value} · {bullet.meta}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="public-section">
        <h2 className="ttl">How I work.</h2>
        <div className="work-grid">
          <div className="work-step">
            <span className="n">01</span>
            <span className="ttl-sm">You send sessions from ARC.</span>
            <p className="body">
              Range, chips, putts. Whatever you actually practiced. The packet arrives in my
              Today feed — body geometry, dispersion, and the one-line read.
            </p>
          </div>
          <div className="work-step">
            <span className="n">02</span>
            <span className="ttl-sm">I review within 24 hours.</span>
            <p className="body">
              Two voice notes on the rep that matters most, one written cue for the next
              session, and a guardrail. Nothing dump-truck about it.
            </p>
          </div>
          <div className="work-step">
            <span className="n">03</span>
            <span className="ttl-sm">We measure the cue, not the feeling.</span>
            <p className="body">
              Next session shows whether the cue worked. If it didn't, we don't re-explain —
              we change the cue. ARC keeps the receipts.
            </p>
          </div>
        </div>
      </section>

      <section className="cta-band">
        <div className="l">
          <h2
            dangerouslySetInnerHTML={{
              __html: "I take 12 players. <em>Two slots open in April.</em>",
            }}
          />
          <p>
            One sample brief, one 20-minute call, then we decide. No long-term contract —
            month-to-month, cancel from inside ARC.
          </p>
        </div>
        <div className="r">
          <a className="btn" href="#faq">Read the FAQ</a>
          <a className="btn primary" href={dashboardPath}>Apply for a slot</a>
        </div>
      </section>
    </div>
  );
}

function SignInGate({
  next = "/today",
  coachSlug,
  inviteCode,
}: {
  next?: string;
  coachSlug?: string | null;
  inviteCode?: string | null;
}) {
  if (DEV_AUTH_BYPASS) {
    return <DevAuthBypassGate next={next} coachSlug={coachSlug} />;
  }

  return (
    <SignInGateForm
      next={next}
      coachSlug={coachSlug}
      inviteCode={inviteCode}
    />
  );
}

function DevAuthBypassGate({
  next,
  coachSlug,
}: {
  next: string;
  coachSlug?: string | null;
}) {
  return (
    <Card eyebrow="Dev Access" title="Auth is bypassed on localhost.">
      <p className="arc-muted">
        Vite dev uses demo coach data. Set VITE_REQUIRE_AUTH=1 to test the sign-in flow.
      </p>
      <a className="btn" href={next}>
        Open dashboard
      </a>
      {coachSlug ? <p className="arc-meta">Coach ID: {coachSlug}</p> : null}
    </Card>
  );
}

function SignInGateForm({
  next,
  coachSlug,
  inviteCode,
}: {
  next: string;
  coachSlug?: string | null;
  inviteCode?: string | null;
}) {
  const [email, setEmail] = useState("");
  const [pendingInviteCode, setPendingInviteCode] = useState(inviteCode ?? "");
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [oauthPending, setOauthPending] = useState<"google" | "apple" | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const isBusy = status === "sending" || oauthPending !== null;

  useEffect(() => {
    if (inviteCode) setPendingInviteCode(inviteCode);
  }, [inviteCode]);

  async function sendLink(event: React.FormEvent) {
    event.preventDefault();
    if (!email.trim()) return;
    setStatus("sending");
    setOauthPending(null);
    setErrorMessage(null);

    const nextWithInvite = nextPathWithInvite(next, pendingInviteCode);
    const redirectTo = authRedirectUrl({
      next: nextWithInvite,
      currentOrigin: window.location.origin,
      siteUrl: COACH_SITE_URL,
    });
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: {
        emailRedirectTo: redirectTo,
        shouldCreateUser: true,
      },
    });

    if (error) {
      setStatus("error");
      setErrorMessage(error.message);
      return;
    }
    setStatus("sent");
  }

  async function signInWithProvider(provider: "google" | "apple") {
    setOauthPending(provider);
    setStatus("idle");
    setErrorMessage(null);

    const nextWithInvite = nextPathWithInvite(next, pendingInviteCode);
    rememberPostAuthNextPath(nextWithInvite);
    const redirectTo = authProviderRedirectUrl({
      currentOrigin: window.location.origin,
      siteUrl: COACH_SITE_URL,
    });
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo },
    });

    if (error) {
      setOauthPending(null);
      setStatus("error");
      setErrorMessage(error.message);
    }
  }

  return (
    <div className="signin-page">
      <section className="signin-hero">
        <div className="who-line">
          <ArcBrandLockup
            size="compact"
            showPromise={false}
            aria-label="arc coach. Hit. See. Fix. Next."
          />
          <span style={{ opacity: 0.4 }}>·</span>
          <span>{pendingInviteCode.trim() ? "Invite" : "Sign in"}</span>
          {coachSlug ? (
            <>
              <span style={{ opacity: 0.4 }}>·</span>
              <span>{coachSlug}</span>
            </>
          ) : null}
        </div>
        <h1 className="display">
          {pendingInviteCode.trim() ? (
            <>Join this <em>coach workspace</em>.</>
          ) : (
            <>Coach the <em>next range session</em>.</>
          )}
        </h1>
        <p className="signin-lede">
          {pendingInviteCode.trim()
            ? "One email link creates your ARC Coach account. The workspace opens the moment you land back here."
            : "Review the brief, leave the cue, keep the lesson moving — from a range laptop or iPad."}
        </p>
      </section>

      <section className="signin-card">
        <div className="signin-card-head">
          <span className="tag">// Coach access</span>
          <h2>{pendingInviteCode.trim() ? "Accept the invite." : "Open your dashboard."}</h2>
          <p className="signin-card-meta">
            Use the same sign-in you use in ARC. Once in, Billing lets you link Apple or
            Google to the same coach account.
          </p>
        </div>

        <div className="signin-oauth">
          <button
            type="button"
            className="btn"
            onClick={() => void signInWithProvider("google")}
            disabled={isBusy}
          >
            {oauthPending === "google" ? "Opening Google…" : "Continue with Google"}
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => void signInWithProvider("apple")}
            disabled={isBusy}
          >
            {oauthPending === "apple" ? "Opening Apple…" : "Continue with Apple"}
          </button>
        </div>

        <div className="signin-divider" aria-hidden="true">
          <span className="tag">// or use email</span>
        </div>

        <form onSubmit={sendLink} className="signin-form">
          <div className="signin-field">
            <label className="tag" htmlFor="coach-email">// Email</label>
            <div className="signin-row">
              <input
                id="coach-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
              <button type="submit" className="btn primary" disabled={isBusy}>
                {status === "sending" ? "Sending…" : "Send link"}
              </button>
            </div>
          </div>

          <div className="signin-field">
            <label className="tag" htmlFor="coach-invite">// Invite code · optional</label>
            <input
              id="coach-invite"
              type="text"
              inputMode="text"
              autoCapitalize="characters"
              autoCorrect="off"
              placeholder="ARC-…"
              value={pendingInviteCode}
              onChange={(event) => setPendingInviteCode(event.target.value)}
            />
          </div>
        </form>

        {status === "sent" ? (
          <p className="signin-status positive">
            Check your inbox. The link brings you back here
            {normalizeInviteCode(pendingInviteCode) ? " and joins the workspace." : "."}
          </p>
        ) : null}
        {status === "error" && errorMessage ? (
          <p className="signin-status negative">{errorMessage}</p>
        ) : null}
      </section>
    </div>
  );
}

function CoachInviteJoinForm({
  label,
  buttonLabel,
  initialCode = "",
  onJoin,
}: {
  label: string;
  buttonLabel: string;
  initialCode?: string;
  onJoin: (code: string) => void;
}) {
  const [code, setCode] = useState(initialCode);
  const normalizedCode = normalizeInviteCode(code);

  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!normalizedCode) return;
    onJoin(normalizedCode);
  }

  return (
    <form className="arc-invite-form" onSubmit={submit}>
      <label className="arc-invite-label" htmlFor="coach-invite-code">
        {label}
      </label>
      <div className="arc-invite-controls">
        <input
          id="coach-invite-code"
          type="text"
          inputMode="text"
          autoCapitalize="characters"
          autoCorrect="off"
          placeholder="Invite code"
          value={code}
          onChange={(event) => setCode(event.target.value)}
        />
        <button type="submit" disabled={!normalizedCode}>
          {buttonLabel}
        </button>
      </div>
    </form>
  );
}

function readStoredTheme(): CoachTheme {
  if (typeof window === "undefined") return "dark";
  try {
    return readCoachThemePreference(window.localStorage);
  } catch {
    return "dark";
  }
}

function useCoachTheme(): [CoachTheme, (next: CoachTheme) => void] {
  const [theme, setThemeState] = useState<CoachTheme>(() => readStoredTheme());
  const setTheme = (next: CoachTheme) => {
    try {
      if (typeof window !== "undefined") {
        persistCoachThemePreference(window.localStorage, next);
      }
    } catch {
      // localStorage access can be blocked in private/embedded contexts.
    }
    setThemeState(next);
  };

  useEffect(() => {
    document.documentElement.dataset.coachTheme = theme;
  }, [theme]);
  return [theme, setTheme];
}

function ThemeApply() {
  // Applies the stored theme to <html> across full-bleed routes that do
  // not render the sidebar.
  const [theme] = useCoachTheme();
  void theme;
  return null;
}

function CoachSidebar({
  route,
  onNavigate,
}: {
  route: Route;
  onNavigate: (path: string, replace?: boolean) => void;
}) {
  const unread = useInboxUnreadCount();
  const [theme, setTheme] = useCoachTheme();
  const { user } = useAuthUser();
  const coachSlug = coachSlugFromSearch(window.location.search);

  const isActive = (href: string) =>
    route.kind === "today" ? href === "/today"
      : route.kind === "students" || route.kind === "student" || route.kind === "studentMedia" ? href === "/students"
      : route.kind === "inbox" ? href === "/inbox"
      : route.kind === "library" ? href === "/library"
      : route.kind === "billing" ? href === "/billing"
      : false;

  type Item = { href: string; label: string; count?: string; external?: boolean };
  const inbox: Item[] = [
    { href: "/today", label: "Today" },
    { href: "/students", label: "Students" },
    { href: "/inbox", label: "Threads", count: unread > 0 ? (unread > 99 ? "99+" : String(unread)) : "—" },
  ];
  const tools: Item[] = [{ href: "/library", label: "Drill library", count: "—" }];
  const publicNav: Item[] = coachSlug
    ? [{ href: `/coach/${encodeURIComponent(coachSlug)}`, label: "My coach board", count: "↗", external: true }]
    : [];
  const account: Item[] = [{ href: "/billing", label: "Billing" }];

  const renderItem = (it: Item) => (
    <a
      key={it.href}
      href={it.href}
      className={`nav-item${isActive(it.href) ? " active" : ""}`}
      target={it.external ? "_blank" : undefined}
      rel={it.external ? "noreferrer" : undefined}
      onClick={(event) => {
        if (it.external) return; // let the browser open it normally
        intercept(event, onNavigate);
      }}
    >
      <span>{it.label}</span>
      {it.count ? <span className="count">{it.count}</span> : null}
    </a>
  );

  const initials = coachInitialsFromIdentity(user?.email, coachSlug);
  const displayName = user?.email || (coachSlug ? `${coachSlug}` : "ARC Coach");
  const subline = coachSlug ? `coach.arcgolf.ai/${coachSlug}` : "coach.arcgolf.ai";

  return (
    <aside className="sidebar">
      <a
        href="/today"
        className="brand"
        onClick={(event) => intercept(event, onNavigate)}
        style={{ textDecoration: "none" }}
      >
        <ArcBrandLockup
          size="compact"
          showPromise={false}
          aria-label="arc coach. Hit. See. Fix. Next."
        />
      </a>

      <div className="nav-section">
        <div className="nav-section-label">Inbox</div>
        {inbox.map(renderItem)}
      </div>

      <div className="nav-section">
        <div className="nav-section-label">Tools</div>
        {tools.map(renderItem)}
      </div>

      {publicNav.length > 0 ? (
        <div className="nav-section">
          <div className="nav-section-label">Public</div>
          {publicNav.map(renderItem)}
        </div>
      ) : null}

      <div className="nav-section">
        <div className="nav-section-label">Account</div>
        {account.map(renderItem)}
      </div>

      <div className="sidebar-footer">
        <div className="coach-card">
          <div className="av">{initials}</div>
          <div className="who">
            <b>{displayName}</b>
            <span>{subline}</span>
          </div>
        </div>
        <div className="theme-toggle" role="group" aria-label="Theme">
          <button
            type="button"
            className={`theme-btn${theme === "light" ? " active" : ""}`}
            onClick={() => setTheme("light")}
          >
            // light
          </button>
          <span className="theme-btn-sep">/</span>
          <button
            type="button"
            className={`theme-btn${theme === "dark" ? " active" : ""}`}
            onClick={() => setTheme("dark")}
          >
            // dark
          </button>
        </div>
      </div>
    </aside>
  );
}

function coachInitialsFromIdentity(email: string | null | undefined, slug: string | null): string {
  if (slug) {
    const parts = slug.split(/[-_\s]+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  }
  if (email) {
    const local = email.split("@")[0] ?? "";
    const parts = local.split(/[.\-_]+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    if (local.length >= 2) return local.slice(0, 2).toUpperCase();
  }
  return "AC";
}

function CoachToolbar({
  route,
  onNavigate,
}: {
  route: Route;
  onNavigate: (path: string, replace?: boolean) => void;
}) {
  const today = new Date().toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  let crumbs: React.ReactNode = null;
  switch (route.kind) {
    case "today":
      crumbs = (<><span>Inbox</span><span className="sep">/</span><b>Today</b><span className="sep">·</span><span>{today}</span></>);
      break;
    case "students":
      crumbs = (<><span>Inbox</span><span className="sep">/</span><b>Students</b></>);
      break;
    case "student":
      crumbs = (<><span>Inbox</span><span className="sep">/</span>
        <a href="/students" onClick={(e) => intercept(e, onNavigate)} style={{ color: "inherit" }}>Students</a>
        <span className="sep">/</span><b>{route.id.slice(0, 8)}</b></>);
      break;
    case "studentMedia":
      crumbs = (<><span>Inbox</span><span className="sep">/</span>
        <a href={`/students/${route.id}`} onClick={(e) => intercept(e, onNavigate)} style={{ color: "inherit" }}>Student</a>
        <span className="sep">/</span><b>Media</b></>);
      break;
    case "inbox":
      crumbs = (<><span>Inbox</span><span className="sep">/</span><b>Threads</b></>);
      break;
    case "library":
      crumbs = (<><span>Tools</span><span className="sep">/</span><b>Drill library</b></>);
      break;
    case "billing":
      crumbs = (<><span>Account</span><span className="sep">/</span><b>Billing</b></>);
      break;
    default:
      return null;
  }

  let actions: React.ReactNode = null;
  switch (route.kind) {
    case "today":
      actions = (
        <>
          <button type="button" className="btn ghost">Filter</button>
          <button type="button" className="btn">Mark all read</button>
        </>
      );
      break;
    case "students":
      actions = <button type="button" className="btn primary">Invite player</button>;
      break;
    case "student":
      actions = (
        <>
          <button type="button" className="btn ghost">Skip</button>
          <button type="button" className="btn">Voice note</button>
          <button type="button" className="btn primary">Send cue</button>
        </>
      );
      break;
    case "inbox":
      actions = <button type="button" className="btn ghost">Mark all read</button>;
      break;
    case "library":
      actions = null;
      break;
    case "billing":
      actions = null;
      break;
  }

  return (
    <div className="toolbar">
      <div className="crumbs">{crumbs}</div>
      {actions ? <div className="toolbar-actions">{actions}</div> : null}
    </div>
  );
}

/**
 * Unread inbox count for the tab-bar badge. Four refresh triggers:
 *
 *   1. Mount — one fetch when the tab bar renders.
 *   2. `inbox-refresh` event on `coachEventBus` — emitted after
 *      mark-read + on realtime INSERTs so child surfaces can force
 *      a re-count without owning a Supabase subscription of their
 *      own. Typed + scoped; see `lib/events.ts`.
 *   3. Realtime INSERT on `coach_assignment_replies` — the badge
 *      increments the instant a student reply lands.
 *   4. Polling fallback — kicks in when the realtime channel fails
 *      to reach SUBSCRIBED within a short window (e.g. the
 *      `supabase_realtime` publication is missing, realtime is
 *      disabled on the project, or the connection dropped). Keeps
 *      the badge honest even when the live signal is broken.
 */
function useInboxUnreadCount(): number {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (DEV_AUTH_BYPASS) {
      setCount(demoInboxThreads().reduce((sum, thread) => sum + thread.unreadCount, 0));
      return;
    }

    let cancelled = false;
    let pollTimer: number | null = null;
    let subscribeTimeoutTimer: number | null = null;

    // Resolve once at mount — the config is memoized per page load
    // so the interval stays stable for the life of this subscription.
    const config = getCoachRuntimeConfig();

    async function load() {
      try {
        const next = await countUnreadReplies(supabase);
        if (!cancelled) setCount(next);
      } catch {
        // Swallow errors — a badge that sometimes misses a count is
        // better than an error banner in the nav. The Inbox page
        // surfaces real failures on open.
      }
    }

    function startPolling() {
      if (pollTimer != null) return;
      pollTimer = window.setInterval(() => {
        void load();
      }, config.inboxPollIntervalMs);
    }

    function stopPolling() {
      if (pollTimer != null) {
        window.clearInterval(pollTimer);
        pollTimer = null;
      }
    }

    void load();

    const offRefresh = coachEventBus.on("inbox-refresh", () => void load());

    // If realtime never subscribes, assume the publication isn't
    // deployed + fall back to polling. The timeout handle clears on
    // a successful SUBSCRIBED status below.
    subscribeTimeoutTimer = window.setTimeout(() => {
      startPolling();
    }, config.inboxRealtimeSubscribeTimeoutMs);

    const unsubscribe = subscribeToInboxReplies(
      supabase,
      () => {
        // A fresh reply always increments unread. We fan out
        // through the same `inbox-refresh` bus event that mark-read
        // uses, so every surface (tab bar badge via its own
        // listener, InboxPage thread list) gets refreshed from a
        // single signal.
        notifyInboxRefresh();
      },
      (status: InboxRealtimeStatus) => {
        if (status === "subscribed") {
          // Realtime won — cancel the fallback timer and the poller.
          if (subscribeTimeoutTimer != null) {
            window.clearTimeout(subscribeTimeoutTimer);
            subscribeTimeoutTimer = null;
          }
          stopPolling();
        } else if (status === "error" || status === "timed_out" || status === "closed") {
          // Realtime is unavailable. Start polling immediately so the
          // badge doesn't go stale until the next app reload.
          if (subscribeTimeoutTimer != null) {
            window.clearTimeout(subscribeTimeoutTimer);
            subscribeTimeoutTimer = null;
          }
          startPolling();
        }
      },
    );

    return () => {
      cancelled = true;
      offRefresh();
      if (subscribeTimeoutTimer != null) window.clearTimeout(subscribeTimeoutTimer);
      stopPolling();
      unsubscribe();
    };
  }, []);

  return count;
}

/** Ping every inbox-refresh listener to re-fetch. Called after mark-read + after realtime INSERTs. */
function notifyInboxRefresh() {
  coachEventBus.emit("inbox-refresh");
}

function Card({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string;
  title: string;
  children?: React.ReactNode;
}) {
  return (
    <section className="arc-card">
      <p className="tag">// {eyebrow}</p>
      <h1>{title}</h1>
      {children ? <div className="arc-section-body">{children}</div> : null}
    </section>
  );
}

function ThreadSection({
  title,
  empty,
  children,
}: {
  title: string;
  empty: string;
  children: React.ReactNode[];
}) {
  return (
    <section className="arc-card">
      <p className="tag">// {title}</p>
      {children.length === 0 ? (
        <p className="arc-muted arc-section-body">{empty}</p>
      ) : (
        <div className="arc-stack arc-section-body">{children}</div>
      )}
    </section>
  );
}

function useAuthUser() {
  const [user, setUser] = useState<User | null>(
    DEV_AUTH_BYPASS ? DEV_COACH_USER : null,
  );
  const [loading, setLoading] = useState(!DEV_AUTH_BYPASS);

  useEffect(() => {
    if (DEV_AUTH_BYPASS) return;

    let cancelled = false;

    async function load() {
      const {
        data: { user: currentUser },
      } = await supabase.auth.getUser();
      if (!cancelled) {
        setUser(currentUser);
        setLoading(false);
      }
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    void load();
    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  return { user, loading };
}

// ---------------------------------------------------------------------------
// Entitlement hook + gate
// ---------------------------------------------------------------------------

type EntitlementState = {
  subscription: CoachSubscription | null;
  isEntitled: boolean;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
};

/**
 * Reads the caller's Coach+ entitlement once on mount. Re-fetches when
 * `reload()` is called (e.g. after a successful checkout return). The
 * `isEntitled` boolean is server-computed via `coach_subscription_status`
 * so the grace-period logic stays authoritative.
 */
function useAuthEntitlement(): EntitlementState {
  const [subscription, setSubscription] = useState<CoachSubscription | null>(
    DEV_AUTH_BYPASS ? demoCoachSubscription() : null,
  );
  const [loading, setLoading] = useState(!DEV_AUTH_BYPASS);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    if (DEV_AUTH_BYPASS) {
      setSubscription(demoCoachSubscription());
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const row = await getCoachSubscription(supabase);
      setSubscription(row);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (DEV_AUTH_BYPASS) return;
    void reload();
  }, []);

  return {
    subscription,
    isEntitled: subscription?.isEntitled ?? false,
    loading,
    error,
    reload,
  };
}

/**
 * Slim upsell banner shown above non-entitled surfaces that still let
 * the coach read (Today, Students, Inbox). The CTA deep-links to
 * `/billing` — we never auto-redirect because the Today feed still has
 * value for a coach evaluating the product.
 */
function EntitlementBanner({
  state,
  onNavigate,
}: {
  state: EntitlementState;
  onNavigate: (path: string, replace?: boolean) => void;
}) {
  if (state.loading || state.isEntitled) return null;
  const copy = coachEntitlementUpsellCopy(state.subscription);
  return (
    <section
      className="arc-card"
      style={{ borderColor: "rgba(255, 197, 95, 0.45)" }}
    >
      <p className="tag">// Coach+</p>
      <p className="arc-section-body" style={{ margin: "0 0 12px" }}>
        {copy}
      </p>
      <button
        type="button"
        className="btn"
        onClick={() => onNavigate("/billing")}
      >
        {state.subscription && state.subscription.status !== "pending"
          ? "Review billing"
          : "Subscribe to Coach+"}
      </button>
    </section>
  );
}

/**
 * Full-surface hard gate for locked routes or panels. Use it when the
 * underlying content should not render at all until Coach+ is active.
 * The gate itself is plain text + a button; no sleight of hand with
 * paywalls that look like the real page.
 */
function EntitlementHardGate({
  state,
  surfaceTitle,
  onNavigate,
}: {
  state: EntitlementState;
  surfaceTitle: string;
  onNavigate: (path: string, replace?: boolean) => void;
}) {
  if (state.loading) {
    return <Card eyebrow={surfaceTitle} title="Checking your access." />;
  }
  if (state.error) {
    return (
      <Card eyebrow={surfaceTitle} title="Could not check your Coach+ access.">
        <p className="arc-muted">{state.error}</p>
        <button type="button" className="btn" onClick={() => void state.reload()}>
          Try again
        </button>
      </Card>
    );
  }
  const copy = coachEntitlementUpsellCopy(state.subscription, {
    surface: surfaceTitle === "ARC Coach" ? "arc_coach" : "default",
  });
  return (
    <Card eyebrow={surfaceTitle} title={`${surfaceTitle} is a Coach+ surface.`}>
      <p className="arc-muted">{copy}</p>
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "12px" }}>
        <button type="button" className="btn" onClick={() => onNavigate("/billing")}>
          {state.subscription && state.subscription.status !== "pending"
            ? "Review billing"
            : "Subscribe to Coach+"}
        </button>
        <button type="button" className="btn" onClick={() => onNavigate("/today")}>
          Back to Today
        </button>
      </div>
    </Card>
  );
}

function parseRoute(pathname: string): Route {
  if (pathname === "/" || pathname === "/today") return { kind: "today" };
  if (pathname.startsWith("/coach/")) {
    const slug = decodeURIComponent(pathname.slice("/coach/".length)).replace(/^\/+|\/+$/g, "");
    if (slug) return { kind: "publicCoach", slug };
  }
  if (pathname === "/students") return { kind: "students" };
  if (pathname.startsWith("/students/") && pathname.endsWith("/media")) {
    const id = pathname.slice("/students/".length, -"/media".length);
    return { kind: "studentMedia", id: decodeURIComponent(id.replace(/\/+$/g, "")) };
  }
  if (pathname.startsWith("/students/")) {
    return { kind: "student", id: decodeURIComponent(pathname.slice("/students/".length)) };
  }
  if (pathname === "/inbox") return { kind: "inbox" };
  if (pathname === "/library") return { kind: "library" };
  if (pathname === "/billing") return { kind: "billing" };
  if (pathname.startsWith("/s/")) {
    return { kind: "preview", token: decodeURIComponent(pathname.slice("/s/".length)) };
  }
  if (pathname === "/auth/callback") return { kind: "authCallback" };
  return { kind: "notFound" };
}

function routePath(
  route: Exclude<Route, { kind: "preview" | "publicCoach" | "authCallback" | "notFound" }>,
) {
  switch (route.kind) {
    case "today":
      return "/today";
    case "students":
      return "/students";
    case "student":
      return `/students/${encodeURIComponent(route.id)}`;
    case "studentMedia":
      return `/students/${encodeURIComponent(route.id)}/media`;
    case "inbox":
      return "/inbox";
    case "library":
      return "/library";
    case "billing":
      return "/billing";
  }
}

function navigate(path: string, options: { replace?: boolean } = {}) {
  if (options.replace) {
    window.history.replaceState({}, "", path);
  } else {
    window.history.pushState({}, "", path);
  }
}

function intercept(
  event: React.MouseEvent<HTMLAnchorElement>,
  onNavigate: (path: string, replace?: boolean) => void,
) {
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const href = event.currentTarget.getAttribute("href");
  if (!href || href.startsWith("http")) return;
  event.preventDefault();
  onNavigate(href);
}

function safeNextPath(next: string | null): string {
  if (!next || !next.startsWith("/") || next.startsWith("//")) return "/today";
  return next;
}

function authCallbackPath(next: string): string {
  return `/auth/callback?next=${encodeURIComponent(safeNextPath(next))}`;
}

function sessionFromAuthCallbackHash(hash: string): {
  access_token: string;
  refresh_token: string;
} | null {
  const trimmed = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!trimmed) return null;

  const params = new URLSearchParams(trimmed);
  const accessToken = params.get("access_token")?.trim();
  const refreshToken = params.get("refresh_token")?.trim();
  if (!accessToken || !refreshToken) return null;

  return {
    access_token: accessToken,
    refresh_token: refreshToken,
  };
}

function loginPathForNext(next: string): string {
  const url = new URL(safeNextPath(next), window.location.origin);
  url.searchParams.set("login", "1");
  return `${url.pathname}${url.search}${url.hash}`;
}

function authCallbackErrorTitle(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  const lower = message.toLowerCase();
  if (lower.includes("code verifier") || lower.includes("code_verifier")) {
    return "That sign-in link needs a fresh browser session.";
  }
  return "That sign-in link could not be used.";
}

function authCallbackErrorBody(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  const lower = message.toLowerCase();
  if (lower.includes("code verifier") || lower.includes("code_verifier")) {
    return "This can happen when the email link was opened in a different browser, on another device, or after browser storage was cleared. Request a fresh link from this browser and ARC will bring you back to the same coach page.";
  }
  return "Request a fresh link from this browser and ARC will bring you back to the same coach page.";
}

function providerLabel(provider: string): string {
  switch (provider) {
    case "google":
      return "Google";
    case "apple":
      return "Apple";
    case "email":
      return "Email";
    default:
      return provider.charAt(0).toUpperCase() + provider.slice(1);
  }
}

function normalizeMergeCode(value: string): string {
  return value.trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
}

function identityLinkErrorMessage(provider: "google" | "apple", error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  const lower = message.toLowerCase();
  if (lower.includes("manual linking")) {
    return `${providerLabel(provider)} linking is not enabled for this Supabase project yet. Turn on manual linking in Auth before using this flow.`;
  }
  if (lower.includes("already linked") || lower.includes("identity is already linked")) {
    return `${providerLabel(provider)} is already attached to another ARC Coach account. Sign into the extra account, generate a merge code there, then redeem it here.`;
  }
  return message;
}

function mergeSummaryLabel(value: string): string {
  switch (value) {
    case "moved":
      return "moved to this account";
    case "merged_preferred_source":
      return "moved here and kept the source Coach+ state";
    case "merged_kept_target":
      return "kept this account's Coach+ row and backfilled missing billing fields";
    case "kept_target":
      return "kept this account's current setting";
    case "none":
    default:
      return "unchanged";
  }
}

function coachSlugFromSearch(search: string): string | null {
  const slug = new URLSearchParams(search).get("coach")?.trim();
  return slug || null;
}

function inviteCodeFromSearch(search: string): string | null {
  const params = new URLSearchParams(search);
  return normalizeInviteCode(
    params.get("invite") ?? params.get("code") ?? params.get("share_code") ?? "",
  );
}

function normalizeInviteCode(code: string | null | undefined): string {
  return code?.trim().toUpperCase().replace(/\s+/g, "") ?? "";
}

function invitePath(code: string): string {
  return `/today?invite=${encodeURIComponent(normalizeInviteCode(code))}`;
}

function nextPathWithInvite(next: string, inviteCode: string): string {
  const normalizedCode = normalizeInviteCode(inviteCode);
  if (!normalizedCode) return next;

  const url = new URL(next, window.location.origin);
  url.searchParams.delete("code");
  url.searchParams.delete("share_code");
  url.searchParams.set("invite", normalizedCode);
  return `${url.pathname}${url.search}${url.hash}`;
}

function inviteErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  const lower = message.toLowerCase();
  if (lower.includes("invite invalid") || lower.includes("unavailable")) {
    return "That invite is invalid, already claimed by another account, or no longer available. Ask the player for a fresh invite.";
  }
  if (lower.includes("not authenticated")) {
    return "Your session expired before we could join the workspace. Sign in again and we will retry.";
  }
  return message || "Something went wrong while joining this workspace.";
}

function buildPublicCoachProfile(slug: string): PublicCoachProfile {
  const normalizedSlug = normalizeCoachSlug(slug);
  const displayName = titleFromSlug(normalizedSlug);
  const seed = seedFromSlug(normalizedSlug);
  const reviewed = 46 + (seed % 42);
  const students = 9 + (seed % 18);
  const plans = 7 + (seed % 14);
  const turnaroundHours = 4 + (seed % 7);
  const turnaroundMinutes = (seed % 6) * 10;
  const dispersionBefore = 38 + (seed % 14);
  const dispersionAfter = Math.max(18, dispersionBefore - (9 + (seed % 8)));
  const shotCarry = 166 + (seed % 22);
  const shotOffline = 2 + (seed % 6);
  const cue = chooseBySeed(
    seed,
    [
      "Finish chest-high",
      "Hold posture through strike",
      "Start the ball left edge",
      "Let the trail shoulder turn through",
    ],
  );
  const drill = chooseBySeed(
    seed + 3,
    ["Pump-to-Finish", "Three-Ball Ladder", "Half-Speed Start Line", "Trail-Hand Release"],
  );

  return {
    slug: normalizedSlug,
    displayName,
    headline: `${students} students kept practicing after the lesson.`,
    summary:
      "A public ARC board for the work that happens between lessons: range reps, coach cues, student wins, and the one swing worth talking about.",
    proofLine: "The range remembers the cue, the rep, and the change that followed.",
    stats: [
      {
        label: "Swings Reviewed",
        value: String(reviewed),
        detail: "Student swings opened from range briefs this week.",
      },
      {
        label: "Students Practiced",
        value: String(students),
        detail: "Students completed coach-assigned range work.",
      },
      {
        label: "Review Turnaround",
        value: `${turnaroundHours}h ${turnaroundMinutes}m`,
        detail: "Median time from student upload to coach response.",
      },
      {
        label: "Range Plans",
        value: String(plans),
        detail: "Coach plans sent back into student sessions.",
      },
    ],
    bullets: [
      {
        eyebrow: "Shot of the Week",
        title: "One swing made the lesson click.",
        value: `${shotCarry} yd 7-iron / ${shotOffline} yd offline`,
        detail: `A student found the window after two reps of "${cue}" and finished the set with their tightest strike pattern of the week.`,
        meta: "Anonymized student highlight.",
        tone: "cool",
      },
      {
        eyebrow: "Most Improved Session",
        title: "Dispersion tightened before the bucket ended.",
        value: `${dispersionBefore} yd -> ${dispersionAfter} yd`,
        detail: "ARC compared the first ten and final ten shots from the same range session.",
        meta: "Range-session delta.",
        tone: "positive",
      },
      {
        eyebrow: "Cue of the Week",
        title: cue,
        value: `${Math.round((students / (students + 5)) * 100)}% follow-through rate`,
        detail: "The most repeated coach cue across student practice plans.",
        meta: "Based on assigned-cue completion.",
        tone: "cool",
      },
      {
        eyebrow: "Drill That Worked",
        title: drill,
        value: `${61 + (seed % 18)}% cleaner contact trend`,
        detail: "Students using this drill were more likely to finish with a playable final pattern.",
        meta: "Aggregate drill signal.",
        tone: "positive",
      },
      {
        eyebrow: "Range Wins",
        title: "Small milestones stacked up.",
        value: `${3 + (seed % 5)} student breakthroughs`,
        detail: "First five-shot wedge ladder, first sub-30-yard pattern, and first driver start-line streak.",
        meta: "Private names, public momentum.",
        tone: "caution",
      },
    ],
    signatureFixes: chooseFixes(seed),
  };
}

function normalizeCoachSlug(slug: string): string {
  return slug.trim().toLowerCase().replace(/[^a-z0-9_-]+/g, "-").replace(/^-+|-+$/g, "") || "coach";
}

function titleFromSlug(slug: string): string {
  const nameOverrides: Record<string, string> = {
    johnstamos: "John Stamos",
  };
  if (nameOverrides[slug]) return nameOverrides[slug];

  return slug
    .split(/[-_]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ") || "ARC Coach";
}

function seedFromSlug(slug: string): number {
  return Array.from(slug).reduce((hash, char) => (hash * 31 + char.charCodeAt(0)) % 9973, 17);
}

function chooseBySeed(seed: number, values: string[]): string {
  return values[seed % values.length];
}

function chooseFixes(seed: number): string[] {
  const fixes = [
    "Face control",
    "Driver start line",
    "Wedge distance control",
    "Tempo under pressure",
    "Posture through strike",
    "Playable fade pattern",
  ];
  return [fixes[seed % fixes.length], fixes[(seed + 2) % fixes.length], fixes[(seed + 4) % fixes.length]];
}

type DemoCoachSeed = {
  name: string;
  club: string;
  sessionLabel: string;
  title: string;
  oneLineRead: string;
  confidence: Confidence;
  priority: string;
  category: DriverCategory;
  driverTitle: string;
  correctiveCue: string;
  costlyMiss: string;
  primaryMetric: Measurement;
  secondaryMetric: Measurement;
};

function demoCoachCollaborations(): Collaboration[] {
  const seeds: DemoCoachSeed[] = [
    {
      name: "Norm Zeng",
      club: "7-iron",
      sessionLabel: "Range · 60 reps",
      title: "Stock 7-iron is solid again.",
      oneLineRead:
        "Stock 7-iron is solid again. The right misses cluster after rep 35 — that's not the swing, it's the shoulder line drifting open at setup.",
      confidence: "high",
      priority:
        "Re-anchor the shoulder line at address before the back nine of any session.",
      category: "setup",
      driverTitle: "Shoulder line opens 4° between rep 1 and rep 40.",
      correctiveCue:
        "Re-set shoulders square every 3 balls. Use the alignment stick.",
      costlyMiss: "Right miss after rep 35 when shoulders open at address.",
      primaryMetric: metric("Right miss share", 61, "61%", "%", "-14 pts vs 30-day", "measured"),
      secondaryMetric: metric("Aim drift onset", 35, "rep 35", "", "+8 reps later than last", "measured"),
    },
  ];
  // Below were the original demo seeds. Kept commented out for quick recall.
  const _legacySeeds: DemoCoachSeed[] = [
    {
      name: "Avery Chen",
      club: "Driver",
      sessionLabel: "Driver range block",
      title: "Start line tightened when tempo stayed even.",
      oneLineRead: "Avery's best swings start inside the left edge and hold speed through the strike.",
      confidence: "high",
      priority: "Keep the takeaway quiet, then hit 8 balls through the left-edge gate.",
      category: "delivery",
      driverTitle: "Face arrives late when transition jumps.",
      correctiveCue: "Count one beat at the top before turning through.",
      costlyMiss: "Fast transition sends the first ball right.",
      primaryMetric: metric("Start line", 1.8, "1.8 deg left", "deg", "-2.4 deg vs early set", "measured"),
      secondaryMetric: metric("Club speed", 103, "103 mph", "mph", "+4 mph vs baseline", "estimated"),
    },
    {
      name: "Maya Rodriguez",
      club: "56 degree",
      sessionLabel: "Wedge ladder",
      title: "Landing band narrowed after the third ladder.",
      oneLineRead: "Maya controlled carry once the finish matched the length of the backswing.",
      confidence: "medium",
      priority: "Run a 9-ball ladder and stop when two balls finish long.",
      category: "body",
      driverTitle: "Finish height predicts carry misses.",
      correctiveCue: "Match the finish to the backswing, then hold the chest.",
      costlyMiss: "Long wedge when the finish runs past shoulder height.",
      primaryMetric: metric("Carry spread", 9, "9 yd", "yd", "-6 yd vs first ladder", "measured"),
      secondaryMetric: metric("Typical carry", 74, "74 yd", "yd", "+2 yd vs baseline", "estimated"),
    },
    {
      name: "Jordan Lee",
      club: "7-iron",
      sessionLabel: "7-iron posture set",
      title: "Posture held early, then drifted after rep 14.",
      oneLineRead: "Jordan owns the contact pattern until fatigue pulls the chest lower.",
      confidence: "low",
      priority: "Split the next set into 2 groups of 7 and reset posture between groups.",
      category: "setup",
      driverTitle: "Late-set posture drop changes low point.",
      correctiveCue: "Reset height before each ball after rep 7.",
      costlyMiss: "Heavy contact in the final third of the set.",
      primaryMetric: metric("Strike quality", 71, "71%", "%", "-12 pts after rep 14", "proxy"),
      secondaryMetric: metric("Shots in play", 68, "68%", "%", "-9 pts vs first half", "estimated"),
    },
    {
      name: "Sam Patel",
      club: "5-iron",
      sessionLabel: "Start-line gate",
      title: "Right miss showed up only under target pressure.",
      oneLineRead: "Sam can keep the window when the target stays wide, then leaks right when the gate narrows.",
      confidence: "high",
      priority: "Run the gate wide for 5 balls, then narrow it only after two starts hold.",
      category: "delivery",
      driverTitle: "Target pressure changes face timing.",
      correctiveCue: "Start the ball at the left post before chasing speed.",
      costlyMiss: "Push fade after the target narrows.",
      primaryMetric: metric("Start-line hold", 78, "78%", "%", "+18 pts after reset", "measured"),
      secondaryMetric: metric("Offline average", 11, "11 yd", "yd", "-7 yd vs opener", "measured"),
    },
    {
      name: "Nora Kim",
      club: "Hybrid",
      sessionLabel: "Hybrid contact block",
      title: "Contact stabilized when setup stayed taller.",
      oneLineRead: "Nora's thin miss falls away when she resets height before each ball.",
      confidence: "medium",
      priority: "Use a height reset before every hybrid until 4 of 6 launch clean.",
      category: "setup",
      driverTitle: "Setup height predicts thin contact.",
      correctiveCue: "Stand tall, then let the club brush the turf.",
      costlyMiss: "Thin launch after rushed setup.",
      primaryMetric: metric("Clean launch", 67, "67%", "%", "+21 pts after reset", "proxy"),
      secondaryMetric: metric("Carry trend", 142, "142 yd", "yd", "+9 yd vs first set", "estimated"),
    },
    {
      name: "Eli Brooks",
      club: "Pitching wedge",
      sessionLabel: "Tempo ladder",
      title: "Tempo got better as the ladder slowed down.",
      oneLineRead: "Eli's carry window tightens when the backswing length stays matched to the finish.",
      confidence: "high",
      priority: "Keep the 70% tempo ladder and do not add speed until the final three.",
      category: "load",
      driverTitle: "Speed jumps widen the landing band.",
      correctiveCue: "Seven out of ten speed, same finish every ball.",
      costlyMiss: "Long wedge when speed jumps late.",
      primaryMetric: metric("Landing band", 8, "8 yd", "yd", "-5 yd vs baseline", "measured"),
      secondaryMetric: metric("Tempo variance", 6, "6%", "%", "-11 pts after ladder", "proxy"),
    },
    {
      name: "Priya Shah",
      club: "Driver",
      sessionLabel: "Driver speed set",
      title: "Speed is up without losing the playable window.",
      oneLineRead: "Priya added speed and kept the right miss from taking over the set.",
      confidence: "medium",
      priority: "Keep the same speed intent, but require two playable starts before max effort.",
      category: "load",
      driverTitle: "Max effort needs a start-line checkpoint.",
      correctiveCue: "Earn speed with two starts in play.",
      costlyMiss: "Right miss on first max-effort ball.",
      primaryMetric: metric("Club speed", 96, "96 mph", "mph", "+5 mph vs baseline", "estimated"),
      secondaryMetric: metric("Shots in play", 73, "73%", "%", "+12 pts vs last session", "measured"),
    },
    {
      name: "Lucas Martin",
      club: "8-iron",
      sessionLabel: "Contact tune-up",
      title: "Low point moved forward after the pause drill.",
      oneLineRead: "Lucas found the ground later in the set once the transition paused.",
      confidence: "medium",
      priority: "Open with 6 pause-drill reps before returning to full tempo.",
      category: "body",
      driverTitle: "Transition rush moves low point back.",
      correctiveCue: "Pause, then turn the chest through the ball.",
      costlyMiss: "Heavy contact before the drill.",
      primaryMetric: metric("Contact score", 82, "82%", "%", "+19 pts after drill", "proxy"),
      secondaryMetric: metric("Carry spread", 12, "12 yd", "yd", "-4 yd vs opener", "measured"),
    },
    {
      name: "Hannah Green",
      club: "3-wood",
      sessionLabel: "Fairway wood block",
      title: "Ball position cleanup made the 3-wood playable.",
      oneLineRead: "Hannah's 3-wood launch cleaned up when ball position moved a ball forward.",
      confidence: "low",
      priority: "Mark ball position for the first 8 reps and keep only clean launches.",
      category: "setup",
      driverTitle: "Ball position explains launch misses.",
      correctiveCue: "One ball forward, then sweep through the tee.",
      costlyMiss: "Low heel launch from back ball position.",
      primaryMetric: metric("Playable launch", 58, "58%", "%", "+16 pts after mark", "proxy"),
      secondaryMetric: metric("Typical carry", 181, "181 yd", "yd", "+11 yd vs opener", "estimated"),
    },
    {
      name: "Owen Walsh",
      club: "9-iron",
      sessionLabel: "Distance-control set",
      title: "Carry control was best with the half-speed rehearsal.",
      oneLineRead: "Owen's distance misses shrink when the rehearsal stays slower than the swing.",
      confidence: "high",
      priority: "Rehearse half-speed, then hit 10 balls without changing target.",
      category: "body",
      driverTitle: "Rehearsal tempo controls carry drift.",
      correctiveCue: "Slow rehearsal, same finish, one target.",
      costlyMiss: "Short miss after rushed rehearsal.",
      primaryMetric: metric("Carry spread", 7, "7 yd", "yd", "-8 yd vs no rehearsal", "measured"),
      secondaryMetric: metric("Target hold", 81, "81%", "%", "+17 pts vs opener", "measured"),
    },
    {
      name: "Tessa Nguyen",
      club: "Driver",
      sessionLabel: "Driver start-line check",
      title: "The slice miss softened when grip pressure dropped.",
      oneLineRead: "Tessa kept more starts in play after lowering grip pressure at address.",
      confidence: "medium",
      priority: "Use the grip-pressure check before every driver for one full set.",
      category: "setup",
      driverTitle: "Grip pressure shows up as open-face delivery.",
      correctiveCue: "Soften the hands, then turn through the same window.",
      costlyMiss: "High slice after tense setup.",
      primaryMetric: metric("Right miss rate", 24, "24%", "%", "-19 pts after check", "measured"),
      secondaryMetric: metric("Start-line spread", 4.8, "4.8 deg", "deg", "-1.6 deg vs opener", "estimated"),
    },
    {
      name: "Marco Silva",
      club: "Gap wedge",
      sessionLabel: "Scoring wedge block",
      title: "Wedge distance got noisy after the third target change.",
      oneLineRead: "Marco can own one window, then needs a reset before switching targets again.",
      confidence: "low",
      priority: "Use only two target changes next session and log the miss after each switch.",
      category: "load",
      driverTitle: "Too many target changes add distance noise.",
      correctiveCue: "Reset the landing picture before each new target.",
      costlyMiss: "Long-left wedge after the third target.",
      primaryMetric: metric("Target-change spread", 14, "14 yd", "yd", "+6 yd after third change", "proxy"),
      secondaryMetric: metric("Typical carry", 101, "101 yd", "yd", "+3 yd vs baseline", "estimated"),
    },
    {
      name: "Grace Turner",
      club: "6-iron",
      sessionLabel: "Path checkpoint",
      title: "Path improved when the trail shoulder kept turning.",
      oneLineRead: "Grace's playable fade returned when she kept the trail shoulder moving through impact.",
      confidence: "medium",
      priority: "Pair every ball with the trail-shoulder cue until two draws disappear.",
      category: "delivery",
      driverTitle: "Trail shoulder stall creates the left miss.",
      correctiveCue: "Let the trail shoulder chase the ball.",
      costlyMiss: "Pull draw when the shoulder stalls.",
      primaryMetric: metric("Left miss rate", 18, "18%", "%", "-22 pts after cue", "measured"),
      secondaryMetric: metric("Shots in play", 76, "76%", "%", "+14 pts vs opener", "measured"),
    },
    {
      name: "Ben Harper",
      club: "Putter",
      sessionLabel: "Pace ladder",
      title: "Pace control held until the long-lag finish.",
      oneLineRead: "Ben owns the middle distances, but the long-lag finish needs a smaller rehearsal.",
      confidence: "medium",
      priority: "Keep long-lag rehearsal below knee height for the next pace ladder.",
      category: "load",
      driverTitle: "Long rehearsal adds too much pace.",
      correctiveCue: "Small rehearsal, eyes on the landing spot.",
      costlyMiss: "Long lag after oversized rehearsal.",
      primaryMetric: metric("Pace window", 62, "62%", "%", "+15 pts through middle ladder", "measured"),
      secondaryMetric: metric("Long miss", 7, "7 ft", "ft", "-3 ft after cue", "estimated"),
    },
    {
      name: "Sofia Alvarez",
      club: "Sand wedge",
      sessionLabel: "Bunker carry set",
      title: "Entry point became predictable with the line drill.",
      oneLineRead: "Sofia's bunker carry steadied once the entry line stayed two inches behind the ball.",
      confidence: "high",
      priority: "Repeat the entry-line drill for 12 reps before changing lies.",
      category: "delivery",
      driverTitle: "Entry point controls carry distance.",
      correctiveCue: "Enter on the line, then finish waist high.",
      costlyMiss: "Short bunker shot from early entry.",
      primaryMetric: metric("Entry hold", 84, "84%", "%", "+26 pts after line", "measured"),
      secondaryMetric: metric("Carry spread", 5, "5 yd", "yd", "-7 yd vs opener", "measured"),
    },
  ];

  void _legacySeeds; // retained for quick recall during demo iteration
  return seeds.map((seed, index) => {
    const slug = normalizeCoachSlug(seed.name);
    const sessionId = `dev-session-${slug}`;
    const practiceHistory = makeDemoPracticeHistory(seed, index, slug);
    const isNormZeng = seed.name === "Norm Zeng";
    return {
      id: `dev-${slug}`,
      title: seed.name,
      summary: seed.oneLineRead,
      ownerEmail: isNormZeng
        ? "normzeng@gmail.com"
        : `${slug.replace(/-/g, ".")}@example.com`,
      ownerPhone: demoPhoneNumber(index),
      acceptedUserId: `dev-student-${slug}`,
      role: "coach",
      status: "accepted",
      sessionId,
      shareCode: `DEV${slug.replace(/-/g, "").slice(0, 8).toUpperCase()}`,
      previewToken: `dev-preview-${slug}`,
      briefV2: makeDemoBrief({
        packetId: `dev-packet-${slug}`,
        sessionId,
        sessionLabel: seed.sessionLabel,
        club: seed.club,
        title: seed.title,
        oneLineRead: seed.oneLineRead,
        confidence: seed.confidence,
        priority: seed.priority,
        category: seed.category,
        driverTitle: seed.driverTitle,
        correctiveCue: seed.correctiveCue,
        costlyMiss: seed.costlyMiss,
        primaryMetric: seed.primaryMetric,
        secondaryMetric: seed.secondaryMetric,
      }),
      latestRangePlanStatus: null,
      practiceHistory,
      updatedAt: practiceHistory[0]?.practicedAt ?? new Date(Date.UTC(2026, 3, 22, 4 - index, 50, 0)).toISOString(),
    };
  });
}

const DEMO_CLUB_ROTATION = [
  "Driver",
  "7-iron",
  "Pitching wedge",
  "56 degree",
  "Putter",
  "Hybrid",
  "5-iron",
  "9-iron",
  "3-wood",
  "Sand wedge",
];

function makeDemoPracticeHistory(
  seed: DemoCoachSeed,
  index: number,
  slug: string,
): CoachPracticeSession[] {
  const priorClubOne = DEMO_CLUB_ROTATION[(index + 3) % DEMO_CLUB_ROTATION.length];
  const priorClubTwo = DEMO_CLUB_ROTATION[(index + 7) % DEMO_CLUB_ROTATION.length];
  return [
    {
      id: `dev-practice-${slug}-latest`,
      practicedAt: new Date(Date.UTC(2026, 3, 22, 4 - index, 50, 0)).toISOString(),
      club: seed.club,
      label: seed.sessionLabel,
      outcome: seed.title,
    },
    {
      id: `dev-practice-${slug}-previous-1`,
      practicedAt: new Date(Date.UTC(2026, 3, 20, 19 - (index % 4), 20, 0)).toISOString(),
      club: priorClubOne,
      label: practiceLabelForCategory(seed.category, priorClubOne),
      outcome: "Pattern held for the first half, then needed one reset.",
    },
    {
      id: `dev-practice-${slug}-previous-2`,
      practicedAt: new Date(Date.UTC(2026, 3, 17, 18 - (index % 5), 10, 0)).toISOString(),
      club: priorClubTwo,
      label: practiceLabelForCategory(seed.category, priorClubTwo),
      outcome: "Best reps came after the same setup checkpoint.",
    },
  ];
}

function practiceLabelForCategory(category: DriverCategory, club: string): string {
  switch (category) {
    case "delivery":
      return `${club} start-line check`;
    case "body":
      return `${club} posture block`;
    case "load":
      return `${club} tempo ladder`;
    case "setup":
      return `${club} setup reset`;
  }
}

function demoPhoneNumber(index: number): string {
  return `(415) 555-${String(1200 + index).slice(-4)}`;
}

function makeDemoBrief({
  packetId,
  sessionId,
  sessionLabel,
  club,
  title,
  oneLineRead,
  confidence,
  priority,
  category,
  driverTitle,
  correctiveCue,
  costlyMiss,
  primaryMetric,
  secondaryMetric,
}: {
  packetId: string;
  sessionId: string;
  sessionLabel: string;
  club: string;
  title: string;
  oneLineRead: string;
  confidence: Confidence;
  priority: string;
  category: DriverCategory;
  driverTitle: string;
  correctiveCue: string;
  costlyMiss: string;
  primaryMetric: Measurement;
  secondaryMetric: Measurement;
}): CoachBriefV2 {
  const supportingMeasurements = supportingMeasurementsForCategory(category);
  return {
    meta: {
      schemaVersion: 1,
      packetId,
      generatedAt: "2026-04-22T05:00:00.000Z",
      sessionId,
      sessionLabel,
      practiceMode: "range",
      analysisScope: "full_session",
      taskLabel: null,
      club,
      dataConfidence: confidence,
      dataCoverage: {
        questionnaire: true,
        biokinematics: true,
        sessionOutcomes: true,
        historicals: true,
        watchLoad: false,
      },
    },
    executiveSummary: {
      title,
      oneLineRead,
      topFacts: [
        {
          label: primaryMetric.label,
          value: primaryMetric.formatted,
          delta: primaryMetric.delta,
          provenance: primaryMetric.provenance,
          source: "Session outcomes",
        },
        {
          label: secondaryMetric.label,
          value: secondaryMetric.formatted,
          delta: secondaryMetric.delta,
          provenance: secondaryMetric.provenance,
          source: "Swing metrics",
        },
        {
          label: "Why it happened",
          value: driverTitle,
          delta: null,
          provenance: "measured",
          source: "Pattern read",
        },
      ],
      topPriority: priority,
    },
    readiness: {
      statedGoal: "Tighten the pattern before the next lesson.",
      energyLevel: "Normal",
      soreness: null,
      painFlag: null,
      mobilityNote: null,
      environmentNote: "Indoor range mat.",
      coachGuardrail: null,
    },
    outcomes: {
      primary: [primaryMetric],
      secondary: [secondaryMetric, ...supportingMeasurements],
      dominantShape: "Small fade",
    },
    missPattern: {
      stockShot: "Playable fade",
      costlyMiss,
      driftOnset: "Late in the set",
      fatigueOnset: "After 14 reps",
      underLoadChange: "Tempo quickens when the target narrows.",
      clusters: ["Early set playable", "Late set right miss"],
    },
    causalDrivers: [
      {
        id: `${packetId}-driver`,
        title: driverTitle,
        category,
        impactSummary: "The miss pattern appears when the movement speeds up under a tighter target.",
        evidence: [primaryMetric.formatted, secondaryMetric.formatted],
        linkedOutcomes: [primaryMetric.label, secondaryMetric.label],
        confidence,
        correctiveCue,
      },
    ],
    representativeSamples: [
      {
        id: `${packetId}-sample-best`,
        role: "best_stock",
        label: "Cue landed",
        whyChosen: "Shows the pattern when the cue lands cleanly.",
        clip: {
          swingId: `${sessionId}-best-stock`,
          signedURL: null,
          signedURLExpiresAt: null,
          thumbnailURL: null,
          durationSeconds: 4,
        },
        keyMetrics: [primaryMetric, supportingMeasurements[0]].filter(Boolean),
        playerNote: null,
        coachNote: correctiveCue,
      },
      {
        id: `${packetId}-sample-miss`,
        role: "typical_miss",
        label: "Shoulder stall",
        whyChosen: costlyMiss,
        clip: {
          swingId: `${sessionId}-typical-miss`,
          signedURL: null,
          signedURLExpiresAt: null,
          thumbnailURL: null,
          durationSeconds: 4,
        },
        keyMetrics: [secondaryMetric, supportingMeasurements[1]].filter(Boolean),
        playerNote: null,
        coachNote: driverTitle,
      },
      {
        id: `${packetId}-sample-pressure`,
        role: "pressure_rep",
        label: "Target pressure",
        whyChosen: "Shows what changes when the target narrows.",
        clip: {
          swingId: `${sessionId}-pressure-rep`,
          signedURL: null,
          signedURLExpiresAt: null,
          thumbnailURL: null,
          durationSeconds: 4,
        },
        keyMetrics: supportingMeasurements.slice(2, 4),
        playerNote: null,
        coachNote: "Review before sending the next cue.",
      },
    ],
    historicalContext: {
      last7Days: {
        window: "Last 7 days",
        summary: "Pattern is moving in the right direction.",
        movers: [primaryMetric.delta ?? primaryMetric.formatted],
      },
      last30Days: null,
      last90Days: null,
      lastSession: null,
      bestWindowBaseline: null,
      recurringStrengths: ["Starts the set with a clear pattern."],
      recurringFailureModes: [costlyMiss],
      topRegressionTarget: priority,
    },
    playbook: {
      weeklyTheme: `${club} pattern control`,
      recap: oneLineRead,
      nextSessionObjective: priority,
      guardrail: "Stop the set when the same miss appears twice.",
      focusAreas: [category, "tempo"],
      steps: [
        {
          id: `${packetId}-step-1`,
          order: 1,
          title: "Baseline window",
          detail: "Hit 5 balls with the current cue and record the dominant miss.",
          durationMinutes: 6,
        },
        {
          id: `${packetId}-step-2`,
          order: 2,
          title: "Constraint set",
          detail: priority,
          durationMinutes: 10,
        },
        {
          id: `${packetId}-step-3`,
          order: 3,
          title: "Transfer check",
          detail: "Change target once and keep the same cue for 3 balls.",
          durationMinutes: 5,
        },
      ],
      verificationTest: "Pattern holds after one target change.",
      successCriteria: "Two of final three finish inside the intended window.",
    },
    collaboration: {
      workspaceStatus: "accepted",
      latestCoachUpdate: null,
      noteIds: [],
      assignmentIds: [],
      nextLessonAt: null,
    },
    appendix: {
      sessionMetrics: [primaryMetric, secondaryMetric, ...supportingMeasurements],
      swingTable: [],
      clipReferences: [],
      provenanceNotes: ["Dev fixture for local dashboard review."],
      questionnaireHistory: [],
      notesHistory: [],
    },
  };
}

function metric(
  label: string,
  value: number,
  formatted: string,
  unit: string,
  delta: string,
  provenance: Measurement["provenance"],
): Measurement {
  return {
    label,
    value,
    formatted,
    unit,
    baselineValue: null,
    baselineLabel: "Dev baseline",
    delta,
    provenance,
  };
}

function supportingMeasurementsForCategory(category: DriverCategory): Measurement[] {
  switch (category) {
    case "delivery":
      return [
        metric("Start-line window", 62, "62%", "%", "+18 pts after cue", "measured"),
        metric("Face window", 2.4, "2.4 deg", "deg", "-1.1 deg vs opener", "estimated"),
        metric("Path hold", 71, "71%", "%", "+12 pts in final set", "measured"),
        metric("Playable finish", 82, "82%", "%", "+16 pts vs baseline", "measured"),
      ];
    case "body":
      return [
        metric("Posture hold", 74, "74%", "%", "+15 pts after reset", "proxy"),
        metric("Low-point control", 79, "79%", "%", "+11 pts vs opener", "estimated"),
        metric("Finish balance", 81, "81%", "%", "+9 pts in final set", "measured"),
        metric("Clean contact", 77, "77%", "%", "+13 pts after cue", "measured"),
      ];
    case "load":
      return [
        metric("Tempo hold", 84, "84%", "%", "+18 pts after ladder", "proxy"),
        metric("Speed control", 72, "72%", "%", "+10 pts vs opener", "estimated"),
        metric("Landing window", 9, "9 yd", "yd", "-5 yd after reset", "measured"),
        metric("Final-set carry", 78, "78%", "%", "+12 pts vs first set", "measured"),
      ];
    case "setup":
      return [
        metric("Setup repeat", 80, "80%", "%", "+19 pts after mark", "proxy"),
        metric("Ball-position hold", 76, "76%", "%", "+14 pts vs opener", "measured"),
        metric("Launch window", 69, "69%", "%", "+10 pts after reset", "estimated"),
        metric("First-ball readiness", 73, "73%", "%", "+8 pts vs baseline", "measured"),
      ];
  }
}

function demoCoachAnnotations(collaborationId: string): CoachAnnotation[] {
  return [
    {
      id: `${collaborationId}-note-1`,
      collaborationId,
      authorUserId: DEV_COACH_USER.id,
      authorKind: "coach",
      sessionId: `${collaborationId}-session`,
      swingId: null,
      title: "Coach review",
      body: "Reviewed the latest range block. The trail-shoulder cue is ready to send; keep the check specific to the next set.",
      tag: "note",
      scope: "session",
      audioPath: null,
      audioDurationSeconds: null,
      createdAt: "2026-04-22T05:05:00.000Z",
      updatedAt: "2026-04-22T05:05:00.000Z",
    },
  ];
}

function demoCoachAssignments(collaborationId: string): CoachAssignment[] {
  if (collaborationId === "dev-grace-turner") {
    const mcnallyPlan = rangePlanPresetById("mcnally-5w-validation");
    return [
      {
        id: `${collaborationId}-cue-1`,
        collaborationId,
        authorUserId: DEV_COACH_USER.id,
        authorKind: "coach",
        sessionId: `${collaborationId}-session`,
        title: "Follow the 5W range plan",
        detail:
          "Run the blocks in order. Tap the watch only for real struck balls, and stop early if the sanity block is ugly.",
        dueDate: null,
        status: "sent",
        isCue: true,
        sourceDrillTemplateId: null,
        rangePlanPrescription: null,
        createdAt: "2026-04-22T05:08:00.000Z",
        updatedAt: "2026-04-22T05:08:00.000Z",
      },
      {
        id: `${collaborationId}-assignment-1`,
        collaborationId,
        authorUserId: DEV_COACH_USER.id,
        authorKind: "coach",
        sessionId: `${collaborationId}-session`,
        title: mcnallyPlan.title,
        detail: planDetailFromPrescription(mcnallyPlan),
        dueDate: null,
        status: "sent",
        isCue: false,
        sourceDrillTemplateId: mcnallyPlan.templateId,
        rangePlanPrescription: mcnallyPlan,
        createdAt: "2026-04-22T05:10:00.000Z",
        updatedAt: "2026-04-22T05:10:00.000Z",
      },
    ];
  }

  return [
    {
      id: `${collaborationId}-cue-1`,
      collaborationId,
      authorUserId: DEV_COACH_USER.id,
      authorKind: "coach",
      sessionId: `${collaborationId}-session`,
      title: "Next-swing cue",
      detail: "Hit through the gate and stop if the same miss shows twice.",
      dueDate: null,
      status: "sent",
      isCue: true,
      sourceDrillTemplateId: null,
      rangePlanPrescription: null,
      createdAt: "2026-04-22T05:08:00.000Z",
      updatedAt: "2026-04-22T05:08:00.000Z",
    },
    {
      id: `${collaborationId}-assignment-1`,
      collaborationId,
      authorUserId: DEV_COACH_USER.id,
      authorKind: "coach",
      sessionId: `${collaborationId}-session`,
      title: "Range plan: pattern-control ladder",
      detail: "Three sets of 5. Change target after each set and keep the same cue.",
      dueDate: null,
      status: "sent",
      isCue: false,
      sourceDrillTemplateId: "dev-template-gate",
      rangePlanPrescription: null,
      createdAt: "2026-04-22T05:10:00.000Z",
      updatedAt: "2026-04-22T05:10:00.000Z",
    },
  ];
}

function demoInboxThreads(): InboxThreadRow[] {
  return [
    {
      assignmentId: "dev-norm-zeng-cue-1",
      assignmentTitle: "Re-set shoulders square every 3 balls",
      assignmentIsCue: true,
      collaborationId: "dev-norm-zeng",
      collaborationTitle: "Norm Zeng",
      studentEmail: "normzeng@gmail.com",
      replyCount: 1,
      unreadCount: 1,
      latestReplyBody:
        "Held the line through rep 50. Drift came back at the end — going to reset between blocks next time.",
      latestReplyAt: "2026-04-22T05:20:00.000Z",
    },
  ];
}

function demoInboxReplies(assignmentId: string): CoachAssignmentReply[] {
  const thread =
    demoInboxThreads().find((candidate) => candidate.assignmentId === assignmentId) ??
    demoInboxThreads()[0];
  return [
    {
      id: `${assignmentId}-reply-1`,
      assignmentId,
      collaborationId: thread.collaborationId,
      authorUserId: "dev-student",
      body: thread.latestReplyBody,
      audioPath: null,
      audioDurationSeconds: null,
      readAt: thread.unreadCount > 0 ? null : "2026-04-22T05:22:00.000Z",
      createdAt: thread.latestReplyAt,
      updatedAt: thread.latestReplyAt,
    },
  ];
}

function demoDrillTemplates(): CoachDrillTemplate[] {
  const coachCuePlan = rangePlanPresetById("coach-cue-progression");
  const driverPlan = rangePlanPresetById("driver-start-line-reset");
  const mcnallyPlan = rangePlanPresetById("mcnally-5w-validation");
  const verificationPlan = rangePlanPresetById("pre-lesson-verification");
  const homeworkPlan = rangePlanPresetById("next-lesson-homework");
  const wedgePlan = rangePlanPresetById("wedge-distance-matrix");
  const fairwayPlan = rangePlanPresetById("fairway-transfer-bridge");
  const contactPlan = rangePlanPresetById("contact-window-ladder");
  const freePracticePlan = rangePlanPresetById("free-practice-guardrails");
  const puttingPlan = rangePlanPresetById("putting-pace-ladder");

  return [
    {
      id: "dev-template-coach-cue-progression",
      ownerUserId: DEV_COACH_USER.id,
      title: coachCuePlan.title,
      detail: planDetailFromPrescription(coachCuePlan),
      focusTag: "general",
      defaultDurationSwings: coachCuePlan.totalBalls,
      defaultDurationMinutes: null,
      isArchived: false,
      createdAt: "2026-04-22T04:18:00.000Z",
      updatedAt: "2026-04-22T04:18:00.000Z",
    },
    {
      id: "dev-template-driver-start-line-reset",
      ownerUserId: DEV_COACH_USER.id,
      title: driverPlan.title,
      detail: planDetailFromPrescription(driverPlan),
      focusTag: "start_line",
      defaultDurationSwings: driverPlan.totalBalls,
      defaultDurationMinutes: null,
      isArchived: false,
      createdAt: "2026-04-22T04:19:00.000Z",
      updatedAt: "2026-04-22T04:19:00.000Z",
    },
    {
      id: "dev-template-mcnally-5w",
      ownerUserId: DEV_COACH_USER.id,
      title: mcnallyPlan.title,
      detail: planDetailFromPrescription(mcnallyPlan),
      focusTag: "general",
      defaultDurationSwings: mcnallyPlan.totalBalls,
      defaultDurationMinutes: null,
      isArchived: false,
      createdAt: "2026-04-22T04:20:00.000Z",
      updatedAt: "2026-04-22T04:20:00.000Z",
    },
    {
      id: "dev-template-pre-lesson-verification",
      ownerUserId: DEV_COACH_USER.id,
      title: verificationPlan.title,
      detail: planDetailFromPrescription(verificationPlan),
      focusTag: "general",
      defaultDurationSwings: verificationPlan.totalBalls,
      defaultDurationMinutes: null,
      isArchived: false,
      createdAt: "2026-04-22T04:22:00.000Z",
      updatedAt: "2026-04-22T04:22:00.000Z",
    },
    {
      id: "dev-template-homework-ladder",
      ownerUserId: DEV_COACH_USER.id,
      title: homeworkPlan.title,
      detail: planDetailFromPrescription(homeworkPlan),
      focusTag: "general",
      defaultDurationSwings: homeworkPlan.totalBalls,
      defaultDurationMinutes: null,
      isArchived: false,
      createdAt: "2026-04-22T04:25:00.000Z",
      updatedAt: "2026-04-22T04:25:00.000Z",
    },
    {
      id: "dev-template-wedge-distance-matrix",
      ownerUserId: DEV_COACH_USER.id,
      title: wedgePlan.title,
      detail: planDetailFromPrescription(wedgePlan),
      focusTag: "contact",
      defaultDurationSwings: wedgePlan.totalBalls,
      defaultDurationMinutes: null,
      isArchived: false,
      createdAt: "2026-04-22T04:26:00.000Z",
      updatedAt: "2026-04-22T04:26:00.000Z",
    },
    {
      id: "dev-template-fairway-transfer-bridge",
      ownerUserId: DEV_COACH_USER.id,
      title: fairwayPlan.title,
      detail: planDetailFromPrescription(fairwayPlan),
      focusTag: "general",
      defaultDurationSwings: fairwayPlan.totalBalls,
      defaultDurationMinutes: null,
      isArchived: false,
      createdAt: "2026-04-22T04:27:00.000Z",
      updatedAt: "2026-04-22T04:27:00.000Z",
    },
    {
      id: "dev-template-contact-window-ladder",
      ownerUserId: DEV_COACH_USER.id,
      title: contactPlan.title,
      detail: planDetailFromPrescription(contactPlan),
      focusTag: "contact",
      defaultDurationSwings: contactPlan.totalBalls,
      defaultDurationMinutes: null,
      isArchived: false,
      createdAt: "2026-04-22T04:27:30.000Z",
      updatedAt: "2026-04-22T04:27:30.000Z",
    },
    {
      id: "dev-template-free-practice-guardrails",
      ownerUserId: DEV_COACH_USER.id,
      title: freePracticePlan.title,
      detail: planDetailFromPrescription(freePracticePlan),
      focusTag: "general",
      defaultDurationSwings: freePracticePlan.totalBalls,
      defaultDurationMinutes: null,
      isArchived: false,
      createdAt: "2026-04-22T04:28:00.000Z",
      updatedAt: "2026-04-22T04:28:00.000Z",
    },
    {
      id: "dev-template-putting-pace-ladder",
      ownerUserId: DEV_COACH_USER.id,
      title: puttingPlan.title,
      detail: planDetailFromPrescription(puttingPlan),
      focusTag: "putting",
      defaultDurationSwings: puttingPlan.totalBalls,
      defaultDurationMinutes: null,
      isArchived: false,
      createdAt: "2026-04-22T04:29:00.000Z",
      updatedAt: "2026-04-22T04:29:00.000Z",
    },
    {
      id: "dev-template-gate",
      ownerUserId: DEV_COACH_USER.id,
      title: "Start-line gate",
      detail: "Hit 10 balls through a two-yard start-line gate. Restart the count after two same-side misses.",
      focusTag: "start_line",
      defaultDurationSwings: 10,
      defaultDurationMinutes: null,
      isArchived: false,
      createdAt: "2026-04-22T04:30:00.000Z",
      updatedAt: "2026-04-22T04:30:00.000Z",
    },
    {
      id: "dev-template-wedge-ladder",
      ownerUserId: DEV_COACH_USER.id,
      title: "Wedge ladder",
      detail: "Land three balls each at short, stock, and long windows. Stop when two finish long.",
      focusTag: "contact",
      defaultDurationSwings: 9,
      defaultDurationMinutes: 12,
      isArchived: false,
      createdAt: "2026-04-22T04:35:00.000Z",
      updatedAt: "2026-04-22T04:35:00.000Z",
    },
  ];
}

function demoCoachSubscription(): CoachSubscription {
  return {
    userId: DEV_COACH_USER.id,
    status: "active",
    tier: "coach_plus",
    cancelAtPeriodEnd: false,
    currentPeriodStart: "2026-04-01T00:00:00.000Z",
    currentPeriodEnd: "2026-05-01T00:00:00.000Z",
    trialEnd: null,
    isEntitled: true,
    updatedAt: "2026-04-22T05:00:00.000Z",
  };
}

function demoNotificationPreferences(): CoachNotificationPreferences {
  return {
    userId: DEV_COACH_USER.id,
    emailOnStudentReply: true,
    dedupeWindowSeconds: 600,
    updatedAt: "2026-04-22T05:00:00.000Z",
  };
}

function compareCoachRows(a: Collaboration, b: Collaboration): number {
  const priorityDelta = rowPriorityScore(b) - rowPriorityScore(a);
  if (priorityDelta !== 0) return priorityDelta;
  return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
}

function rowPriorityScore(row: Collaboration): number {
  const brief = row.briefV2;
  if (!brief) return 0;
  const confidence = confidenceRank(brief.meta.dataConfidence);
  const hasPriority = brief.executiveSummary.topPriority.trim() ? 2 : 0;
  const driverWeight = Math.min(brief.causalDrivers.length, 3);
  return confidence + hasPriority + driverWeight;
}

function confidenceRank(confidence: Confidence | null | undefined): number {
  switch (confidence) {
    case "high":
      return 3;
    case "medium":
      return 2;
    case "low":
      return 1;
    default:
      return 0;
  }
}

function confidenceTone(
  confidence: Confidence | null | undefined,
): "positive" | "caution" | "negative" | "muted" {
  switch (confidence) {
    case "high":
      return "positive";
    case "medium":
      return "caution";
    case "low":
      return "negative";
    default:
      return "muted";
  }
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function analysisScopeLabel(scope: CoachBriefV2["meta"]["analysisScope"]): string {
  switch (scope) {
    case "single_shot":
      return "Single shot";
    case "three_shot_window":
      return "3-shot window";
    case "full_session":
      return "Full session";
  }
}

function provenanceDisplayLabel(provenance: Measurement["provenance"]): string | null {
  return provenance === "measured" ? null : PROVENANCE_LABEL[provenance];
}

function practiceModeLabel(mode: PracticeMode): string {
  switch (mode) {
    case "range":
      return "Range session";
    case "short_game_chip":
      return "Short game";
    case "short_game_putt":
      return "Putting";
  }
}

function driverCategoryLabel(category: DriverCategory): string {
  switch (category) {
    case "delivery":
      return "Swing delivery";
    case "body":
      return "Body motion";
    case "load":
      return "Speed / load";
    case "setup":
      return "Setup";
  }
}

type StudentBagItem = {
  club: string;
  displayName: string;
  status: "latest" | "recent" | "tracked";
  statusLabel: string;
  note: string;
  fitNote: string;
};

const STUDENT_BAG_CATALOG: Record<string, { displayName: string; fitNote: string }> = {
  Driver: {
    displayName: "TaylorMade Qi10 Max driver",
    fitNote: "10.5 degrees, Ventus TR Blue 5S. High launch when the tee stays forward.",
  },
  "3-wood": {
    displayName: "Callaway Paradym Ai Smoke 3-wood",
    fitNote: "15 degrees. Used as the controlled tee club when start line matters more than carry.",
  },
  Hybrid: {
    displayName: "PING G430 4-hybrid",
    fitNote: "22 degrees. Reliable rescue club, but face can close when tempo speeds up.",
  },
  "6-iron": {
    displayName: "Titleist T150 6-iron",
    fitNote: 'Built 1/2" short and 1 degree flat. Best club for Grace\'s path checkpoints.',
  },
  "9-iron": {
    displayName: "Titleist T150 9-iron",
    fitNote: "Stock scoring club. Good read on whether the setup checkpoint transfers.",
  },
  "Sand wedge": {
    displayName: "Vokey SM10 54.10S sand wedge",
    fitNote: "Neutral bounce for flighted wedges and bunker work.",
  },
  Putter: {
    displayName: "Odyssey Ai-One Seven putter",
    fitNote: "Face-balanced mallet. Keeps start line steadier on short putts.",
  },
};

const DEFAULT_BAG_CLUBS = Object.keys(STUDENT_BAG_CATALOG);

function studentBagItems(row: Collaboration): StudentBagItem[] {
  const history = recentPracticeHistory(row);
  const clubs = Array.from(new Set([...history.map((session) => session.club), ...DEFAULT_BAG_CLUBS]));
  return clubs.slice(0, 8).map((club) => {
    const sessionIndex = history.findIndex((session) => session.club === club);
    const session = sessionIndex >= 0 ? history[sessionIndex] : null;
    const catalogItem = STUDENT_BAG_CATALOG[club] ?? {
      displayName: club,
      fitNote: "No build note yet.",
    };
    if (sessionIndex === 0 && session) {
      return {
        club,
        ...catalogItem,
        status: "latest",
        statusLabel: "Latest",
        note: session.label,
      };
    }
    if (session) {
      return {
        club,
        ...catalogItem,
        status: "recent",
        statusLabel: formatShortDate(session.practicedAt),
        note: session.label,
      };
    }
    return {
      club,
      ...catalogItem,
      status: "tracked",
      statusLabel: "In bag",
      note: "Ready for future review.",
    };
  });
}

function sampleRoleLabel(role: CoachBriefV2["representativeSamples"][number]["role"]): string {
  switch (role) {
    case "best_stock":
      return "Best stock swing";
    case "typical_miss":
      return "Typical miss";
    case "fatigue_rep":
      return "Fatigue rep";
    case "pressure_rep":
      return "Pressure rep";
  }
}

function frameHref(
  collaborationId: string,
  sample: CoachBriefV2["representativeSamples"][number],
  sessionId: string | null = null,
): string {
  return buildStudentMediaPath(collaborationId, {
    sessionId,
    swingId: sample.clip.swingId,
  });
}

function SampleFrameMedia({
  clip,
  open,
}: {
  clip: CoachBriefV2["representativeSamples"][number]["clip"];
  open: boolean;
}) {
  if (clip.thumbnailURL) {
    return (
      <img
        className="sample-poster"
        src={clip.thumbnailURL}
        alt=""
        loading="lazy"
      />
    );
  }
  if (clip.signedURL) {
    return (
      <video
        className="sample-poster"
        src={clip.signedURL}
        preload="metadata"
        muted
        playsInline
        aria-label="Swing clip"
      />
    );
  }
  return <PoseLines open={open} />;
}

type StudentMediaItem = {
  swingId: string;
  title: string;
  roleLabel: string;
  summary: string;
  metrics: Measurement[];
  club: string;
  createdAt: string;
  sessionId: string;
  sessionLabel: string;
  sessionOutcome: string;
  reviewHref: string;
  url: string | null;
};

function studentMediaItems(row: Collaboration): StudentMediaItem[] {
  const brief = row.briefV2;
  if (!brief) return [];
  const history = recentPracticeHistory(row);
  const latestSession = history[0] ?? null;
  const sessionId = brief.meta.sessionId || latestSession?.id || row.sessionId || `${row.id}-session`;
  const sessionLabel = brief.meta.sessionLabel || latestSession?.label || "Range session";
  const sessionOutcome = latestSession?.outcome || brief.executiveSummary.title;
  const createdAt = latestSession?.practicedAt || brief.meta.generatedAt;
  const club = brief.meta.club || latestSession?.club || "All clubs";
  return brief.representativeSamples.map((sample) => ({
    swingId: sample.clip.swingId,
    title: sample.label,
    roleLabel: sampleRoleLabel(sample.role),
    summary: sample.whyChosen,
    metrics: sample.keyMetrics,
    club,
    createdAt,
    sessionId,
    sessionLabel,
    sessionOutcome,
    reviewHref: buildStudentMediaPath(row.id, {
      sessionId,
      swingId: sample.clip.swingId,
    }),
    url: sample.clip.signedURL,
  }));
}

type StudentMediaSessionGroup = {
  id: string;
  label: string;
  outcome: string;
  club: string;
  practicedAt: string;
  swings: StudentMediaItem[];
};

type StudentMediaDateGroup = {
  dateKey: string;
  dateLabel: string;
  practicedAt: string;
  sessions: StudentMediaSessionGroup[];
};

function groupStudentMediaByDateAndSession(items: StudentMediaItem[]): StudentMediaDateGroup[] {
  const dateGroups = new Map<string, StudentMediaDateGroup>();

  for (const item of items) {
    const dateKey = localDateKey(item.createdAt);
    let dateGroup = dateGroups.get(dateKey);
    if (!dateGroup) {
      dateGroup = {
        dateKey,
        dateLabel: formatShortDate(item.createdAt),
        practicedAt: item.createdAt,
        sessions: [],
      };
      dateGroups.set(dateKey, dateGroup);
    }

    let sessionGroup = dateGroup.sessions.find((session) => session.id === item.sessionId);
    if (!sessionGroup) {
      sessionGroup = {
        id: item.sessionId,
        label: item.sessionLabel,
        outcome: item.sessionOutcome,
        club: item.club,
        practicedAt: item.createdAt,
        swings: [],
      };
      dateGroup.sessions.push(sessionGroup);
    }

    sessionGroup.swings.push(item);
  }

  return Array.from(dateGroups.values())
    .map((dateGroup) => ({
      ...dateGroup,
      sessions: [...dateGroup.sessions].sort(
        (left, right) => Date.parse(right.practicedAt) - Date.parse(left.practicedAt),
      ),
    }))
    .sort((left, right) => Date.parse(right.practicedAt) - Date.parse(left.practicedAt));
}

function localDateKey(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "unknown";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function recentPracticeHistory(row: Collaboration): CoachPracticeSession[] {
  if (row.practiceHistory?.length) return row.practiceHistory;
  if (!row.briefV2) return [];
  return [
    {
      id: `${row.id}-latest-range-work`,
      practicedAt: row.updatedAt,
      club: row.briefV2.meta.club || "Mixed clubs",
      label: row.briefV2.meta.sessionLabel,
      outcome: row.briefV2.executiveSummary.title,
    },
  ];
}

function clubSummary(sessions: CoachPracticeSession[]): string {
  const clubs = Array.from(new Set(sessions.map((session) => session.club).filter(Boolean)));
  if (clubs.length === 0) return "No clubs yet";
  if (clubs.length === 1) return clubs[0];
  if (clubs.length === 2) return `${clubs[0]} + ${clubs[1]}`;
  return `${clubs[0]} + ${clubs.length - 1} clubs`;
}

function countDriverCategories(rows: Collaboration[]): Record<"delivery" | "body" | "setup" | "load", number> {
  const counts = { delivery: 0, body: 0, setup: 0, load: 0 };
  for (const row of rows) {
    for (const driver of row.briefV2?.causalDrivers ?? []) {
      counts[driver.category] += 1;
    }
  }
  return counts;
}

function measurementHighlights(brief: CoachBriefV2, limit?: number): Measurement[] {
  const seen = new Set<string>();
  const candidates = [
    ...brief.outcomes.primary,
    ...brief.outcomes.secondary,
    ...brief.appendix.sessionMetrics,
    ...brief.representativeSamples.flatMap((sample) => sample.keyMetrics),
  ];
  const uniqueMeasurements = candidates
    .filter((measurement) => measurement.formatted.trim() || measurement.value != null)
    .filter((measurement) => {
      const key = `${measurement.label}:${measurement.formatted}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  return limit == null ? uniqueMeasurements : uniqueMeasurements.slice(0, limit);
}

function formatShortDate(value: string | Date): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No date";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

function formatDate(value: string | Date): string {
  return new Date(value).toLocaleString();
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
